import React, { useState, useEffect } from 'react';
import api, { uploadImage, getImageUrl } from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import './Admin.css';

const ALL_TABS = ['orders','rentals','contacts','customers','products','categories','blog','biketowork','home','slides','reviews','testimonials','branding','navigation','footer','contactSettings','gateways','delivery','smtp','twilio','support','services'];

function AdminLogin({ onLogin }) {
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    setLoading(true);
    try {
      const { data } = await api.post('/admin/login', { username: u, password: p });
      sessionStorage.setItem('adminToken', data.token);
      sessionStorage.setItem('adminRole', data.role);
      sessionStorage.setItem('adminPerms', JSON.stringify(data.permissions || ALL_TABS));
      sessionStorage.setItem('adminUsername', data.username);
      onLogin({ role: data.role, permissions: data.permissions || ALL_TABS, username: data.username });
    } catch (err) {
      setErr(err.response?.data?.error || 'Invalid username or password.');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', padding: 40, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.1)', width: 360 }}>
        <h2 style={{ margin: '0 0 6px', textAlign: 'center' }}>Admin Login</h2>
        <p style={{ color: '#888', fontSize: 14, textAlign: 'center', margin: '0 0 24px' }}>Sign in to manage your site</p>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <input placeholder="Username" value={u} onChange={e => setU(e.target.value)}
            style={{ padding: '11px 14px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 15 }} required autoFocus />
          <input type="password" placeholder="Password" value={p} onChange={e => setP(e.target.value)}
            style={{ padding: '11px 14px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 15 }} required />
          {err && <p style={{ color: '#dc3545', fontSize: 13, margin: 0 }}>{err}</p>}
          <button type="submit" className="btn" style={{ padding: '13px', fontSize: 15 }} disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Admin() {
  const [session, setSession] = useState(() => {
    // Clear legacy auth key from old login system
    sessionStorage.removeItem('adminAuth');
    const token = sessionStorage.getItem('adminToken');
    if (!token) return null;
    return {
      role: sessionStorage.getItem('adminRole') || 'admin',
      permissions: JSON.parse(sessionStorage.getItem('adminPerms') || 'null') || ALL_TABS,
      username: sessionStorage.getItem('adminUsername') || 'admin',
    };
  });

  const handleLogout = () => {
    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminRole');
    sessionStorage.removeItem('adminPerms');
    sessionStorage.removeItem('adminUsername');
    setSession(null);
  };

  // Attach JWT to every API request
  useEffect(() => {
    const token = sessionStorage.getItem('adminToken');
    if (token) api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }, [session]);

  if (!session) return <AdminLogin onLogin={(s) => { setSession(s); api.defaults.headers.common['Authorization'] = `Bearer ${sessionStorage.getItem('adminToken')}`; }} />;

  return <AdminPanel session={session} onLogout={handleLogout} />;
}

function AdminPanel({ session, onLogout }) {
  const { role, permissions, username } = session;
  const can = (tab) => role === 'superadmin' || (permissions || []).includes(tab);
  const currency = useCurrency();
  const [activeTab, setActiveTab] = useState(() => {
    const first = ['orders','contacts','products','blog','biketowork','home','slides','reviews','testimonials','branding','navigation','footer','contactSettings','gateways','smtp','adminUsers','changePassword'].find(t => role === 'superadmin' || (permissions||[]).includes(t));
    return first || 'orders';
  });
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productImages, setProductImages] = useState(['']);
  const [productImageFiles, setProductImageFiles] = useState([null]);
  const [productForm, setProductForm] = useState({ name: '', brand: '', model: '', description: '', price: '', oldPrice: '', discount: '', discountType: 'percentage', range: '', inStock: true, stockCount: '', category: 'electric-scooters', subCategory: '', compatibleProducts: [], linkedSpareParts: [] });

  const [uploading, setUploading] = useState(false);
  const [productCatFilter, setProductCatFilter] = useState('all');
  const [productSort, setProductSort] = useState('order');
  const [adminCategories, setAdminCategories] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ key: '', label: '', order: 0, enabled: true });
  const [editingCategory, setEditingCategory] = useState(null);

  const handleFileSelect = (i, file) => {
    if (!file) return;
    
    // Store the File object
    setProductImageFiles(files => files.map((f, idx) => idx === i ? file : f));
    
    // Show preview using FileReader
    const reader = new FileReader();
    reader.onload = (e) => {
      updateImageField(i, e.target.result);
    };
    reader.readAsDataURL(file);
  };
 const addImageField = () => {
    setProductImages(imgs => [...imgs, '']);
    setProductImageFiles(files => [...files, null]);
  };
  const removeImageField = (i) => {
    setProductImages(imgs => imgs.filter((_, idx) => idx !== i));
    setProductImageFiles(files => files.filter((_, idx) => idx !== i));
  };
  const updateImageField = (i, val) => setProductImages(imgs => imgs.map((img, idx) => idx === i ? val : img));
  const setField = (f, v) => setProductForm(p => ({ ...p, [f]: v }));
  const [testimonialForm, setTestimonialForm] = useState({ text: '', author: '' });
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [navMenu, setNavMenu] = useState([]);
  const [topline, setTopline] = useState([]); // eslint-disable-line no-unused-vars
  const [toplineJson, setToplineJson] = useState('');
  const [editingMenu, setEditingMenu] = useState(null);
  const [menuForm, setMenuForm] = useState({ name: '', path: '', order: 0, enabled: true, submenu: [] });
  const [submenuJson, setSubmenuJson] = useState('[]'); // eslint-disable-line no-unused-vars
  const [saveMsg, setSaveMsg] = useState('');
  const [branding, setBranding] = useState({ siteName: '', logo: '', favicon: '', currency: '€', timezone: 'Europe/Dublin', googleAnalyticsId: '', googleAdsId: '', gtmId: '' });
  const [brandingUploading, setBrandingUploading] = useState({ logo: false, favicon: false });
  const [slides, setSlides] = useState([]);
  const [slideUploading, setSlideUploading] = useState({});
  const [footerData, setFooterData] = useState({ address: '', email: '', phone: '', hours: '', disclaimer: '', copyright: '', social: [], badges: [], support: [], scooters: [], bikes: [] });
  const [homeData, setHomeData] = useState({ whyUsTitle: '', whyUsParas: [''], whyUsCta: '', whyUsCtaLink: '', whyUsContact: '', serviceTitle: '', serviceSubtitle: '', serviceParas: [''], serviceContact: '' });
  const [aboutData, setAboutData] = useState({ title: '', content: '', mission: '', values: [], stats: [] });
  const [aboutMsg, setAboutMsg] = useState('');
  const [gwSettings, setGwSettings] = useState({ paypal: { enabled: true, clientId: '', secret: '', mode: 'sandbox' }, stripe: { enabled: false, publishableKey: '', secretKey: '' } });
  const [gwMsg, setGwMsg] = useState('');
  const [deliverySettings, setDeliverySettings] = useState({ categories: [], deliveryFee: 0, freeDeliveryThreshold: 0, estimatedDate: '' });
  const [deliveryMsg, setDeliveryMsg] = useState('');
  const [contactSettings, setContactSettings] = useState({
    title: 'Contact Us',
    address: 'Unit 6, Park Shopping Centre, Prussia Street, Dublin 7, D07K802, Ireland',
    phone: '087 100 2241',
    email: 'sales@greenwheelsscooters.ie',
    hours: '10:30am - 7:00pm, Mon - Sun',
    mapEmbed: '',
    directionsUrl: '',
  });
  const [contactMsg, setContactMsg] = useState('');
  const [blogPosts, setBlogPosts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [blogForm, setBlogForm] = useState({ title: '', slug: '', excerpt: '', image: '', category: '', author: '', published: true });
  const [blogSections, setBlogSections] = useState([]);
  const [editingPost, setEditingPost] = useState(null);
  const [blogMsg, setBlogMsg] = useState('');
  const [reviews, setReviews] = useState([]);
  const [reviewForm, setReviewForm] = useState({ title: '', body: '', author: '', date: '', stars: 5, visible: true });
  const [editingReview, setEditingReview] = useState(null);
  const [reviewMsg, setReviewMsg] = useState('');
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [serpSettings, setSerpSettings] = useState({ serpApiKey: '', serpDataId: '' });
  const [serpImporting, setSerpImporting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [orderMsg, setOrderMsg] = useState(''); // eslint-disable-line no-unused-vars
  const [orderFilter, setOrderFilter] = useState('all');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderModal, setOrderModal] = useState(null);
  const [btwData, setBtwData] = useState(null);
  const [btwMsg, setBtwMsg] = useState('');
  const [smtpSettings, setSmtpSettings] = useState({ host: 'smtp.gmail.com', port: '587', user: '', pass: '', from: '', adminEmail: '' });
  const [smtpMsg, setSmtpMsg] = useState('');
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [twilioSettings, setTwilioSettings] = useState({ accountSid: '', authToken: '', whatsappFrom: 'whatsapp:+14155238886', smsFrom: '', adminWhatsapp: '', channel: 'whatsapp', mode: 'sandbox' });
  const [twilioMsg, setTwilioMsg] = useState('');
  const [twilioTesting, setTwilioTesting] = useState(false);
  const [twilioConfigured, setTwilioConfigured] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersMsg, setAdminUsersMsg] = useState('');
  const [adminUserForm, setAdminUserForm] = useState({ username: '', password: '', role: 'admin', permissions: ALL_TABS });
  const [editingAdminUser, setEditingAdminUser] = useState(null);
  const [changePwForm, setChangePwForm] = useState({ current: '', next: '', confirm: '' });
  const [changePwMsg, setChangePwMsg] = useState('');
  const [dbSettings, setDbSettings] = useState({ uri: '', status: '', connected: false, dbName: '', collections: [] });
  const [dbMsg, setDbMsg] = useState('');
  const [dbTesting, setDbTesting] = useState(false);
  const [dbTestUri, setDbTestUri] = useState('');
  const [backups, setBackups] = useState([]);
  const [backupCreating, setBackupCreating] = useState(false);
  const [backupRestoring, setBackupRestoring] = useState(false);
  const [backupCol, setBackupCol] = useState('');
  const [backupMsg, setBackupMsg] = useState('');
  const [backupSchedule, setBackupSchedule] = useState({ enabled: false, interval: '12h', keep: 20, lastRun: null });
  const POLICY_PAGES = [
    { key: 'tos',           icon: '📋', title: 'Terms of Service' },
    { key: 'warranty',      icon: '🛡️', title: 'Warranty Policy' },
    { key: 'privacy',       icon: '🔒', title: 'Privacy Policy' },
    { key: 'return-refund', icon: '↩️', title: 'Return & Refund Policy' },
  ];
  const [supportData, setSupportData] = useState({
    heroTitle: 'Support Centre',
    heroSubtitle: 'Everything you need — policies, warranty info, and ways to reach us.',
    ctaTitle: 'Still need help?',
    ctaSubtitle: 'Our team is available 7 days a week — 10:30am to 7:00pm.',
    ctaPhone: '0871002241',
    links: [
      { icon: '📋', title: 'Terms of Service', desc: 'Read our full terms and conditions for using our website and services.', href: '/support/tos', external: false },
      { icon: '🛡️', title: 'Warranty Policy', desc: 'Understand what is covered under our product warranty and how to make a claim.', href: '/support/warranty', external: false },
      { icon: '🔒', title: 'Privacy Policy', desc: 'Learn how we collect, use, and protect your personal information.', href: '/support/privacy', external: false },
      { icon: '↩️', title: 'Return & Refund Policy', desc: 'Find out how to return a product and our refund process and timelines.', href: '/support/return-refund', external: false },
      { icon: '💬', title: 'Contact Us', desc: 'Have a question or need help? Get in touch with our team directly.', href: '/contact', external: false },
    ],
  });
  const [supportMsg, setSupportMsg] = useState('');
  const [supportSubTab, setSupportSubTab] = useState('settings');
  const [policyContents, setPolicyContents] = useState({ tos: '', warranty: '', privacy: '', 'return-refund': '' });
  const [policyMsg, setPolicyMsg] = useState('');

  const [servicesData, setServicesData] = useState({
    heroTitle: 'Bike Rentals', heroSubtitle: '', heroImage: '', introTitle: 'Why Rent With Us?', introText: '',
    rentalPlans: [], fleet: [], howItWorks: [], includes: [], requirements: [], faq: [],
    ctaTitle: 'Ready to Ride?', ctaSubtitle: '', ctaPhone: '0871002241', ctaButtonText: 'Book Now', ctaButtonLink: '/contact',
    showRentalPlans: true, showFleet: true, showHowItWorks: true, showIncludes: true, showRequirements: true, showFaq: true, showCta: true,
  });
  const [servicesMsg, setServicesMsg] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [authSettings, setAuthSettings] = useState({ loginMode: 'both' });
  const [authMsg, setAuthMsg] = useState('');
  const [rentalBookings, setRentalBookings] = useState([]);
  const [rentalMsg, setRentalMsg] = useState(''); // eslint-disable-line no-unused-vars
  const [rentalFilter, setRentalFilter] = useState('all');
  const [rentalModal, setRentalModal] = useState(null);

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      if (activeTab === 'products') {
        const [{ data }, { data: cats }] = await Promise.all([api.get('/admin/products'), api.get('/admin/categories')]);
        setProducts(data);
        setAdminCategories(cats);
      } else if (activeTab === 'categories') {
        const { data } = await api.get('/admin/categories');
        setAdminCategories(data);
      } else if (activeTab === 'testimonials') {
        const { data } = await api.get('/testimonials');
        setTestimonials(data);
      } else if (activeTab === 'contacts') {
        const { data } = await api.get('/admin/contacts');
        setContacts(data);
      } else if (activeTab === 'navigation') {
        const [{ data: menuItems }, { data: hdr }, { data: prods }] = await Promise.all([
          api.get('/admin/menu'),
          api.get('/content/header'),
          api.get('/products'),
        ]);
        setNavMenu(menuItems);
        setTopline(hdr.topline || []);
        setToplineJson(JSON.stringify(hdr.topline || [], null, 2));
        setAllProducts(prods);
      } else if (activeTab === 'branding') {
        const [{ data: b }, { data: cc }, { data: auth }] = await Promise.all([
          api.get('/branding'),
          api.get('/content/country_codes').catch(() => ({ data: null })),
          api.get('/admin/auth-settings').catch(() => ({ data: { loginMode: 'both' } })),
        ]);
        setBranding({ siteName: b.siteName || '', logo: b.logo || '', favicon: b.favicon || '', currency: b.currency || '€', timezone: b.timezone || 'Europe/Dublin', googleAnalyticsId: b.googleAnalyticsId || '', googleAdsId: b.googleAdsId || '', gtmId: b.gtmId || '', countryCodes: cc?.codes || [{ code: '+353', name: 'Ireland', flag: 'ie' }] });
        setAuthSettings(auth);
      } else if (activeTab === 'slides') {
        const { data: s } = await api.get('/slides');
        setSlides(Array.isArray(s) ? s : []);
      } else if (activeTab === 'footer') {
        const { data: fd } = await api.get('/content/footer').catch(() => ({ data: {} }));
        const legacyMap = { '/tos': '/support/tos', '/warranty-policy': '/support/warranty', '/privacy-policy': '/support/privacy', '/return-refund-policy': '/support/return-refund' };
        const remappedSupport = (fd.support?.length ? fd.support : [
          { name: 'Terms of Service', path: '/support/tos' },
          { name: 'Warranty Policy', path: '/support/warranty' },
          { name: 'Privacy Policy', path: '/support/privacy' },
          { name: 'Return & Refund Policy', path: '/support/return-refund' },
          { name: 'Contact Us', path: '/contact' },
        ]).map(l => ({ ...l, path: legacyMap[l.path] || l.path }));
        setFooterData({
          address: 'Unit 6, Park Shopping Centre, Prussia Street, Dublin 7, D07K802, Ireland',
          email: 'sales@greenwheelsscooters.ie',
          phone: '087 100 2241',
          hours: '10:30am - 7:00pm, Mon - Sun',
          disclaimer: '', copyright: '', social: [], badges: [], scooters: [], bikes: [],
          ...fd,
          support: remappedSupport,
        });
      } else if (activeTab === 'home') {
        const { data: hd } = await api.get('/content/home').catch(() => ({ data: {} }));
        setHomeData(hd || { whyUsTitle: '', whyUsParas: [''], whyUsCta: '', whyUsCtaLink: '', whyUsContact: '', serviceTitle: '', serviceSubtitle: '', serviceParas: [''], serviceContact: '' });
      } else if (activeTab === 'about') {
        const { data: ad } = await api.get('/content/about').catch(() => ({ data: {} }));
        const defaults = { title: '', content: '', mission: '', values: [], stats: [
          { num: '5000+', label: 'Happy Customers' },
          { num: '100+', label: 'Products' },
          { num: '5\u2605', label: 'Google Rating' },
          { num: '7 Days', label: 'Open Every Week' },
        ] };
        setAboutData({ ...defaults, ...ad, stats: ad?.stats?.length ? ad.stats : defaults.stats });
      } else if (activeTab === 'gateways') {
        const { data: gw } = await api.get('/admin/payment-gateways');
        setGwSettings(gw);
      } else if (activeTab === 'delivery') {
        const { data: ds } = await api.get('/delivery-settings');
        setDeliverySettings(ds);
      } else if (activeTab === 'contactSettings') {
        const { data: cs } = await api.get('/content/contact').catch(() => ({ data: {} }));
        setContactSettings(prev => ({
          ...prev,
          title: cs.title || prev.title,
          address: cs.address || prev.address,
          phone: cs.phone || prev.phone,
          email: cs.email || prev.email,
          hours: cs.hours || prev.hours,
          mapEmbed: cs.mapEmbed || prev.mapEmbed,
          directionsUrl: cs.directionsUrl || prev.directionsUrl,
        }));
      } else if (activeTab === 'reviews') {
        const [{ data: rv }, { data: ss }] = await Promise.all([
          api.get('/admin/reviews'),
          api.get('/admin/serpapi-settings').catch(() => ({ data: { serpApiKey: '', serpDataId: '' } }))
        ]);
        setReviews(rv || []);
        setSerpSettings({ serpApiKey: ss.serpApiKey || '', serpDataId: ss.serpDataId || '' });
      } else if (activeTab === 'blog') {
        const [{ data: bp }, { data: prods }] = await Promise.all([
          api.get('/admin/blog'),
          api.get('/products'),
        ]);
        setBlogPosts(bp);
        setAllProducts(prods);
      } else if (activeTab === 'orders') {
        const { data } = await api.get('/admin/orders');
        setOrders(data);
      } else if (activeTab === 'biketowork') {
        const { data } = await api.get('/content/bike-to-work').catch(() => ({ data: {} }));
        setBtwData(data || {});
      } else if (activeTab === 'smtp') {
        const { data } = await api.get('/admin/smtp-settings');
        setSmtpSettings({ host: data.host || 'smtp.gmail.com', port: data.port || '587', user: data.user || '', pass: data.pass || '', from: data.from || '', adminEmail: data.adminEmail || '' });
        setSmtpConfigured(data.configured || false);
      } else if (activeTab === 'twilio') {
        const { data } = await api.get('/admin/twilio-settings').catch(() => ({ data: {} }));
        setTwilioSettings({ accountSid: data.accountSid || '', authToken: data.authToken ? '••••••••' : '', whatsappFrom: data.whatsappFrom || 'whatsapp:+14155238886', smsFrom: data.smsFrom || '', adminWhatsapp: data.adminWhatsapp || '', channel: data.channel || 'whatsapp', mode: data.mode || 'sandbox' });
        setTwilioConfigured(data.configured || false);
      } else if (activeTab === 'adminUsers') {
        const { data } = await api.get('/admin/users');
        setAdminUsers(data);
      } else if (activeTab === 'dbSettings') {
        const [{ data }, { data: bk }, { data: sched }] = await Promise.all([
          api.get('/admin/db-settings'),
          api.get('/admin/backups').catch(() => ({ data: [] })),
          api.get('/admin/backups/schedule').catch(() => ({ data: { enabled: false, interval: '12h', keep: 20, lastRun: null } })),
        ]);
        setDbSettings(data);
        setDbTestUri(data.uri || '');
        setBackups(bk || []);
        setBackupSchedule(sched || { enabled: false, interval: '12h', keep: 20, lastRun: null });
      } else if (activeTab === 'support') {
        const { data } = await api.get('/support').catch(() => ({ data: null }));
        if (data) setSupportData(prev => ({ ...prev, ...data, links: data.links?.length ? data.links : prev.links }));
        // Load all policy page contents
        const keys = ['tos', 'warranty', 'privacy', 'return-refund'];
        const results = await Promise.all(keys.map(k => api.get(`/content/policy_${k}`).catch(() => ({ data: null }))));
        const contents = {};
        keys.forEach((k, i) => {
          const raw = results[i].data?.sections || [];
          // migrate old paragraphs format to blocks
          contents[k] = raw.map(sec => sec.blocks ? sec : {
            heading: sec.heading || '',
            blocks: (sec.paragraphs || []).map(p => ({ type: 'paragraph', value: p }))
          });
        });
        setPolicyContents(contents);
      } else if (activeTab === 'services') {
        const [resp1, resp2] = await Promise.all([
          api.get('/services').catch(() => ({ data: null })),
          api.get('/admin/products'),
        ]);
        const svc = resp1.data;
        if (svc) {
          const newState = {
            heroTitle: svc.heroTitle || 'Bike Rentals',
            heroSubtitle: svc.heroSubtitle || '',
            heroImage: svc.heroImage || '',
            introTitle: svc.introTitle || '',
            introText: svc.introText || '',
            rentalPlans: Array.isArray(svc.rentalPlans) ? svc.rentalPlans : [],
            fleet: Array.isArray(svc.fleet) ? svc.fleet : [],
            howItWorks: Array.isArray(svc.howItWorks) ? svc.howItWorks : [],
            includes: Array.isArray(svc.includes) ? svc.includes : [],
            requirements: Array.isArray(svc.requirements) ? svc.requirements : [],
            faq: Array.isArray(svc.faq) ? svc.faq : [],
            ctaTitle: svc.ctaTitle || '',
            ctaSubtitle: svc.ctaSubtitle || '',
            ctaPhone: svc.ctaPhone || '',
            ctaButtonText: svc.ctaButtonText || 'Book Now',
            ctaButtonLink: svc.ctaButtonLink || '/contact',
            showRentalPlans: svc.showRentalPlans !== false,
            showFleet: svc.showFleet !== false,
            showHowItWorks: svc.showHowItWorks !== false,
            showIncludes: svc.showIncludes !== false,
            showRequirements: svc.showRequirements !== false,
            showFaq: svc.showFaq !== false,
            showCta: svc.showCta !== false,
          };
          console.log('SERVICES DATA SET:', JSON.stringify({ includes: newState.includes.length, requirements: newState.requirements.length }));
          setServicesData(newState);
        }
        setProducts(resp2.data);
      } else if (activeTab === 'customers') {
        const { data } = await api.get('/admin/customers');
        setCustomers(data);
      } else if (activeTab === 'rentals') {
        const { data } = await api.get('/admin/rental-bookings');
        setRentalBookings(data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleDeleteProduct = async (id) => {
    if (window.confirm('Delete this product?')) {
      try {
        await api.delete(`/admin/products/${id}`);
        loadData();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const handleDeleteTestimonial = async (id) => {
    if (window.confirm('Delete this testimonial?')) {
      try {
        await api.delete(`/admin/testimonials/${id}`);
        loadData();
      } catch (error) {
        console.error('Error deleting testimonial:', error);
      }
    }
  };

  const handleSaveTestimonial = async (e) => {
    e.preventDefault();
    try {
      if (editingTestimonial) {
        await api.put(`/admin/testimonials/${editingTestimonial._id}`, testimonialForm);
      } else {
        await api.post('/admin/testimonials', testimonialForm);
      }
      setEditingTestimonial(null);
      setTestimonialForm({ text: '', author: '' });
      loadData();
    } catch (error) { console.error('Error saving testimonial:', error); }
  };

  const handleEditTestimonial = (t) => {
    setEditingTestimonial(t);
    setTestimonialForm({ text: t.text, author: t.author });
  };

  const handleDeleteContact = async (id) => {
    if (window.confirm('Delete this submission?')) {
      try {
        await api.delete(`/admin/contacts/${id}`);
        loadData();
      } catch (error) { console.error('Error deleting contact:', error); }
    }
  };

  const handleSaveProduct = async (e) => {
    // Prevent any default behavior if called from an event
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (uploading) {
      alert('Please wait for image uploads to complete');
      return false;
    }
    
    // Validate required fields
    if (!productForm.name || !productForm.price) {
      alert('Please fill in Product Name and Price');
      return false;
    }
    
    setUploading(true);
    
    try {
      // Upload all new files first
      const uploadedUrls = [];
      for (let i = 0; i < productImageFiles.length; i++) {
        const file = productImageFiles[i];
        if (file && file instanceof File) {
          // This is a new file, upload it
          try {
            const url = await uploadImage(file);
            uploadedUrls[i] = url;
          } catch (uploadError) {
            console.error('Failed to upload file:', file.name, uploadError);
            alert(`Failed to upload ${file.name}. Please try again.`);
            setUploading(false);
            return false;
          }
        } else {
          // This is an existing URL or empty
          uploadedUrls[i] = productImages[i];
        }
      }
      
      const images = uploadedUrls.filter(u => u && u.trim() && !u.startsWith('data:'));
      
      const product = { 
        ...productForm,
        model: productForm.model || slugify(productForm.name),
        price: parseFloat(productForm.price) || 0, 
        oldPrice: parseFloat(productForm.oldPrice) || 0, 
        discount: parseFloat(productForm.discount) || 0,
        stockCount: productForm.stockCount !== '' ? parseInt(productForm.stockCount) : null, 
        image: images[0] || '', 
        images,
        linkedSpareParts: productForm.linkedSpareParts || []
      };
      
      const id = editingProduct?._id || editingProduct?.id;
      if (editingProduct && id) {
        await api.put(`/admin/products/${id}`, product);
        setSaveMsg('Product updated successfully!');
      } else {
        await api.post('/admin/products', product);
        setSaveMsg('Product added successfully!');
      }
      
      setTimeout(() => setSaveMsg(''), 3000);
      setEditingProduct(null);
      setProductImages(['']);
      setProductImageFiles([null]);
      setProductForm({ name: '', brand: '', model: '', description: '', price: '', oldPrice: '', discount: '', discountType: 'percentage', range: '', inStock: true, stockCount: '', category: 'electric-scooters', subCategory: '', compatibleProducts: [], linkedSpareParts: [] });
      await loadData();
    } catch (error) {
      console.error('Error saving product:', error);
      setSaveMsg('Error: ' + (error.response?.data?.error || error.message));
    } finally {
      setUploading(false);
    }
    
    return false;
  };

  const handleEditProduct = (p) => {
    setEditingProduct(p);
    window.scrollTo(0, 0);
    setProductForm({ 
      name: p.name || '', 
      brand: p.brand || '', 
      model: p.model || '', 
      description: p.description || '', 
      price: p.price || '', 
      oldPrice: p.oldPrice || '',
      discount: p.discount || '', 
      discountType: p.discountType || 'percentage',
      range: p.range || '', 
      inStock: p.inStock !== false, 
      stockCount: p.stockCount != null ? p.stockCount : '', 
      category: p.category || 'electric-scooters',
      subCategory: p.subCategory || '',
      compatibleProducts: p.compatibleProducts || [],
      linkedSpareParts: p.category !== 'spare-parts' 
        ? products.filter(sp => sp.category === 'spare-parts' && (sp.compatibleProducts || []).includes(p._id || p.id)).map(sp => sp._id || sp.id)
        : []
    });
    const imgs = p.images?.length ? [...p.images] : [p.image || ''];
    setProductImages(imgs);
    setProductImageFiles(imgs.map(() => null));
  };

  const handleSaveTopline = async () => {
    try {
      const tl = JSON.parse(toplineJson);
      await api.put('/admin/header', { topline: tl });
      setSaveMsg('Topline saved!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch { setSaveMsg('Error: invalid JSON'); }
  };

  const handleSaveMenuItem = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...menuForm };
      if (!editingMenu) payload.order = navMenu.length > 0 ? Math.max(...navMenu.map(m => m.order)) + 1 : 0;
      if (editingMenu) {
        await api.put(`/admin/menu/${editingMenu._id}`, payload);
      } else {
        await api.post('/admin/menu', payload);
      }
      setEditingMenu(null);
      setMenuForm({ name: '', path: '', order: 0, enabled: true, submenu: [] });
      setSaveMsg('Menu saved!');
      setTimeout(() => setSaveMsg(''), 3000);
      loadData();
    } catch { setSaveMsg('Error saving menu item'); }
  };

  const handleEditMenuItem = (item) => { // eslint-disable-line no-unused-vars
    setEditingMenu(item);
    setMenuForm({ name: item.name, path: item.path, order: item.order, enabled: item.enabled });
    setSubmenuJson(JSON.stringify(item.submenu || [], null, 2));
  };

  const handleDeleteMenuItem = async (id) => {
    if (window.confirm('Delete this menu item?')) {
      await api.delete(`/admin/menu/${id}`);
      loadData();
    }
  };

  const handleToggleMenu = async (item) => {
    try {
      await api.put(`/admin/menu/${item._id}`, { enabled: !item.enabled });
      setNavMenu(prev => prev.map(m => m._id === item._id ? { ...m, enabled: !m.enabled } : m));
    } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleMoveMenu = async (index, direction) => {
    const sorted = [...navMenu].sort((a, b) => a.order - b.order);
    const swapIdx = index + direction;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const item1 = sorted[index];
    const item2 = sorted[swapIdx];
    const newOrder1 = item2.order;
    const newOrder2 = item1.order;
    try {
      await Promise.all([
        api.put(`/admin/menu/${item1._id}`, { order: newOrder1 }),
        api.put(`/admin/menu/${item2._id}`, { order: newOrder2 }),
      ]);
      setNavMenu(prev => prev.map(m => {
        if (m._id === item1._id) return { ...m, order: newOrder1 };
        if (m._id === item2._id) return { ...m, order: newOrder2 };
        return m;
      }));
    } catch (e) { setSaveMsg('Error reordering: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSaveFooter = async () => {
    try {
      await api.put('/admin/footer', footerData);
      setSaveMsg('Footer saved!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSaveHome = async () => {
    try {
      await api.post('/admin/content/home', homeData);
      setSaveMsg('Home Content saved!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSaveGateways = async () => {
    try {
      await api.put('/admin/payment-gateways', gwSettings);
      setGwMsg('Payment gateway settings saved!');
      setTimeout(() => setGwMsg(''), 3000);
    } catch (e) { setGwMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSaveContactSettings = async () => {
    try {
      await api.put('/admin/content/contact', contactSettings);
      setContactMsg('Contact page saved!');
      setTimeout(() => setContactMsg(''), 3000);
    } catch (e) { setContactMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const slugify = (t) => t.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');


  const EMPTY_SPLIT = () => ({ type: 'split-row', heading: '', image: '', body: [{ type: 'paragraph', value: '' }] });
  const EMPTY_CARD  = () => ({ _productId: '' });

  const handleSaveBlogPost = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...blogForm, slug: blogForm.slug || slugify(blogForm.title), sections: blogSections, content: '' };
      if (editingPost) {
        await api.put('/admin/blog/' + editingPost._id, payload);
        setBlogMsg('Post updated!');
      } else {
        await api.post('/admin/blog', payload);
        setBlogMsg('Post created!');
      }
      setEditingPost(null);
      setBlogForm({ title: '', slug: '', excerpt: '', image: '', category: '', author: '', published: true });
      setBlogSections([]);
      setTimeout(() => setBlogMsg(''), 3000);
      loadData();
    } catch (e) { setBlogMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleEditPost = (p) => {
    setEditingPost(p);
    setBlogForm({ title: p.title, slug: p.slug, excerpt: p.excerpt || '', image: p.image || '', category: p.category || '', author: p.author || '', published: p.published !== false });
    let sections = p.sections && p.sections.length
      ? JSON.parse(JSON.stringify(p.sections))
      : parseContentToAdminSections(p.content || '');
    // For any full section, try to match legacy cards to products by name
    sections = sections.map(sec => {
      if (sec.type !== 'full') return sec;
      return {
        ...sec,
        cards: (sec.cards || []).map(card => {
          if (card._productId) return card;
          const cardWords = (card.name || '').toLowerCase().replace(/[^a-z0-9 ]/g, '').split(' ').filter(w => w.length > 2);
          const match = allProducts.find(prod => {
            const prodName = (prod.name || '').toLowerCase();
            const hits = cardWords.filter(w => prodName.includes(w));
            return hits.length >= Math.min(cardWords.length, 2);
          });
          return { _productId: match ? (match._id || match.id) : '' };
        })
      };
    });
    setBlogSections(sections);
  };

  const handleDeletePost = async (id) => {
    if (window.confirm('Delete this post?')) {
      await api.delete('/admin/blog/' + id);
      loadData();
    }
  };

  const updateSection = (i, patch) => setBlogSections(ss => ss.map((s, idx) => idx === i ? { ...s, ...patch } : s));
  const removeSection = (i) => setBlogSections(ss => ss.filter((_, idx) => idx !== i));
  const addSection = (type) => {
    if (type === 'split-row') setBlogSections(ss => [...ss, EMPTY_SPLIT()]);
    else if (type === 'full') setBlogSections(ss => [...ss, { type: 'full', heading: '', subheading: '', cards: [EMPTY_CARD()] }]);
    else if (type === 'features') setBlogSections(ss => [...ss, { type: 'features', heading: 'Why Choose Green Wheels', items: [{ icon: '', heading: '', text: '' }] }]);
    else if (type === 'cta') setBlogSections(ss => [...ss, { type: 'cta', heading: '', text: '' }]);
  };

  const updateSplitBody = (si, bi, patch) => setBlogSections(ss => ss.map((s, idx) => {
    if (idx !== si) return s;
    return { ...s, body: s.body.map((b, bidx) => bidx === bi ? { ...b, ...patch } : b) };
  }));
  const addSplitBody = (si, type) => setBlogSections(ss => ss.map((s, idx) => {
    if (idx !== si) return s;
    const item = type === 'list' ? { type: 'list', value: '' } : { type: 'paragraph', value: '' };
    return { ...s, body: [...s.body, item] };
  }));
  const removeSplitBody = (si, bi) => setBlogSections(ss => ss.map((s, idx) => {
    if (idx !== si) return s;
    return { ...s, body: s.body.filter((_, bidx) => bidx !== bi) };
  }));

  const updateCard = (si, ci, patch) => setBlogSections(ss => ss.map((s, idx) => {
    if (idx !== si) return s;
    return { ...s, cards: s.cards.map((c, cidx) => cidx === ci ? { ...c, ...patch } : c) };
  }));
  const addCard = (si) => setBlogSections(ss => ss.map((s, idx) => idx !== si ? s : { ...s, cards: [...s.cards, EMPTY_CARD()] }));
  const removeCard = (si, ci) => setBlogSections(ss => ss.map((s, idx) => idx !== si ? s : { ...s, cards: s.cards.filter((_, cidx) => cidx !== ci) }));

  const updateFeature = (si, fi, patch) => setBlogSections(ss => ss.map((s, idx) => {
    if (idx !== si) return s;
    return { ...s, items: s.items.map((f, fidx) => fidx === fi ? { ...f, ...patch } : f) };
  }));
  const addFeature = (si) => setBlogSections(ss => ss.map((s, idx) => idx !== si ? s : { ...s, items: [...s.items, { icon: '', heading: '', text: '' }] }));
  const removeFeature = (si, fi) => setBlogSections(ss => ss.map((s, idx) => idx !== si ? s : { ...s, items: s.items.filter((_, fidx) => fidx !== fi) }));
;

  // Parse legacy HTML content into admin-editable sections
  const parseContentToAdminSections = (html) => {
    if (!html) return [];
    const sections = [];
    const doc = new DOMParser().parseFromString(html, 'text/html');

    doc.querySelectorAll('.split-row').forEach(row => {
      const imgEl = row.querySelector('.split-image img');
      const heading = row.querySelector('.split-text h2')?.textContent?.trim() || '';
      const body = [];
      row.querySelectorAll('.split-text > p, .split-text > ul').forEach(el => {
        if (el.tagName === 'P') {
          body.push({ type: 'paragraph', value: el.textContent.trim() });
        } else if (el.tagName === 'UL') {
          const items = [...el.querySelectorAll('li')].map(li => li.textContent.trim());
          body.push({ type: 'list', value: items.join('\n') });
        }
      });
      sections.push({
        type: 'split-row',
        heading,
        image: imgEl ? imgEl.getAttribute('src') || '' : '',
        body: body.length ? body : [{ type: 'paragraph', value: '' }]
      });
    });

    doc.querySelectorAll('.full-section').forEach(sec => {
      const heading = sec.querySelector('h2')?.textContent?.trim() || '';
      const subheading = sec.querySelector(':scope > p')?.textContent?.trim() || '';
      const cards = [...sec.querySelectorAll('.product-card')].map(card => ({
        name:     card.querySelector('h4')?.textContent?.trim() || '',
        specs:    card.querySelector('.specs')?.textContent?.trim() || '',
        price:    card.querySelector('.price .current')?.textContent?.trim() || '',
        oldPrice: card.querySelector('.price .original')?.textContent?.trim() || '',
        image:    card.querySelector('img')?.getAttribute('src') || '',
        link:     card.querySelector('.btn-shop')?.getAttribute('href') || '',
      }));
      if (cards.length) sections.push({ type: 'full', heading, subheading, cards });
    });

    const featBar = doc.querySelector('.features-bar');
    if (featBar) {
      sections.push({
        type: 'features',
        heading: featBar.querySelector('h2')?.textContent?.trim() || 'Why Choose Green Wheels',
        items: [...featBar.querySelectorAll('.feature-item')].map(f => ({
          icon:    f.querySelector('.icon')?.textContent?.trim() || '',
          heading: f.querySelector('h4')?.textContent?.trim() || '',
          text:    f.querySelector('p')?.textContent?.trim() || '',
        })),
      });
    }

    const cta = doc.querySelector('.cta-section');
    if (cta) {
      sections.push({
        type:    'cta',
        heading: cta.querySelector('h2')?.textContent?.trim() || '',
        text:    cta.querySelector('p')?.textContent?.trim() || '',
      });
    }

    return sections;
  };

  const handleSaveSlides = async () => {
    try {
      await api.put('/admin/slides', slides);
      setSaveMsg('Slides saved!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleSlideUpload = async (i, field, file) => {
    if (!file) return;
    setSlideUploading(u => ({ ...u, [`${i}_${field}`]: true }));
    try {
      const url = await uploadImage(file);
      setSlides(s => s.map((sl, idx) => idx === i ? { ...sl, [field]: url } : sl));
    } catch { alert('Upload failed'); }
    setSlideUploading(u => ({ ...u, [`${i}_${field}`]: false }));
  };

  const addSlide = () => setSlides(s => [...s, { image: '', imageMobile: '', title: '', subtitle: '', cta: '', ctaLink: '' }]);
  const removeSlide = (i) => setSlides(s => s.filter((_, idx) => idx !== i));
  const updateSlide = (i, field, val) => setSlides(s => s.map((sl, idx) => idx === i ? { ...sl, [field]: val } : sl));
  const moveSlide = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= slides.length) return;
    const s = [...slides];
    [s[i], s[j]] = [s[j], s[i]];
    setSlides(s);
  };

  const handleSaveBranding = async () => {
    try {
      await api.put('/admin/branding', branding);
      document.title = branding.siteName || document.title;
      if (branding.favicon) {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) { link = document.createElement('link'); link.rel = 'icon'; document.head.appendChild(link); }
        link.href = getImageUrl(branding.favicon);
      }
      setSaveMsg('Branding saved!');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
  };

  const handleBrandingUpload = async (field, file) => {
    if (!file) return;
    setBrandingUploading(u => ({ ...u, [field]: true }));
    try {
      const url = await uploadImage(file);
      setBranding(b => ({ ...b, [field]: url }));
    } catch { alert('Upload failed'); }
    setBrandingUploading(u => ({ ...u, [field]: false }));
  };

  return (
    <div className="admin-page section">
      <div className="container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h1 style={{ margin: 0 }}>Admin Panel</h1>
            <span style={{ fontSize: 13, color: '#888' }}>
              Signed in as <strong>{username}</strong>
              <span style={{ marginLeft: 8, padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, background: role === 'superadmin' ? '#fce4ec' : role === 'admin' ? '#e3f2fd' : '#f3e5f5', color: role === 'superadmin' ? '#c62828' : role === 'admin' ? '#1565c0' : '#6a1b9a', textTransform: 'uppercase' }}>{role}</span>
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setActiveTab('changePassword')} className="btn-small" style={{ background: '#555' }}>Change Password</button>
            <button onClick={onLogout} className="btn-small btn-danger">Logout</button>
          </div>
        </div>

        <div className="admin-layout">
        
        <div className="admin-nav">
          <div className="admin-nav-group">
            <span className="admin-nav-label">Sales</span>
            {can('orders') && <button onClick={() => setActiveTab('orders')} className={activeTab === 'orders' ? 'active' : ''}><span className="nav-icon">🛒</span>Orders</button>}
            {can('rentals') && <button onClick={() => setActiveTab('rentals')} className={activeTab === 'rentals' ? 'active' : ''}><span className="nav-icon">🚲</span>Rentals</button>}
            {can('contacts') && <button onClick={() => setActiveTab('contacts')} className={activeTab === 'contacts' ? 'active' : ''}><span className="nav-icon">✉️</span>Enquiries</button>}
            {can('customers') && <button onClick={() => setActiveTab('customers')} className={activeTab === 'customers' ? 'active' : ''}><span className="nav-icon">👤</span>Customers</button>}
          </div>
          <div className="admin-nav-group">
            <span className="admin-nav-label">Catalogue</span>
            {can('products') && <button onClick={() => setActiveTab('products')} className={activeTab === 'products' ? 'active' : ''}><span className="nav-icon">📦</span>Products</button>}
            {can('categories') && <button onClick={() => setActiveTab('categories')} className={activeTab === 'categories' ? 'active' : ''}><span className="nav-icon">📁</span>Categories</button>}
            {can('blog') && <button onClick={() => setActiveTab('blog')} className={activeTab === 'blog' ? 'active' : ''}><span className="nav-icon">📝</span>Blog</button>}
            {can('biketowork') && <button onClick={() => setActiveTab('biketowork')} className={activeTab === 'biketowork' ? 'active' : ''}><span className="nav-icon">🚲</span>Bike to Work</button>}
          </div>
          <div className="admin-nav-group">
            <span className="admin-nav-label">Content</span>
            {can('home') && <button onClick={() => setActiveTab('home')} className={activeTab === 'home' ? 'active' : ''}><span className="nav-icon">🏠</span>Home</button>}
            {can('home') && <button onClick={() => setActiveTab('about')} className={activeTab === 'about' ? 'active' : ''}><span className="nav-icon">ℹ️</span>About Page</button>}
            {can('slides') && <button onClick={() => setActiveTab('slides')} className={activeTab === 'slides' ? 'active' : ''}><span className="nav-icon">🖼️</span>Hero Slides</button>}
            {can('reviews') && <button onClick={() => setActiveTab('reviews')} className={activeTab === 'reviews' ? 'active' : ''}><span className="nav-icon">⭐</span>Reviews</button>}
            {can('testimonials') && <button onClick={() => setActiveTab('testimonials')} className={activeTab === 'testimonials' ? 'active' : ''}><span className="nav-icon">💬</span>Testimonials</button>}
          </div>
          <div className="admin-nav-group">
            <span className="admin-nav-label">Site</span>
            {can('branding') && <button onClick={() => setActiveTab('branding')} className={activeTab === 'branding' ? 'active' : ''}><span className="nav-icon">🎨</span>Branding</button>}
            {can('navigation') && <button onClick={() => setActiveTab('navigation')} className={activeTab === 'navigation' ? 'active' : ''}><span className="nav-icon">🔗</span>Navigation</button>}
            {can('footer') && <button onClick={() => setActiveTab('footer')} className={activeTab === 'footer' ? 'active' : ''}><span className="nav-icon">📄</span>Footer</button>}
            {can('contactSettings') && <button onClick={() => setActiveTab('contactSettings')} className={activeTab === 'contactSettings' ? 'active' : ''}><span className="nav-icon">📍</span>Contact Page</button>}
            {can('gateways') && <button onClick={() => setActiveTab('gateways')} className={activeTab === 'gateways' ? 'active' : ''}><span className="nav-icon">💳</span>Payment</button>}
            {can('delivery') && <button onClick={() => setActiveTab('delivery')} className={activeTab === 'delivery' ? 'active' : ''}><span className="nav-icon">🚚</span>Delivery</button>}
            {can('support') && <button onClick={() => setActiveTab('support')} className={activeTab === 'support' ? 'active' : ''}><span className="nav-icon">🤝</span>Support Page</button>}
            {can('services') && <button onClick={() => setActiveTab('services')} className={activeTab === 'services' ? 'active' : ''}><span className="nav-icon">🔧</span>Services Page</button>}
           </div>
          <div className="admin-nav-group">
            <span className="admin-nav-label">Communication</span>
            {can('smtp') && <button onClick={() => setActiveTab('smtp')} className={activeTab === 'smtp' ? 'active' : ''}><span className="nav-icon">📧</span>Email / SMTP</button>}
            {can('twilio') && <button onClick={() => setActiveTab('twilio')} className={activeTab === 'twilio' ? 'active' : ''}><span className="nav-icon">📱</span>WhatsApp / Twilio</button>}
          </div>
          {role === 'superadmin' && (
            <div className="admin-nav-group">
              <span className="admin-nav-label">System</span>
              <button onClick={() => setActiveTab('adminUsers')} className={activeTab === 'adminUsers' ? 'active' : ''}><span className="nav-icon">👥</span>Admin Users</button>
              <button onClick={() => setActiveTab('dbSettings')} className={activeTab === 'dbSettings' ? 'active' : ''}><span className="nav-icon">🗄️</span>Database</button>
            </div>
          )}
        </div>

        <div className="admin-main">

        {activeTab === 'orders' && (
          <div className="admin-content">
            <h2>Orders</h2>

            {/* Order Detail Modal */}
            {orderModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setOrderModal(null)}>
                <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                  <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 17 }}>Order Details</h3>
                      <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#888' }}>{orderModal.orderID}</span>
                    </div>
                    <button onClick={() => setOrderModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888', lineHeight: 1 }}>✕</button>
                  </div>
                  <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 18 }}>

                    {/* Customer */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 8, letterSpacing: 1 }}>Customer</div>
                      <div style={{ fontSize: 14 }}><strong>{orderModal.customer?.name}</strong></div>
                      {orderModal.customer?.email && <div style={{ fontSize: 13, color: '#555' }}>{orderModal.customer.email}</div>}
                      {orderModal.customer?.mobile && <div style={{ fontSize: 13, color: '#555' }}>{orderModal.customer.mobile}</div>}
                      {orderModal.customer?.address?.line1 && (
                        <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>
                          {orderModal.customer.address.line1}{orderModal.customer.address.line2 ? ', ' + orderModal.customer.address.line2 : ''}<br />
                          {[orderModal.customer.address.city, orderModal.customer.address.county, orderModal.customer.address.postcode, orderModal.customer.address.country].filter(Boolean).join(', ')}
                        </div>
                      )}
                    </div>

                    {/* Order Info */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 8, letterSpacing: 1 }}>Order Info</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 16px', fontSize: 13 }}>
                        <div><span style={{ color: '#888' }}>Date:</span> {new Date(orderModal.createdAt).toLocaleString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</div>
                        <div><span style={{ color: '#888' }}>Gateway:</span> {orderModal.gateway === 'stripe' ? 'Stripe' : 'PayPal'}</div>
                        <div><span style={{ color: '#888' }}>Amount:</span> <strong>{orderModal.currency} {orderModal.amount}</strong></div>
                        {orderModal.paymentId && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#888' }}>Payment ID:</span> <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{orderModal.paymentId}</span></div>}
                        {orderModal.delivery && <div><span style={{ color: '#888' }}>Delivery:</span> {orderModal.delivery.type === 'registered' ? 'Registered' : 'Non-Registered'}{orderModal.delivery.fee > 0 ? ` (${orderModal.currency}${orderModal.delivery.fee})` : ' (Free)'}</div>}
                        {orderModal.delivery?.estimatedDate && <div><span style={{ color: '#888' }}>Est. Delivery:</span> <strong>{orderModal.delivery.estimatedDate}</strong></div>}
                      </div>
                    </div>

                    {/* Items */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 8, letterSpacing: 1 }}>Items</div>
                      {(orderModal.items || []).map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                          {item.image && <img src={getImageUrl(item.image)} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600 }}>{item.name}</div>
                            {item.model && <div style={{ fontSize: 12, color: '#888' }}>{item.model}</div>}
                          </div>
                          <div style={{ textAlign: 'right', fontSize: 13 }}>
                            <div style={{ color: '#888' }}>×{item.qty}</div>
                            <div style={{ fontWeight: 700 }}>{orderModal.currency} {item.price}</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Status & Notes actions */}
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 8, letterSpacing: 1 }}>Status</div>
                      <select
                        value={orderModal.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          try {
                            await api.put(`/admin/orders/${orderModal._id}`, { status: newStatus });
                            setOrders(prev => prev.map(o => o._id === orderModal._id ? { ...o, status: newStatus } : o));
                            setOrderModal(m => ({ ...m, status: newStatus }));
                          } catch (err) { alert('Error: ' + err.message); }
                        }}
                        style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid #ddd', fontSize: 14, fontWeight: 600, marginBottom: 16,
                          background: { pending: '#fff8e1', processing: '#e3f2fd', shipped: '#e8f5e9', delivered: '#e8f5e9', cancelled: '#fce4ec', refunded: '#f3e5f5', return: '#ffebee', reshipment: '#e1f5fe' }[orderModal.status] || '#fff',
                          color: { pending: '#f57c00', processing: '#1565c0', shipped: '#2e7d32', delivered: '#1b5e20', cancelled: '#c62828', refunded: '#6a1b9a', return: '#d84315', reshipment: '#0277bd' }[orderModal.status] || '#333'
                        }}
                      >
                        {['pending','processing','shipped','delivered','cancelled','refunded','return','reshipment'].map(s => (
                          <option key={s} value={s}>{s === 'reshipment' ? 'Re-shipment' : s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>

                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 8, letterSpacing: 1 }}>Notes</div>
                      <textarea
                        rows={3}
                        placeholder="Add note..."
                        defaultValue={orderModal.notes || ''}
                        key={orderModal._id}
                        onBlur={async (e) => {
                          if (e.target.value === (orderModal.notes || '')) return;
                          try {
                            await api.put(`/admin/orders/${orderModal._id}`, { notes: e.target.value });
                            setOrders(prev => prev.map(o => o._id === orderModal._id ? { ...o, notes: e.target.value } : o));
                            setOrderModal(m => ({ ...m, notes: e.target.value }));
                          } catch {}
                        }}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, marginBottom: 16, resize: 'vertical' }}
                      />

                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 8, letterSpacing: 1 }}>Actions</div>
                      <button
                        className="btn-small btn-danger"
                        onClick={async () => {
                          if (!window.confirm('Delete this order?')) return;
                          await api.delete(`/admin/orders/${orderModal._id}`);
                          setOrders(prev => prev.filter(o => o._id !== orderModal._id));
                          setOrderModal(null);
                        }}
                      >Delete Order</button>
                    </div>

                  </div>
                </div>
              </div>
            )}

            {orderMsg && <p className="save-msg">{orderMsg}</p>}

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
              <input
                placeholder="Search by name, order ID, email..."
                value={orderSearch}
                onChange={e => setOrderSearch(e.target.value)}
                style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 7, fontSize: 14, minWidth: 240 }}
              />
              {['all','pending','processing','shipped','delivered','cancelled','refunded','return','reshipment'].map(s => (
                <button
                  key={s}
                  className={`btn-small${orderFilter === s ? ' active' : ''}`}
                  style={{ textTransform: 'capitalize', background: orderFilter === s ? '#222' : '', color: orderFilter === s ? '#fff' : '' }}
                  onClick={() => setOrderFilter(s)}
                >{s === 'all' ? 'All' : s === 'reshipment' ? 'Re-shipment' : s.charAt(0).toUpperCase() + s.slice(1)}</button>
              ))}
              <button className="btn-small" onClick={loadData} style={{ marginLeft: 'auto' }}>↻ Refresh</button>
            </div>

            <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Amount</th>
                  <th>Gateway</th>
                  <th>Est. Delivery</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders
                  .filter(o => orderFilter === 'all' || o.status === orderFilter)
                  .filter(o => {
                    if (!orderSearch.trim()) return true;
                    const q = orderSearch.toLowerCase();
                    return (
                      o.orderID?.toLowerCase().includes(q) ||
                      o.customer?.name?.toLowerCase().includes(q) ||
                      o.customer?.email?.toLowerCase().includes(q) ||
                      o.customer?.mobile?.toLowerCase().includes(q)
                    );
                  })
                  .map((order) => (
                    <tr key={order._id}>
                      <td style={{ fontSize: 11, fontFamily: 'monospace', maxWidth: 90, wordBreak: 'break-all' }}>{order.orderID}</td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(order.createdAt).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}<br /><span style={{ color: '#999', fontSize: 11 }}>{new Date(order.createdAt).toLocaleTimeString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</span></td>
                      <td style={{ fontSize: 13 }}><strong>{order.customer?.name}</strong></td>
                      <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{order.currency} {order.amount}</td>
                      <td>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                          background: order.gateway === 'stripe' ? '#f3e8ff' : '#e8f0fe',
                          color: order.gateway === 'stripe' ? '#7c3aed' : '#1a73e8' }}>
                          {order.gateway === 'stripe' ? 'Stripe' : 'PayPal'}
                        </span>
                      </td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        <input
                          type="date"
                          defaultValue={order.estimatedDelivery ? new Date(order.estimatedDelivery).toISOString().split('T')[0] : ''}
                          key={order._id + (order.estimatedDelivery || '')}
                          onBlur={async (e) => {
                            if (e.target.value === (order.estimatedDelivery ? new Date(order.estimatedDelivery).toISOString().split('T')[0] : '')) return;
                            try {
                              await api.put(`/admin/orders/${order._id}`, { estimatedDelivery: e.target.value || null });
                              setOrders(prev => prev.map(o => o._id === order._id ? { ...o, estimatedDelivery: e.target.value } : o));
                            } catch {}
                          }}
                          style={{ padding: '4px 6px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 12 }}
                        />
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <button className="btn-small" onClick={() => setOrderModal(order)}>View Details</button>
                        <button
                          className="btn-small btn-danger"
                          onClick={async () => {
                            if (!window.confirm('Delete this order?')) return;
                            await api.delete(`/admin/orders/${order._id}`);
                            setOrders(prev => prev.filter(o => o._id !== order._id));
                          }}
                        >Delete</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>

            {orders.filter(o => orderFilter === 'all' || o.status === orderFilter).length === 0 && (
              <p style={{ color: '#999', textAlign: 'center', padding: 32 }}>No orders found.</p>
            )}
          </div>
        )}

        {activeTab === 'rentals' && (
          <div className="admin-content">
            <h2>Rental Bookings</h2>
            {rentalMsg && <p className="save-msg">{rentalMsg}</p>}

            {/* Rental Detail Modal */}
            {rentalModal && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setRentalModal(null)}>
                <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
                  <div style={{ padding: '20px 24px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 17 }}>Rental Booking</h3>
                      <span style={{ fontSize: 12, fontFamily: 'monospace', color: '#888' }}>{rentalModal.bookingID}</span>
                    </div>
                    <button onClick={() => setRentalModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888' }}>✕</button>
                  </div>
                  <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#888', marginBottom: 6 }}>Customer</div>
                      <div style={{ fontSize: 14 }}><strong>{rentalModal.customer?.name}</strong></div>
                      {rentalModal.customer?.email && <div style={{ fontSize: 13, color: '#555' }}>{rentalModal.customer.email}</div>}
                      {rentalModal.customer?.mobile && <div style={{ fontSize: 13, color: '#555' }}>{rentalModal.customer.mobile}</div>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', fontSize: 13 }}>
                      <div><span style={{ color: '#888' }}>Plan:</span> <strong>{rentalModal.plan}</strong></div>
                      <div><span style={{ color: '#888' }}>Bike:</span> <strong>{rentalModal.bike}</strong></div>
                      <div><span style={{ color: '#888' }}>Amount:</span> <strong>{rentalModal.currency} {rentalModal.amount}</strong></div>
                      <div><span style={{ color: '#888' }}>Deposit:</span> <strong>{rentalModal.currency} {rentalModal.deposit}</strong></div>
                      <div><span style={{ color: '#888' }}>Start:</span> {rentalModal.startDate ? new Date(rentalModal.startDate).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" }) : '—'}</div>
                      <div><span style={{ color: '#888' }}>End:</span> {rentalModal.endDate ? new Date(rentalModal.endDate).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" }) : '—'}</div>
                      {rentalModal.trackingId && <div style={{ gridColumn: '1/-1' }}><span style={{ color: '#888' }}>Tracking ID:</span> <span style={{ fontFamily: 'monospace' }}>{rentalModal.trackingId}</span></div>}
                    </div>

                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#888', display: 'block', marginBottom: 6 }}>Status</label>
                      <select
                        value={rentalModal.status}
                        onChange={async (e) => {
                          const newStatus = e.target.value;
                          await api.put(`/admin/rental-bookings/${rentalModal._id}`, { status: newStatus });
                          setRentalBookings(prev => prev.map(b => b._id === rentalModal._id ? { ...b, status: newStatus } : b));
                          setRentalModal(m => ({ ...m, status: newStatus }));
                        }}
                        style={{ padding: '8px 12px', borderRadius: 6, border: '1.5px solid #ddd', fontSize: 14, fontWeight: 600 }}
                      >
                        {['pending','confirmed','active','completed','cancelled','overdue'].map(s => (
                          <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#888', display: 'block', marginBottom: 6 }}>Tracking ID</label>
                      <input
                        defaultValue={rentalModal.trackingId || ''}
                        placeholder="Enter tracking ID..."
                        onBlur={async (e) => {
                          if (e.target.value === (rentalModal.trackingId || '')) return;
                          await api.put(`/admin/rental-bookings/${rentalModal._id}`, { trackingId: e.target.value });
                          setRentalBookings(prev => prev.map(b => b._id === rentalModal._id ? { ...b, trackingId: e.target.value } : b));
                          setRentalModal(m => ({ ...m, trackingId: e.target.value }));
                        }}
                        style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }}
                      />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#888', display: 'block', marginBottom: 6 }}>Start Date</label>
                        <input type="date" defaultValue={rentalModal.startDate ? new Date(rentalModal.startDate).toISOString().split('T')[0] : ''}
                          onBlur={async (e) => { await api.put(`/admin/rental-bookings/${rentalModal._id}`, { startDate: e.target.value || null }); setRentalBookings(prev => prev.map(b => b._id === rentalModal._id ? { ...b, startDate: e.target.value } : b)); setRentalModal(m => ({ ...m, startDate: e.target.value })); }}
                          style={{ padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, fontWeight: 700, color: '#888', display: 'block', marginBottom: 6 }}>End Date</label>
                        <input type="date" defaultValue={rentalModal.endDate ? new Date(rentalModal.endDate).toISOString().split('T')[0] : ''}
                          onBlur={async (e) => { await api.put(`/admin/rental-bookings/${rentalModal._id}`, { endDate: e.target.value || null }); setRentalBookings(prev => prev.map(b => b._id === rentalModal._id ? { ...b, endDate: e.target.value } : b)); setRentalModal(m => ({ ...m, endDate: e.target.value })); }}
                          style={{ padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, width: '100%', boxSizing: 'border-box' }} />
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
                        <input type="checkbox" checked={rentalModal.depositReturned || false}
                          onChange={async (e) => {
                            await api.put(`/admin/rental-bookings/${rentalModal._id}`, { depositReturned: e.target.checked });
                            setRentalBookings(prev => prev.map(b => b._id === rentalModal._id ? { ...b, depositReturned: e.target.checked } : b));
                            setRentalModal(m => ({ ...m, depositReturned: e.target.checked }));
                          }} />
                        Deposit Returned ({rentalModal.currency} {rentalModal.deposit})
                      </label>
                    </div>

                    <div>
                      <label style={{ fontSize: 12, fontWeight: 700, color: '#888', display: 'block', marginBottom: 6 }}>Notes</label>
                      <textarea rows={3} defaultValue={rentalModal.notes || ''} placeholder="Add notes..."
                        onBlur={async (e) => {
                          if (e.target.value === (rentalModal.notes || '')) return;
                          await api.put(`/admin/rental-bookings/${rentalModal._id}`, { notes: e.target.value });
                          setRentalBookings(prev => prev.map(b => b._id === rentalModal._id ? { ...b, notes: e.target.value } : b));
                          setRentalModal(m => ({ ...m, notes: e.target.value }));
                        }}
                        style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, resize: 'vertical' }} />
                    </div>

                    <button className="btn-small btn-danger" onClick={async () => {
                      if (!window.confirm('Delete this booking?')) return;
                      await api.delete(`/admin/rental-bookings/${rentalModal._id}`);
                      setRentalBookings(prev => prev.filter(b => b._id !== rentalModal._id));
                      setRentalModal(null);
                    }}>Delete Booking</button>
                  </div>
                </div>
              </div>
            )}

            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 16, alignItems: 'center' }}>
              {['all','pending','confirmed','active','completed','cancelled','overdue'].map(s => (
                <button key={s} className={`btn-small${rentalFilter === s ? ' active' : ''}`}
                  style={{ textTransform: 'capitalize', background: rentalFilter === s ? '#222' : '', color: rentalFilter === s ? '#fff' : '' }}
                  onClick={() => setRentalFilter(s)}>{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}</button>
              ))}
              <button className="btn-small" onClick={loadData} style={{ marginLeft: 'auto' }}>↻ Refresh</button>
            </div>

            <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Booking ID</th>
                  <th>Date</th>
                  <th>Customer</th>
                  <th>Plan / Bike</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rentalBookings
                  .filter(b => rentalFilter === 'all' || b.status === rentalFilter)
                  .map(booking => (
                    <tr key={booking._id}>
                      <td style={{ fontSize: 11, fontFamily: 'monospace' }}>{booking.bookingID}</td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(booking.createdAt).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}<br /><span style={{ color: "#999", fontSize: 11 }}>{new Date(booking.createdAt).toLocaleTimeString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</span></td>
                      <td style={{ fontSize: 13 }}><strong>{booking.customer?.name}</strong></td>
                      <td style={{ fontSize: 12 }}>{booking.plan}<br /><span style={{ color: '#888' }}>{booking.bike}</span></td>
                      <td style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{booking.currency} {booking.amount}</td>
                      <td>
                        <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                          background: { pending: '#fff8e1', confirmed: '#e3f2fd', active: '#e8f5e9', completed: '#e8f5e9', cancelled: '#fce4ec', overdue: '#ffebee' }[booking.status] || '#f5f5f5',
                          color: { pending: '#f57c00', confirmed: '#1565c0', active: '#2e7d32', completed: '#1b5e20', cancelled: '#c62828', overdue: '#d84315' }[booking.status] || '#333'
                        }}>{booking.status?.charAt(0).toUpperCase() + booking.status?.slice(1)}</span>
                      </td>
                      <td>
                        <button className="btn-small" onClick={() => setRentalModal(booking)}>View</button>
                        <button className="btn-small btn-danger" onClick={async () => {
                          if (!window.confirm('Delete?')) return;
                          await api.delete(`/admin/rental-bookings/${booking._id}`);
                          setRentalBookings(prev => prev.filter(b => b._id !== booking._id));
                        }}>Delete</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>
            {rentalBookings.filter(b => rentalFilter === 'all' || b.status === rentalFilter).length === 0 && (
              <p style={{ color: '#999', textAlign: 'center', padding: 32 }}>No rental bookings found.</p>
            )}
          </div>
        )}

        {activeTab === 'products' && (
          <div className="admin-content">
            <h2>Manage Products</h2>
            {saveMsg && <p className="save-msg">{saveMsg}</p>}
            <div className="admin-form" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.type !== 'textarea' && e.target.type !== 'submit') { e.preventDefault(); } }}>
              <input placeholder="Product Name" value={productForm.name} onChange={e => setField('name', e.target.value)} required />
              <input placeholder="Brand" value={productForm.brand} onChange={e => setField('brand', e.target.value)} />
              <input placeholder="Model slug (e.g. aovopro-es80)" value={productForm.model} onChange={e => setField('model', e.target.value)} />
              <textarea placeholder="Description" value={productForm.description} onChange={e => setField('description', e.target.value)} required />
              
              <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: 16, marginBottom: 12 }}>
                <label style={{ display: 'block', marginBottom: 10, fontSize: 14, fontWeight: 700, color: '#333' }}>💰 Pricing & Discount</label>
                
                <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600 }}>Original Price ({currency})</label>
                    <input 
                      type="number" 
                      step="0.01"
                      placeholder="e.g. 999.00" 
                      value={productForm.oldPrice} 
                      onChange={e => {
                        const oldPrice = e.target.value;
                        setField('oldPrice', oldPrice);
                        
                        // Auto-calculate based on what fields are filled
                        const old = parseFloat(oldPrice);
                        const curr = parseFloat(productForm.price);
                        const disc = parseFloat(productForm.discount);
                        
                        if (old > 0 && disc > 0 && !curr) {
                          // Calculate sale price from original + discount
                          if (productForm.discountType === 'percentage') {
                            setField('price', (old * (1 - disc / 100)).toFixed(2));
                          } else {
                            setField('price', (old - disc).toFixed(2));
                          }
                        } else if (old > 0 && curr > 0 && old > curr) {
                          // Calculate discount from prices
                          const discPct = ((old - curr) / old * 100).toFixed(1);
                          setField('discount', discPct);
                          setField('discountType', 'percentage');
                        }
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600 }}>Sale Price ({currency}) *</label>
                    <input 
                      type="number" 
                      step="0.01"
                      placeholder="e.g. 799.00" 
                      value={productForm.price} 
                      onChange={e => {
                        const salePrice = e.target.value;
                        setField('price', salePrice);
                        
                        // Auto-calculate discount if original price exists
                        const old = parseFloat(productForm.oldPrice);
                        const curr = parseFloat(salePrice);
                        
                        if (old > 0 && curr > 0 && old > curr) {
                          const discPct = ((old - curr) / old * 100).toFixed(1);
                          setField('discount', discPct);
                          setField('discountType', 'percentage');
                        } else if (old > 0 && curr >= old) {
                          // Clear discount if sale price >= original
                          setField('discount', '');
                        }
                      }}
                      required 
                    />
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600 }}>Discount Amount</label>
                    <input 
                      type="number" 
                      step="0.01"
                      min="0"
                      placeholder="e.g. 20 or 200" 
                      value={productForm.discount} 
                      onChange={e => {
                        const discount = e.target.value;
                        setField('discount', discount);
                        
                        // Auto-calculate sale price if original price exists
                        const old = parseFloat(productForm.oldPrice);
                        const disc = parseFloat(discount);
                        
                        if (old > 0 && disc > 0) {
                          if (productForm.discountType === 'percentage') {
                            const salePrice = (old * (1 - disc / 100)).toFixed(2);
                            setField('price', salePrice);
                          } else {
                            const salePrice = (old - disc).toFixed(2);
                            setField('price', salePrice);
                          }
                        }
                      }}
                      style={{ flex: 1 }}
                    />
                  </div>
                  
                  <div style={{ width: 140 }}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600 }}>Type</label>
                    <select 
                      value={productForm.discountType} 
                      onChange={e => {
                        const newType = e.target.value;
                        setField('discountType', newType);
                        
                        // Recalculate sale price with new discount type
                        const old = parseFloat(productForm.oldPrice);
                        const disc = parseFloat(productForm.discount);
                        
                        if (old > 0 && disc > 0) {
                          if (newType === 'percentage') {
                            setField('price', (old * (1 - disc / 100)).toFixed(2));
                          } else {
                            setField('price', (old - disc).toFixed(2));
                          }
                        }
                      }}
                      style={{ width: '100%', padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 14 }}
                    >
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ({currency})</option>
                    </select>
                  </div>
                  
                  <button 
                    type="button" 
                    onClick={() => {
                      setField('oldPrice', '');
                      setField('discount', '');
                    }}
                    style={{ padding: '8px 14px', background: '#6c757d', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}
                  >
                    Clear Discount
                  </button>
                </div>
              </div>
              
              {(productForm.oldPrice && parseFloat(productForm.oldPrice) > parseFloat(productForm.price || 0)) && (
                <div style={{ background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)', border: '2px solid #66bb6a', borderRadius: 10, padding: '14px 18px', fontSize: 13, marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={{ fontSize: 20 }}>🎉</span>
                    <strong style={{ color: '#1b5e20', fontSize: 15 }}>Price Preview</strong>
                  </div>
                  <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>Original Price</div>
                      <div style={{ fontSize: 18, fontWeight: 600, textDecoration: 'line-through', color: '#757575' }}>{currency}{parseFloat(productForm.oldPrice || 0).toFixed(2)}</div>
                    </div>
                    <div style={{ fontSize: 24, color: '#66bb6a', fontWeight: 700 }}>→</div>
                    <div>
                      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>Sale Price</div>
                      <div style={{ fontSize: 22, fontWeight: 800, color: '#1b5e20' }}>{currency}{parseFloat(productForm.price || 0).toFixed(2)}</div>
                    </div>
                    <div style={{ marginLeft: 'auto', textAlign: 'right', background: '#fff', padding: '8px 16px', borderRadius: 8, border: '1px solid #a5d6a7' }}>
                      <div style={{ fontSize: 11, color: '#555', textTransform: 'uppercase', fontWeight: 600, marginBottom: 2 }}>Customer Saves</div>
                      <div style={{ fontSize: 18, fontWeight: 800, color: '#d84315' }}>
                        {currency}{(parseFloat(productForm.oldPrice || 0) - parseFloat(productForm.price || 0)).toFixed(2)}
                        <span style={{ fontSize: 14, marginLeft: 6, color: '#f57c00' }}>({((parseFloat(productForm.oldPrice || 0) - parseFloat(productForm.price || 0)) / parseFloat(productForm.oldPrice || 1) * 100).toFixed(1)}% OFF)</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div style={{ background: '#fff9e6', border: '1px solid #ffe082', borderRadius: 8, padding: '12px 16px', fontSize: 13, lineHeight: 1.7, marginBottom: 12 }}>
                <strong style={{ color: '#f57c00', display: 'block', marginBottom: 6 }}>💡 How to Use:</strong>
                <div style={{ color: '#5d4037' }}>
                  <strong>Method 1:</strong> Enter Original Price + Discount → Sale Price auto-calculates<br />
                  <strong>Method 2:</strong> Enter Original Price + Sale Price → Discount auto-calculates<br />
                  <strong>Method 3:</strong> Enter Sale Price only (no discount shown)
                </div>
              </div>
              
              <input placeholder="Range tag (e.g. 45 Km Range)" value={productForm.range} onChange={e => setField('range', e.target.value)} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={productForm.inStock} onChange={e => setField('inStock', e.target.checked)} />
                In Stock (uncheck to show Out of Stock)
              </label>
              <input
                type="number"
                min="0"
                placeholder="Stock Count (leave blank = unlimited)"
                value={productForm.stockCount}
                onChange={e => setField('stockCount', e.target.value)}
              />
              <select value={productForm.category} onChange={e => { setField('category', e.target.value); if (e.target.value !== 'spare-parts') { setField('subCategory', ''); setField('compatibleProducts', []); } }} required>
                {adminCategories.filter(c => c.key !== 'all').map(c => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
                {adminCategories.length === 0 && <>
                  <option value="electric-scooters">Electric Scooters</option>
                  <option value="electric-bikes">Electric Bikes</option>
                  <option value="spare-parts">Spare Parts</option>
                </>}
              </select>

              {productForm.category === 'spare-parts' && (
                <>
                  <label><strong>Spare Parts Sub-Category</strong></label>
                  <select value={productForm.subCategory || ''} onChange={e => { setField('subCategory', e.target.value); setField('compatibleProducts', []); }} required>
                    <option value="">— Select Sub-Category —</option>
                    <option value="electric-scooters">Electric Scooters</option>
                    <option value="electric-bikes">Electric Bikes</option>
                    <option value="other">Other</option>
                  </select>

                  {productForm.subCategory && productForm.subCategory !== "other" && (
                    <>
                      <label><strong>Compatible Products</strong> <span style={{ fontWeight: 400, color: '#888' }}>(select products this spare part fits)</span></label>
                      <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: 12, maxHeight: 240, overflowY: 'auto' }}>
                        {products
                          .filter(p => p.category === productForm.subCategory)
                          .map(p => (
                            <label key={p._id || p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 6, marginBottom: 4, background: (productForm.compatibleProducts || []).includes(p._id || p.id) ? '#e3f2fd' : 'transparent' }}>
                              <input
                                type="checkbox"
                                checked={(productForm.compatibleProducts || []).includes(p._id || p.id)}
                                onChange={e => {
                                  const productId = p._id || p.id;
                                  const current = productForm.compatibleProducts || [];
                                  setField('compatibleProducts', e.target.checked ? [...current, productId] : current.filter(id => id !== productId));
                                }}
                              />
                              {p.image && <img src={getImageUrl(p.image)} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} onError={e => { e.target.style.display = 'none'; }} />}
                              <span style={{ fontSize: 13, flex: 1 }}>{p.name}</span>
                              <span style={{ fontSize: 11, color: '#888' }}>{p.brand}</span>
                            </label>
                          ))}
                        {products.filter(p => p.category === productForm.subCategory).length === 0 && (
                          <p style={{ color: '#999', fontSize: 13, margin: 0, padding: 8, textAlign: 'center' }}>No {productForm.subCategory === 'electric-scooters' ? 'Electric Scooters' : 'Electric Bikes'} found. Add products first.</p>
                        )}
                      </div>
                      {(productForm.compatibleProducts || []).length > 0 && (
                        <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#2e7d32', marginTop: 8 }}>
                          ✓ Selected {(productForm.compatibleProducts || []).length} compatible product{(productForm.compatibleProducts || []).length !== 1 ? 's' : ''}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {productForm.category !== 'spare-parts' && (
                <>
                  <label><strong>Linked Spare Parts</strong> <span style={{ fontWeight: 400, color: '#888' }}>(select spare parts that fit this product)</span></label>
                  <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: 12, maxHeight: 240, overflowY: 'auto' }}>
                    {products
                      .filter(p => p.category === 'spare-parts')
                      .map(p => (
                        <label key={p._id || p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', cursor: 'pointer', borderRadius: 6, marginBottom: 4, background: (productForm.linkedSpareParts || []).includes(p._id || p.id) ? '#e8f5e9' : 'transparent' }}>
                          <input
                            type="checkbox"
                            checked={(productForm.linkedSpareParts || []).includes(p._id || p.id)}
                            onChange={e => {
                              const partId = p._id || p.id;
                              const current = productForm.linkedSpareParts || [];
                              setField('linkedSpareParts', e.target.checked ? [...current, partId] : current.filter(id => id !== partId));
                            }}
                          />
                          {p.image && <img src={getImageUrl(p.image)} alt="" style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} onError={e => { e.target.style.display = 'none'; }} />}
                          <span style={{ fontSize: 13, flex: 1 }}>{p.name}</span>
                        </label>
                      ))}
                    {products.filter(p => p.category === 'spare-parts').length === 0 && (
                      <p style={{ color: '#999', fontSize: 13, margin: 0, padding: 8, textAlign: 'center' }}>No spare parts found. Add spare parts first.</p>
                    )}
                  </div>
                  {(productForm.linkedSpareParts || []).length > 0 && (
                    <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 6, padding: '8px 12px', fontSize: 13, color: '#2e7d32', marginTop: 8 }}>
                      ✓ {(productForm.linkedSpareParts || []).length} spare part{(productForm.linkedSpareParts || []).length !== 1 ? 's' : ''} linked
                    </div>
                  )}
                </>
              )}

              <label><strong>Images</strong></label>
              {productImages.map((url, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input
                    placeholder={i === 0 ? 'Main image URL' : `Image ${i + 1} URL`}
                    value={url}
                    onChange={e => updateImageField(i, e.target.value)}
                    style={{ flex: 1 }}
                  />
                  <span className="btn-small" style={{ cursor: 'pointer', margin: 0, position: 'relative', display: 'inline-block' }} onClick={(e) => { 
                    e.preventDefault(); 
                    e.stopPropagation(); 
                    document.getElementById(`file-upload-${i}`)?.click();
                  }}>
                    📁
                  </span>
                  {url && <img src={getImageUrl(url)} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }} onError={e => { e.target.style.display = 'none'; }} />}
                  {productImages.length > 1 && (
                    <button type="button" className="btn-small btn-danger" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeImageField(i); }}>✕</button>
                  )}
                </div>
              ))}
              <button type="button" className="btn-small" onClick={(e) => { e.preventDefault(); addImageField(); }}>+ Add Image</button>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button 
                  type="button" 
                  className="btn" 
                  disabled={uploading} 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSaveProduct(e);
                  }} 
                  style={{ minWidth: 150 }}
                >
                  {uploading ? '⏳ Uploading...' : editingProduct ? '✓ Update Product' : '+ Add Product'}
                </button>
                {editingProduct && <button type="button" onClick={() => { setEditingProduct(null); setProductImages(['']); setProductImageFiles([null]); setProductForm({ name: '', brand: '', model: '', description: '', price: '', oldPrice: '', discount: '', discountType: 'percentage', range: '', inStock: true, stockCount: '', category: 'electric-scooters', subCategory: '', compatibleProducts: [], linkedSpareParts: [] }); }} className="btn">Cancel</button>}
              </div>
              
              {uploading && (
                <div style={{ background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8, padding: '12px 16px', marginTop: 12, fontSize: 13, color: '#1565c0' }}>
                  <strong>⏳ Uploading images and saving product...</strong>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#1976d2' }}>Please wait, do not close or refresh this page.</div>
                </div>
              )}
            </div>

            {/* Hidden file inputs - OUTSIDE form to prevent submission */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px', visibility: 'hidden', pointerEvents: 'none' }}>
              {productImages.map((url, i) => (
                <input
                  key={`file-${i}-${productImages.length}`}
                  id={`file-upload-${i}`}
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) {
                      handleFileSelect(i, file);
                      e.target.value = '';
                    }
                  }}
                />
              ))}
            </div>

            {products.length === 0 ? (
              <div style={{ background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 8, padding: '32px 24px', textAlign: 'center', marginTop: 24 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>📦</div>
                <h3 style={{ margin: '0 0 8px', color: '#495057' }}>No Products Yet</h3>
                <p style={{ color: '#6c757d', margin: 0, fontSize: 14 }}>Add your first product using the form above to get started.</p>
              </div>
            ) : (
            <>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, marginTop: 20, alignItems: 'center' }}>
              {['all','electric-scooters','electric-bikes','spare-parts'].map(c => (
                <button key={c} className={`btn-small${productCatFilter === c ? ' active' : ''}`}
                  style={{ textTransform: 'capitalize', background: productCatFilter === c ? '#222' : '', color: productCatFilter === c ? '#fff' : '' }}
                  onClick={() => setProductCatFilter(c)}>{c === 'all' ? 'All' : c.replace(/-/g, ' ')}</button>
              ))}
              <select value={productSort} onChange={e => setProductSort(e.target.value)} style={{ marginLeft: 'auto', padding: '6px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13 }}>
                <option value="order">Sort: Manual Order</option>
                <option value="name-asc">Sort: Name A–Z</option>
                <option value="name-desc">Sort: Name Z–A</option>
                <option value="price-asc">Sort: Price Low–High</option>
                <option value="price-desc">Sort: Price High–Low</option>
                <option value="date-new">Sort: Newest First</option>
                <option value="date-old">Sort: Oldest First</option>
              </select>
            </div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Name</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {products
                  .filter(p => productCatFilter === 'all' || p.category === productCatFilter)
                  .sort((a, b) => {
                    if (productSort === 'name-asc') return (a.name || '').localeCompare(b.name || '');
                    if (productSort === 'name-desc') return (b.name || '').localeCompare(a.name || '');
                    if (productSort === 'price-asc') return (a.price || 0) - (b.price || 0);
                    if (productSort === 'price-desc') return (b.price || 0) - (a.price || 0);
                    if (productSort === 'date-new') return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
                    if (productSort === 'date-old') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
                    return 0;
                  })
                  .map((product, i, filteredArr) => (
                  <tr key={product._id || product.id || i}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-small" disabled={i === 0 || productSort !== 'order'} onClick={async () => {
                        const id = product._id || product.id;
                        const prevId = filteredArr[i - 1]._id || filteredArr[i - 1].id;
                        const arr = [...products];
                        const realIdx = arr.findIndex(p => (p._id || p.id) === id);
                        const prevRealIdx = arr.findIndex(p => (p._id || p.id) === prevId);
                        [arr[prevRealIdx], arr[realIdx]] = [arr[realIdx], arr[prevRealIdx]];
                        setProducts(arr);
                        const order = arr.map((p, idx) => ({ id: p._id || p.id, sortOrder: idx }));
                        await api.put('/admin/products-reorder', { order });
                      }}>↑</button>
                      <button className="btn-small" disabled={i === filteredArr.length - 1 || productSort !== 'order'} onClick={async () => {
                        const id = product._id || product.id;
                        const nextId = filteredArr[i + 1]._id || filteredArr[i + 1].id;
                        const arr = [...products];
                        const realIdx = arr.findIndex(p => (p._id || p.id) === id);
                        const nextRealIdx = arr.findIndex(p => (p._id || p.id) === nextId);
                        [arr[realIdx], arr[nextRealIdx]] = [arr[nextRealIdx], arr[realIdx]];
                        setProducts(arr);
                        const order = arr.map((p, idx) => ({ id: p._id || p.id, sortOrder: idx }));
                        await api.put('/admin/products-reorder', { order });
                      }}>↓</button>
                    </td>
                    <td>{product.name}</td>
                    <td>{currency}{product.price}</td>
                    <td>
                      {product.inStock === false || product.stockCount === 0
                        ? <span style={{ color: '#dc3545', fontWeight: 700 }}>Out of Stock</span>
                        : product.stockCount != null
                          ? <span style={{ color: product.stockCount <= 10 ? '#e05c00' : '#00875a', fontWeight: 600 }}>{product.stockCount}</span>
                          : <span style={{ color: '#00875a' }}>In Stock</span>
                      }
                    </td>
                    <td>{product.category}</td>
                    <td>
                      <button onClick={() => handleEditProduct(product)} className="btn-small">Edit</button>
                      <button onClick={() => handleDeleteProduct(product._id || product.id)} className="btn-small btn-danger">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
            )}
          </div>
        )}

        {activeTab === 'testimonials' && (
          <div className="admin-content">
            <h2>Manage Testimonials</h2>
            <form onSubmit={handleSaveTestimonial} className="admin-form">
              <textarea placeholder="Testimonial text" rows={3} value={testimonialForm.text}
                onChange={e => setTestimonialForm(f => ({ ...f, text: e.target.value }))} required />
              <input placeholder="Author (e.g. Conor M., Dublin)" value={testimonialForm.author}
                onChange={e => setTestimonialForm(f => ({ ...f, author: e.target.value }))} required />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn">{editingTestimonial ? 'Update' : 'Add'} Testimonial</button>
                {editingTestimonial && <button type="button" className="btn" onClick={() => { setEditingTestimonial(null); setTestimonialForm({ text: '', author: '' }); }}>Cancel</button>}
              </div>
            </form>
            <table className="admin-table">
              <thead>
                <tr><th>Text</th><th>Author</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {testimonials.map((t, i) => (
                  <tr key={t._id || i}>
                    <td>{t.text}</td>
                    <td>{t.author}</td>
                    <td>
                      <button onClick={() => handleEditTestimonial(t)} className="btn-small">Edit</button>
                      <button onClick={() => handleDeleteTestimonial(t._id)} className="btn-small btn-danger">Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'customers' && (
          <div className="admin-content">
            <h2>Customers</h2>
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
              <input
                placeholder="Search by name, email, mobile..."
                value={customerSearch}
                onChange={e => setCustomerSearch(e.target.value)}
                style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 7, fontSize: 14, minWidth: 260 }}
              />
              <span style={{ fontSize: 13, color: '#888' }}>{customers.length} total</span>
              <button className="btn-small" onClick={loadData} style={{ marginLeft: 'auto' }}>↻ Refresh</button>
            </div>
            <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Mobile</th><th>Address</th><th>Joined</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {customers
                  .filter(c => {
                    if (!customerSearch.trim()) return true;
                    const q = customerSearch.toLowerCase();
                    return (c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.mobile?.toLowerCase().includes(q));
                  })
                  .map(c => (
                    <tr key={c._id}>
                      <td><strong>{c.name}</strong></td>
                      <td style={{ fontSize: 13 }}>{c.email || '—'}</td>
                      <td style={{ fontSize: 13 }}>{c.mobile || '—'}</td>
                      <td style={{ fontSize: 12, maxWidth: 200 }}>{c.address ? [c.address.line1, c.address.city, c.address.country].filter(Boolean).join(', ') : '—'}</td>
                      <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(c.createdAt).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}<br /><span style={{ color: "#999", fontSize: 11 }}>{new Date(c.createdAt).toLocaleTimeString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</span></td>
                      <td>
                        <button className="btn-small btn-danger" onClick={async () => {
                          if (!window.confirm(`Delete customer "${c.name}"?`)) return;
                          await api.delete(`/admin/customers/${c._id}`);
                          setCustomers(prev => prev.filter(u => u._id !== c._id));
                        }}>Delete</button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>
            {customers.filter(c => { if (!customerSearch.trim()) return true; const q = customerSearch.toLowerCase(); return (c.name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.mobile?.toLowerCase().includes(q)); }).length === 0 && (
              <p style={{ color: '#999', textAlign: 'center', padding: 32 }}>No customers found.</p>
            )}
          </div>
        )}

        {activeTab === 'contacts' && (
          <div className="admin-content">
            <h2>Contact Submissions</h2>
            <table className="admin-table">
              <thead>
                <tr><th>Name</th><th>Email</th><th>Phone</th><th>Message</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {contacts.map((contact, i) => (
                  <tr key={contact._id || i}>
                    <td>{contact.name}</td>
                    <td>{contact.email}</td>
                    <td>{contact.phone || '-'}</td>
                    <td>{contact.message}</td>
                    <td>{new Date(contact.createdAt).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}<br /><span style={{ color: "#999", fontSize: 11 }}>{new Date(contact.createdAt).toLocaleTimeString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</span></td>
                    <td><button onClick={() => handleDeleteContact(contact._id)} className="btn-small btn-danger">Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {activeTab === 'navigation' && (
          <div className="admin-content">
            <h2>Manage Navigation</h2>
            {saveMsg && <p className="save-msg">{saveMsg}</p>}

            {/* Topline */}
            <div className="admin-form" style={{ marginBottom: 32 }}>
              <label><strong>Topline Messages</strong> (JSON array of HTML strings)</label>
              <textarea rows={4} value={toplineJson} onChange={e => setToplineJson(e.target.value)} />
              <button className="btn" onClick={handleSaveTopline}>Save Topline</button>
            </div>

            {/* Menu Items */}
            <h3 style={{ marginBottom: 16 }}>{editingMenu ? `Editing: ${editingMenu.name}` : 'Add New Menu Item'}</h3>
            <form onSubmit={handleSaveMenuItem} className="admin-form" style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="Label (e.g. Spare Parts)" value={menuForm.name}
                  onChange={e => setMenuForm(f => ({ ...f, name: e.target.value }))} required style={{ flex: 2 }} />
                <input placeholder="Path (e.g. /products?category=spare-parts)" value={menuForm.path}
                  onChange={e => setMenuForm(f => ({ ...f, path: e.target.value }))} required style={{ flex: 3 }} />

                <label style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                  <input type="checkbox" checked={menuForm.enabled}
                    onChange={e => setMenuForm(f => ({ ...f, enabled: e.target.checked }))} /> Enabled
                </label>
              </div>

              {/* Submenu builder */}
              <label><strong>Submenu Items</strong></label>
              {(menuForm.submenu || []).map((sub, si) => (
                <div key={si} style={{ border: '1px solid #e0e0e0', borderRadius: 6, padding: 12, marginBottom: 8, background: '#fafafa' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <input placeholder="Submenu label" value={sub.name}
                      onChange={e => {
                        const s = [...menuForm.submenu];
                        s[si] = { ...s[si], name: e.target.value };
                        setMenuForm(f => ({ ...f, submenu: s }));
                      }} style={{ flex: '2 1 120px' }} />
                    <input placeholder="Submenu path" value={sub.path}
                      onChange={e => {
                        const s = [...menuForm.submenu];
                        s[si] = { ...s[si], path: e.target.value };
                        setMenuForm(f => ({ ...f, submenu: s }));
                      }} style={{ flex: '3 1 160px' }} />
                    <select
                      value=""
                      onChange={e => {
                        if (!e.target.value) return;
                        const prod = allProducts.find(p => (p._id || p.id) === e.target.value);
                        if (!prod) return;
                        const s = [...menuForm.submenu];
                        const prodSlug = prod.model || slugify(prod.name) || (prod._id || prod.id);
                        // For spare parts menu, use ?category=spare-parts&for=ID
                        const isSparePartsMenu = menuForm.path?.includes('spare-parts');
                        if (isSparePartsMenu) {
                          s[si] = { ...s[si], name: s[si].name || prod.name, path: `/products?category=spare-parts&for=${prod._id || prod.id}` };
                        } else {
                          s[si] = { ...s[si], name: s[si].name || prod.name, path: `/products/${prod.category}/${prodSlug}` };
                        }
                        setMenuForm(f => ({ ...f, submenu: s }));
                      }}
                      style={{ flex: '2 1 140px', padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, color: '#555' }}
                    >
                      <option value="">— Set path from product —</option>
                      {allProducts.map(p => (
                        <option key={p._id || p.id} value={p._id || p.id}>{p.name} ({p.category})</option>
                      ))}
                    </select>
                    <button type="button" className="btn-small" style={{ padding: '4px 8px' }} disabled={si === 0} onClick={() => { const s = [...menuForm.submenu]; [s[si-1], s[si]] = [s[si], s[si-1]]; setMenuForm(f => ({ ...f, submenu: s })); }}>↑</button>
                    <button type="button" className="btn-small" style={{ padding: '4px 8px' }} disabled={si === (menuForm.submenu||[]).length - 1} onClick={() => { const s = [...menuForm.submenu]; [s[si], s[si+1]] = [s[si+1], s[si]]; setMenuForm(f => ({ ...f, submenu: s })); }}>↓</button>
                    <button type="button" className="btn-small btn-danger"
                      onClick={() => setMenuForm(f => ({ ...f, submenu: f.submenu.filter((_, i) => i !== si) }))}>✕</button>
                  </div>

                  {/* Children (nested) */}
                  <div style={{ paddingLeft: 16 }}>
                    <label style={{ fontSize: 12, color: '#666' }}>Nested items (children)</label>
                    {(sub.children || []).map((child, ci) => (
                      <div key={ci} style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                        <input placeholder="Child label" value={child.name}
                          onChange={e => {
                            const s = [...menuForm.submenu];
                            const ch = [...(s[si].children || [])];
                            ch[ci] = { ...ch[ci], name: e.target.value };
                            s[si] = { ...s[si], children: ch };
                            setMenuForm(f => ({ ...f, submenu: s }));
                          }} style={{ flex: '2 1 120px' }} />
                        <input placeholder="Child path" value={child.path}
                          onChange={e => {
                            const s = [...menuForm.submenu];
                            const ch = [...(s[si].children || [])];
                            ch[ci] = { ...ch[ci], path: e.target.value };
                            s[si] = { ...s[si], children: ch };
                            setMenuForm(f => ({ ...f, submenu: s }));
                          }} style={{ flex: '3 1 160px' }} />
                        <select
                          value=""
                          onChange={e => {
                            if (!e.target.value) return;
                            const prod = allProducts.find(p => (p._id || p.id) === e.target.value);
                            if (!prod) return;
                            const s = [...menuForm.submenu];
                            const ch = [...(s[si].children || [])];
                            const childSlug = prod.model || slugify(prod.name) || (prod._id || prod.id);
                            const isSparePartsMenu = menuForm.path?.includes('spare-parts');
                            if (isSparePartsMenu) {
                              ch[ci] = { ...ch[ci], name: ch[ci].name || prod.name, path: `/products?category=spare-parts&for=${prod._id || prod.id}` };
                            } else {
                              ch[ci] = { ...ch[ci], name: ch[ci].name || prod.name, path: `/products/${prod.category}/${childSlug}` };
                            }
                            s[si] = { ...s[si], children: ch };
                            setMenuForm(f => ({ ...f, submenu: s }));
                          }}
                          style={{ flex: '2 1 140px', padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13, color: '#555' }}
                        >
                          <option value="">— Set path from product —</option>
                          {allProducts.map(p => (
                            <option key={p._id || p.id} value={p._id || p.id}>{p.name} ({p.category})</option>
                          ))}
                        </select>
                        <button type="button" className="btn-small" style={{ padding: '4px 8px' }} disabled={si === 0} onClick={() => { const s = [...menuForm.submenu]; [s[si-1], s[si]] = [s[si], s[si-1]]; setMenuForm(f => ({ ...f, submenu: s })); }}>↑</button>
                        <button type="button" className="btn-small" style={{ padding: '4px 8px' }} disabled={si === (menuForm.submenu||[]).length - 1} onClick={() => { const s = [...menuForm.submenu]; [s[si], s[si+1]] = [s[si+1], s[si]]; setMenuForm(f => ({ ...f, submenu: s })); }}>↓</button>
                        <button type="button" className="btn-small btn-danger"
                          onClick={() => {
                            const s = [...menuForm.submenu];
                            s[si] = { ...s[si], children: (s[si].children || []).filter((_, i) => i !== ci) };
                            setMenuForm(f => ({ ...f, submenu: s }));
                          }}>✕</button>
                      </div>
                    ))}
                    <button type="button" className="btn-small" style={{ fontSize: 11 }}
                      onClick={() => {
                        const s = [...menuForm.submenu];
                        s[si] = { ...s[si], children: [...(s[si].children || []), { name: '', path: '' }] };
                        setMenuForm(f => ({ ...f, submenu: s }));
                      }}>+ Add Child</button>
                  </div>
                </div>
              ))}
              <button type="button" className="btn-small"
                onClick={() => setMenuForm(f => ({ ...f, submenu: [...(f.submenu || []), { name: '', path: '', children: [] }] }))}>
                + Add Submenu Item
              </button>

              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="submit" className="btn">{editingMenu ? 'Update' : 'Add'} Menu Item</button>
                {editingMenu && (
                  <button type="button" className="btn" onClick={() => {
                    setEditingMenu(null);
                    setMenuForm({ name: '', path: '', order: 0, enabled: true, submenu: [] });
                  }}>Cancel</button>
                )}
              </div>
            </form>

            {/* Menu list */}
            <table className="admin-table">
              <thead><tr><th>Order</th><th>Label</th><th>Path</th><th>Submenu</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {[...navMenu].sort((a, b) => a.order - b.order).map((item, i) => (
                  <tr key={item._id || i}>
                    <td style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <button className="btn-small" onClick={() => handleMoveMenu(i, -1)} disabled={i === 0} style={{ padding: '2px 6px', fontSize: 14 }}>↑</button>
                      <span style={{ minWidth: 20, textAlign: 'center' }}>{item.order}</span>
                      <button className="btn-small" onClick={() => handleMoveMenu(i, 1)} disabled={i === navMenu.length - 1} style={{ padding: '2px 6px', fontSize: 14 }}>↓</button>
                    </td>
                    <td>{item.name}</td>
                    <td style={{ fontSize: 11 }}>{item.path}</td>
                    <td style={{ fontSize: 12 }}>
                      {(item.submenu || []).map((s, i) => (
                        <div key={i}>{s.name}{s.children?.length > 0 ? ` (${s.children.length} children)` : ''}</div>
                      ))}
                    </td>
                    <td><span style={{ color: item.enabled ? '#00d084' : '#999' }}>{item.enabled ? 'Visible' : 'Hidden'}</span></td>
                    <td>
                      <button className="btn-small" onClick={() => {
                        setEditingMenu(item);
                        setMenuForm({ name: item.name, path: item.path, order: item.order, enabled: item.enabled, submenu: JSON.parse(JSON.stringify(item.submenu || [])) });
                      }}>Edit</button>
                      <button className="btn-small" onClick={() => handleToggleMenu(item)}>{item.enabled ? 'Hide' : 'Show'}</button>
                      <button className="btn-small btn-danger" onClick={() => handleDeleteMenuItem(item._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'branding' && (
          <div className="admin-content">
            <h2>Site Branding</h2>
            {saveMsg && <p className="save-msg">{saveMsg}</p>}
            <div className="admin-form">
              <label><strong>Site Name</strong> (used as page title)</label>
              <input placeholder="e.g. Green Wheels Scooters" value={branding.siteName}
                onChange={e => setBranding(b => ({ ...b, siteName: e.target.value }))} />

              <label><strong>Currency Symbol</strong> (shown on all prices)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input placeholder="e.g. €, $, £, ₹" value={branding.currency || ''}
                  onChange={e => setBranding(b => ({ ...b, currency: e.target.value }))}
                  style={{ width: 100 }} />
                <span style={{ fontSize: 13, color: '#888' }}>Preview: <strong>{branding.currency || '€'}99.99</strong></span>
              </div>

              <div className="form-group">
                <label><strong>Timezone</strong> (used for all date/time displays)</label>
                <select value={branding.timezone || "Europe/Dublin"} onChange={e => setBranding(b => ({ ...b, timezone: e.target.value }))} style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 14 }}>
                  <option value="Europe/Dublin">Europe/Dublin (Ireland)</option>
                  <option value="Europe/London">Europe/London (UK)</option>
                  <option value="Europe/Paris">Europe/Paris (France/CET)</option>
                  <option value="Europe/Berlin">Europe/Berlin (Germany/CET)</option>
                  <option value="America/New_York">America/New_York (US Eastern)</option>
                  <option value="America/Chicago">America/Chicago (US Central)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (US Pacific)</option>
                  <option value="Asia/Dubai">Asia/Dubai (UAE)</option>
                  <option value="Asia/Kolkata">Asia/Kolkata (India)</option>
                  <option value="Australia/Sydney">Australia/Sydney (Australia)</option>
                </select>
              </div>

              <label><strong>Logo</strong></label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input placeholder="Logo URL or upload" value={branding.logo}
                  onChange={e => setBranding(b => ({ ...b, logo: e.target.value }))} style={{ flex: 1 }} />
                <button type="button" className="btn-small" style={{ cursor: 'pointer', margin: 0 }} onClick={() => document.getElementById('branding-logo-upload').click()}>
                  {brandingUploading.logo ? '...' : '📁'}
                </button>
                <input id="branding-logo-upload" type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { e.preventDefault(); e.stopPropagation(); if(e.target.files[0]) handleBrandingUpload('logo', e.target.files[0]); }} />
                  {branding.logo && <img src={getImageUrl(branding.logo)}
                    alt="logo preview" style={{ height: 48, borderRadius: 4, border: '1px solid #ddd' }}
                    onError={e => { e.target.style.display = 'none'; }} />}
                </div>

              <label><strong>Favicon</strong> (.ico or .png recommended)</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input placeholder="Favicon URL or upload" value={branding.favicon}
                  onChange={e => setBranding(b => ({ ...b, favicon: e.target.value }))} style={{ flex: 1 }} />
                <button type="button" className="btn-small" style={{ cursor: 'pointer', margin: 0 }} onClick={() => document.getElementById('branding-favicon-upload').click()}>
                  {brandingUploading.favicon ? '...' : '📁'}
                </button>
                <input id="branding-favicon-upload" type="file" accept="image/*,.ico" style={{ display: 'none' }}
                  onChange={e => { e.preventDefault(); e.stopPropagation(); if(e.target.files[0]) handleBrandingUpload('favicon', e.target.files[0]); }} />
                  {branding.favicon && <img src={getImageUrl(branding.favicon)}
                    alt="favicon preview" style={{ width: 32, height: 32, borderRadius: 4, border: '1px solid #ddd' }}
                    onError={e => { e.target.style.display = 'none'; }} />}
                </div>

              <h3 style={{ marginTop: 32, paddingTop: 24, borderTop: '2px solid #f0f0f0' }}>📊 Google Analytics & Tag Manager</h3>
              <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px' }}>Add your Google tracking IDs. Leave blank to disable.</p>

              <label><strong>Google Analytics 4 (GA4) Measurement ID</strong></label>
              <input placeholder="e.g. G-40EQ3W4E5P" value={branding.googleAnalyticsId || ''}
                onChange={e => setBranding(b => ({ ...b, googleAnalyticsId: e.target.value.trim() }))} style={{ fontFamily: 'monospace', fontSize: 13 }} />
              <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0' }}>Find it in Google Analytics → Admin → Data Streams → Measurement ID</p>

              <label><strong>Google Ads Conversion ID</strong></label>
              <input placeholder="e.g. AW-16811975421" value={branding.googleAdsId || ''}
                onChange={e => setBranding(b => ({ ...b, googleAdsId: e.target.value.trim() }))} style={{ fontFamily: 'monospace', fontSize: 13 }} />
              <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0' }}>Find it in Google Ads → Tools → Conversions → Tag setup</p>

              <label><strong>Google Tag Manager Container ID</strong></label>
              <input placeholder="e.g. GTM-MW74K75R" value={branding.gtmId || ''}
                onChange={e => setBranding(b => ({ ...b, gtmId: e.target.value.trim() }))} style={{ fontFamily: 'monospace', fontSize: 13 }} />
              <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0' }}>Find it in Google Tag Manager → Container ID (top right)</p>

              <button className="btn" onClick={handleSaveBranding}>Save Branding</button>

              <h3 style={{ marginTop: 32, paddingTop: 24, borderTop: '2px solid #f0f0f0' }}>Country Codes (for OTP login)</h3>
              <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px' }}>Manage which country codes appear in the phone number dropdown. First one is the default.</p>
              {(branding.countryCodes || []).map((c, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                  <span style={{ fontSize: 13, color: '#555', width: 20, textAlign: 'center' }}>{i + 1}.</span>
                  <img src={`https://flagcdn.com/24x18/${c.flag || 'ie'}.png`} alt="" style={{ width: 20, height: 15, borderRadius: 2 }} onError={e => { e.target.style.display = 'none'; }} />
                  <span style={{ fontSize: 13, fontWeight: 600, minWidth: 50 }}>{c.code}</span>
                  <span style={{ fontSize: 13, color: '#555', flex: 1 }}>{c.name}</span>
                  <button type="button" className="btn-small btn-danger" onClick={() => { const codes = (branding.countryCodes || []).filter((_, idx) => idx !== i); setBranding(b => ({ ...b, countryCodes: codes })); }}>✕</button>
                </div>
              ))}
              <div style={{ marginTop: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 4, display: 'block' }}>Add Country</label>
                <select
                  value=""
                  onChange={e => {
                    if (!e.target.value) return;
                    const [code, flag, ...nameParts] = e.target.value.split('|');
                    const name = nameParts.join('|');
                    const exists = (branding.countryCodes || []).find(c => c.code === code);
                    if (exists) return;
                    setBranding(b => ({ ...b, countryCodes: [...(b.countryCodes || []), { code, name, flag }] }));
                  }}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14 }}
                >
                  <option value="">— Select a country to add —</option>
                  <option value="+353|ie|Ireland">Ireland +353</option>
                  <option value="+44|gb|United Kingdom">United Kingdom +44</option>
                  <option value="+1|us|United States">United States +1</option>
                  <option value="+91|in|India">India +91</option>
                  <option value="+92|pk|Pakistan">Pakistan +92</option>
                  <option value="+971|ae|United Arab Emirates">UAE +971</option>
                  <option value="+966|sa|Saudi Arabia">Saudi Arabia +966</option>
                  <option value="+49|de|Germany">Germany +49</option>
                  <option value="+33|fr|France">France +33</option>
                  <option value="+34|es|Spain">Spain +34</option>
                  <option value="+39|it|Italy">Italy +39</option>
                  <option value="+31|nl|Netherlands">Netherlands +31</option>
                  <option value="+48|pl|Poland">Poland +48</option>
                  <option value="+351|pt|Portugal">Portugal +351</option>
                  <option value="+46|se|Sweden">Sweden +46</option>
                  <option value="+47|no|Norway">Norway +47</option>
                  <option value="+45|dk|Denmark">Denmark +45</option>
                  <option value="+61|au|Australia">Australia +61</option>
                  <option value="+64|nz|New Zealand">New Zealand +64</option>
                  <option value="+86|cn|China">China +86</option>
                  <option value="+81|jp|Japan">Japan +81</option>
                  <option value="+82|kr|South Korea">South Korea +82</option>
                  <option value="+55|br|Brazil">Brazil +55</option>
                  <option value="+52|mx|Mexico">Mexico +52</option>
                  <option value="+27|za|South Africa">South Africa +27</option>
                  <option value="+234|ng|Nigeria">Nigeria +234</option>
                  <option value="+63|ph|Philippines">Philippines +63</option>
                  <option value="+60|my|Malaysia">Malaysia +60</option>
                  <option value="+65|sg|Singapore">Singapore +65</option>
                  <option value="+66|th|Thailand">Thailand +66</option>
                  <option value="+90|tr|Turkey">Turkey +90</option>
                  <option value="+7|ru|Russia">Russia +7</option>
                  <option value="+380|ua|Ukraine">Ukraine +380</option>
                  <option value="+20|eg|Egypt">Egypt +20</option>
                  <option value="+254|ke|Kenya">Kenya +254</option>
                  <option value="+94|lk|Sri Lanka">Sri Lanka +94</option>
                  <option value="+880|bd|Bangladesh">Bangladesh +880</option>
                  <option value="+977|np|Nepal">Nepal +977</option>
                </select>
              </div>
              <p style={{ fontSize: 11, color: '#aaa', marginTop: 8 }}>First country in the list is the default. Drag to reorder (or remove and re-add).</p>

              <button className="btn" style={{ marginTop: 16 }} onClick={async () => {
                try {
                  await api.put('/admin/content/country_codes', { codes: branding.countryCodes || [{ code: '+353', name: 'Ireland', flag: 'ie' }], defaultCode: (branding.countryCodes || [])[0]?.code || '+353' });
                  setSaveMsg('Country codes saved!');
                  setTimeout(() => setSaveMsg(''), 3000);
                } catch (e) { setSaveMsg('Error: ' + (e.response?.data?.error || e.message)); }
              }}>Save Country Codes</button>

              <h3 style={{ marginTop: 32, paddingTop: 24, borderTop: '2px solid #f0f0f0' }}>🔐 Customer Login / Signup Method</h3>
              <p style={{ fontSize: 13, color: '#888', margin: '0 0 12px' }}>Control how customers can log in and sign up during checkout.</p>
              {authMsg && <p className="save-msg" style={{ marginBottom: 10 }}>{authMsg}</p>}
              <select
                value={authSettings.loginMode || 'both'}
                onChange={e => setAuthSettings(s => ({ ...s, loginMode: e.target.value }))}
                style={{ padding: '10px 14px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, width: '100%', marginBottom: 8 }}
              >
                <option value="both">Both — SMS (Mobile) + Email</option>
                <option value="sms_only">SMS / Mobile Only</option>
                <option value="email_only">Email Only</option>
              </select>
              <p style={{ fontSize: 12, color: '#555', margin: '0 0 12px', lineHeight: 1.6 }}>
                {authSettings.loginMode === 'both' && '✅ Customers can choose to log in / sign up via Mobile (SMS/WhatsApp OTP) or Email OTP.'}
                {authSettings.loginMode === 'sms_only' && '📱 Customers can only log in / sign up via Mobile number. Email option is hidden at checkout.'}
                {authSettings.loginMode === 'email_only' && '✉️ Customers can only log in / sign up via Email OTP. Mobile option is hidden at checkout.'}
              </p>
              <button className="btn" onClick={async () => {
                try {
                  await api.put('/admin/auth-settings', authSettings);
                  setAuthMsg('Login method saved!');
                  setTimeout(() => setAuthMsg(''), 3000);
                } catch (e) { setAuthMsg('Error: ' + (e.response?.data?.error || e.message)); }
              }}>Save Login Method</button>
            </div>
          </div>
        )}

        {activeTab === 'footer' && (
          <div className="admin-content">
            <h2>Manage Footer</h2>
            {saveMsg && <p className="save-msg">{saveMsg}</p>}
            <div className="admin-form">
              <label><strong>Contact Info</strong></label>
              <label>Address</label>
              <input placeholder="Unit 6, Park Shopping Centre, Prussia Street, Dublin 7, D07K802, Ireland" value={footerData.address || ''} onChange={e => setFooterData(f => ({ ...f, address: e.target.value }))} />
              <label>E-mail</label>
              <input type="email" placeholder="sales@greenwheelsscooters.ie" value={footerData.email || ''} onChange={e => setFooterData(f => ({ ...f, email: e.target.value }))} />
              <label>Phone</label>
              <input placeholder="087 100 2241" value={footerData.phone || ''} onChange={e => setFooterData(f => ({ ...f, phone: e.target.value }))} />
              <label>Opening Hours (one per line for multi-line display)</label>
              <textarea rows={3} placeholder="Mon - Sat: 10:30am - 7:00pm&#10;Sun: 11:00am - 4:30pm&#10;Bank Holidays: Closed" value={footerData.hours || ''} onChange={e => setFooterData(f => ({ ...f, hours: e.target.value }))} />

              <label><strong>Badges</strong> (JSON array of {'{'}icon, text{'}'} )</label>
              <textarea rows={4} value={JSON.stringify(footerData.badges || [], null, 2)}
                onChange={e => { try { setFooterData(f => ({ ...f, badges: JSON.parse(e.target.value) })); } catch {} }} />

              <label><strong>Social Links</strong> (JSON array of {'{'}name, url{'}'} )</label>
              <textarea rows={4} value={JSON.stringify(footerData.social || [], null, 2)}
                onChange={e => { try { setFooterData(f => ({ ...f, social: JSON.parse(e.target.value) })); } catch {} }} />

              <label><strong>Support Links</strong> (JSON array of {'{'}name, path{'}'} )</label>
              <textarea rows={6} value={JSON.stringify(footerData.support || [], null, 2)}
                onChange={e => { try { setFooterData(f => ({ ...f, support: JSON.parse(e.target.value) })); } catch {} }} />

              <label><strong>Electric Scooters Links</strong> (JSON array of {'{'}name, path{'}'} )</label>
              <textarea rows={8} value={JSON.stringify(footerData.scooters || [], null, 2)}
                onChange={e => { try { setFooterData(f => ({ ...f, scooters: JSON.parse(e.target.value) })); } catch {} }} />

              <label><strong>Electric Bikes Links</strong> (JSON array of {'{'}name, path{'}'} )</label>
              <textarea rows={5} value={JSON.stringify(footerData.bikes || [], null, 2)}
                onChange={e => { try { setFooterData(f => ({ ...f, bikes: JSON.parse(e.target.value) })); } catch {} }} />

              <label><strong>Disclaimer</strong></label>
              <textarea rows={3} placeholder="Legal disclaimer text" value={footerData.disclaimer || ''}
                onChange={e => setFooterData(f => ({ ...f, disclaimer: e.target.value }))} />

              <label><strong>Copyright</strong></label>
              <input placeholder="© 2025 Green Wheels Scooters..." value={footerData.copyright || ''}
                onChange={e => setFooterData(f => ({ ...f, copyright: e.target.value }))} />

              <button className="btn" onClick={handleSaveFooter}>Save Footer</button>
            </div>
          </div>
        )}

        {activeTab === 'slides' && (
          <div className="admin-content">
            <h2>Hero Slides</h2>
            {saveMsg && <p className="save-msg">{saveMsg}</p>}
            {slides.map((slide, i) => (
              <div key={i} className="admin-form" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>Slide {i + 1}</strong>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-small" onClick={() => moveSlide(i, -1)} disabled={i === 0}>↑</button>
                    <button className="btn-small" onClick={() => moveSlide(i, 1)} disabled={i === slides.length - 1}>↓</button>
                    <button className="btn-small btn-danger" onClick={() => removeSlide(i)}>✕ Remove</button>
                  </div>
                </div>

                <label>Desktop Image</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input placeholder="Desktop image URL" value={slide.image} onChange={e => updateSlide(i, 'image', e.target.value)} style={{ flex: 1 }} />
                  <button type="button" className="btn-small" style={{ cursor: 'pointer', margin: 0 }} onClick={() => document.getElementById(`slide-${i}-image`).click()}>
                    {slideUploading[`${i}_image`] ? '...' : '📁'}
                  </button>
                  <input id={`slide-${i}-image`} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { e.preventDefault(); e.stopPropagation(); if(e.target.files[0]) handleSlideUpload(i, 'image', e.target.files[0]); }} />
                  {slide.image && <img src={getImageUrl(slide.image)} alt="" style={{ height: 48, borderRadius: 4, border: '1px solid #ddd' }} onError={e => { e.target.style.display = 'none'; }} />}
                </div>

                <label>Mobile Image</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <input placeholder="Mobile image URL (optional)" value={slide.imageMobile || ''} onChange={e => updateSlide(i, 'imageMobile', e.target.value)} style={{ flex: 1 }} />
                  <button type="button" className="btn-small" style={{ cursor: 'pointer', margin: 0 }} onClick={() => document.getElementById(`slide-${i}-mobile`).click()}>
                    {slideUploading[`${i}_imageMobile`] ? '...' : '📁'}
                  </button>
                  <input id={`slide-${i}-mobile`} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { e.preventDefault(); e.stopPropagation(); if(e.target.files[0]) handleSlideUpload(i, 'imageMobile', e.target.files[0]); }} />
                </div>

                <input placeholder="Title (optional)" value={slide.title || ''} onChange={e => updateSlide(i, 'title', e.target.value)} />
                <input placeholder="Subtitle (optional)" value={slide.subtitle || ''} onChange={e => updateSlide(i, 'subtitle', e.target.value)} />
                <input placeholder="CTA Button Text (optional, e.g. Shop Now)" value={slide.cta || ''} onChange={e => updateSlide(i, 'cta', e.target.value)} />
                <input placeholder="CTA Link (e.g. /products)" value={slide.ctaLink || ''} onChange={e => updateSlide(i, 'ctaLink', e.target.value)} />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn-small" onClick={addSlide}>+ Add Slide</button>
            </div>
            <button className="btn" onClick={handleSaveSlides}>Save Slides</button>
          </div>
        )}

        {activeTab === 'home' && (
          <div className="admin-content">
            <h2>Manage Home Content</h2>
            {saveMsg && <p className="save-msg">{saveMsg}</p>}
            <div className="admin-form">

              <h3>Why Choose Us</h3>
              <input placeholder="Title (e.g. WHY GREEN WHEELS SCOOTERS)" value={homeData.whyUsTitle || ''} onChange={e => setHomeData(h => ({...h, whyUsTitle: e.target.value}))} />

              <label><strong>Paragraphs</strong></label>
              {(homeData.whyUsParas || ['']).map((para, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <textarea rows={3} placeholder={`Paragraph ${i + 1}`} value={para}
                    onChange={e => {
                      const p = [...(homeData.whyUsParas || [''])];
                      p[i] = e.target.value;
                      setHomeData(h => ({...h, whyUsParas: p}));
                    }}
                    style={{ flex: 1 }} />
                  <button type="button" className="btn-small btn-danger" style={{ marginTop: 4 }}
                    onClick={() => setHomeData(h => ({...h, whyUsParas: (h.whyUsParas || []).filter((_, idx) => idx !== i)}))}>✕</button>
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setHomeData(h => ({...h, whyUsParas: [...(h.whyUsParas || []), '']}))}>+ Add Paragraph</button>

              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <input placeholder="CTA Text (e.g. ORDER NOW)" value={homeData.whyUsCta || ''} onChange={e => setHomeData(h => ({...h, whyUsCta: e.target.value}))} style={{ flex: 1 }} />
                <input placeholder="CTA Link (e.g. /products)" value={homeData.whyUsCtaLink || ''} onChange={e => setHomeData(h => ({...h, whyUsCtaLink: e.target.value}))} style={{ flex: 1 }} />
              </div>
              <input placeholder="Contact Number (e.g. 087 100 2241)" value={homeData.whyUsContact || ''} onChange={e => setHomeData(h => ({...h, whyUsContact: e.target.value}))} />

              <h3 style={{ marginTop: 24 }}>Service Centre</h3>
              <input placeholder="Title (e.g. Green Wheels Service Centre)" value={homeData.serviceTitle || ''} onChange={e => setHomeData(h => ({...h, serviceTitle: e.target.value}))} />
              <input placeholder="Subtitle (e.g. Does your scooter need a tune up?)" value={homeData.serviceSubtitle || ''} onChange={e => setHomeData(h => ({...h, serviceSubtitle: e.target.value}))} />

              <label><strong>Paragraphs</strong></label>
              {(homeData.serviceParas || ['']).map((para, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <textarea rows={3} placeholder={`Paragraph ${i + 1}`} value={para}
                    onChange={e => {
                      const p = [...(homeData.serviceParas || [''])];
                      p[i] = e.target.value;
                      setHomeData(h => ({...h, serviceParas: p}));
                    }}
                    style={{ flex: 1 }} />
                  <button type="button" className="btn-small btn-danger" style={{ marginTop: 4 }}
                    onClick={() => setHomeData(h => ({...h, serviceParas: (h.serviceParas || []).filter((_, idx) => idx !== i)}))}>✕</button>
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setHomeData(h => ({...h, serviceParas: [...(h.serviceParas || []), '']}))}>+ Add Paragraph</button>

              <input placeholder="Contact Number (e.g. 087 100 2241)" value={homeData.serviceContact || ''} onChange={e => setHomeData(h => ({...h, serviceContact: e.target.value}))} style={{ marginTop: 8 }} />

              <button className="btn" onClick={handleSaveHome} style={{ marginTop: 16 }}>Save Home Content</button>
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="admin-content">
            <h2>About Page</h2>
            {aboutMsg && <p className="save-msg">{aboutMsg}</p>}
            <div className="admin-form">
              <label><strong>Page Title</strong></label>
              <input value={aboutData.title || ''} onChange={e => setAboutData(d => ({ ...d, title: e.target.value }))} placeholder="About Green Wheels Scooters" />
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {['left', 'center', 'right'].map(a => (
                  <button key={a} className="btn-small" style={{ background: (aboutData.titleAlign || 'center') === a ? '#1a1a2e' : '#fff', color: (aboutData.titleAlign || 'center') === a ? '#fff' : '#333', border: '1px solid #ddd', textTransform: 'capitalize' }} onClick={() => setAboutData(d => ({ ...d, titleAlign: a }))}>{a}</button>
                ))}
              </div>

              <label style={{ marginTop: 16 }}><strong>Main Content</strong></label>
              <textarea rows={4} value={aboutData.content || ''} onChange={e => setAboutData(d => ({ ...d, content: e.target.value }))} placeholder="We are committed to providing eco-friendly transportation solutions." />
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {['left', 'center', 'right'].map(a => (
                  <button key={a} className="btn-small" style={{ background: (aboutData.contentAlign || 'left') === a ? '#1a1a2e' : '#fff', color: (aboutData.contentAlign || 'left') === a ? '#fff' : '#333', border: '1px solid #ddd', textTransform: 'capitalize' }} onClick={() => setAboutData(d => ({ ...d, contentAlign: a }))}>{a}</button>
                ))}
              </div>

              <label style={{ marginTop: 16 }}><strong>Mission Statement</strong></label>
              <textarea rows={3} value={aboutData.mission || ''} onChange={e => setAboutData(d => ({ ...d, mission: e.target.value }))} placeholder="Our mission is to make cities greener one scooter at a time." />
              <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                {['left', 'center', 'right'].map(a => (
                  <button key={a} className="btn-small" style={{ background: (aboutData.missionAlign || 'left') === a ? '#1a1a2e' : '#fff', color: (aboutData.missionAlign || 'left') === a ? '#fff' : '#333', border: '1px solid #ddd', textTransform: 'capitalize' }} onClick={() => setAboutData(d => ({ ...d, missionAlign: a }))}>{a}</button>
                ))}
              </div>

              <label style={{ marginTop: 16 }}><strong>Values</strong></label>
              {(aboutData.values || []).map((v, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input style={{ flex: 1 }} placeholder="Title" value={v.title || ''} onChange={e => setAboutData(d => ({ ...d, values: d.values.map((val, idx) => idx === i ? { ...val, title: e.target.value } : val) }))} />
                  <input style={{ flex: 2 }} placeholder="Description" value={v.description || ''} onChange={e => setAboutData(d => ({ ...d, values: d.values.map((val, idx) => idx === i ? { ...val, description: e.target.value } : val) }))} />
                  <button className="btn-small" onClick={() => setAboutData(d => ({ ...d, values: d.values.filter((_, idx) => idx !== i) }))}>✕</button>
                </div>
              ))}
              <button className="btn-small" onClick={() => setAboutData(d => ({ ...d, values: [...(d.values || []), { title: '', description: '' }] }))}>+ Add Value</button>

              <label style={{ marginTop: 16 }}><strong>Stats</strong></label>
              <p style={{ fontSize: 12, color: '#888', margin: '4px 0 10px', lineHeight: 1.6 }}>
                Add/remove stat items. Set the <strong>number</strong> (e.g. "5000+", "100+", "5★", "7 Days") and the <strong>label</strong> (e.g. "Happy Customers", "Products", "Google Rating", "Open Every Week").
              </p>
              {(aboutData.stats || []).map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input style={{ flex: 1 }} placeholder="Number (e.g. 5000+)" value={s.num || ''} onChange={e => setAboutData(d => ({ ...d, stats: d.stats.map((st, idx) => idx === i ? { ...st, num: e.target.value } : st) }))} />
                  <input style={{ flex: 2 }} placeholder="Label (e.g. Happy Customers)" value={s.label || ''} onChange={e => setAboutData(d => ({ ...d, stats: d.stats.map((st, idx) => idx === i ? { ...st, label: e.target.value } : st) }))} />
                  <button className="btn-small" onClick={() => setAboutData(d => ({ ...d, stats: d.stats.filter((_, idx) => idx !== i) }))}>✕</button>
                </div>
              ))}
              <button className="btn-small" onClick={() => setAboutData(d => ({ ...d, stats: [...(d.stats || []), { num: '', label: '' }] }))}>+ Add Stat</button>

              <button className="btn" style={{ marginTop: 16 }} onClick={async () => {
                try {
                  await api.put('/admin/content/about', aboutData);
                  setAboutMsg('About page saved!');
                  setTimeout(() => setAboutMsg(''), 3000);
                } catch (e) { setAboutMsg('Error: ' + (e.response?.data?.error || e.message)); }
              }}>Save About Page</button>
            </div>
          </div>
        )}

        {activeTab === 'gateways' && (
          <div className="admin-content">
            <h2>Payment Gateways</h2>
            {gwMsg && <p className="save-msg">{gwMsg}</p>}

            {/* PayPal */}
            <div className="admin-form" style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>PayPal</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={gwSettings.paypal?.enabled || false}
                    onChange={e => setGwSettings(g => ({ ...g, paypal: { ...g.paypal, enabled: e.target.checked } }))} />
                  <span style={{ color: gwSettings.paypal?.enabled ? '#00d084' : '#999', fontWeight: 600 }}>
                    {gwSettings.paypal?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
              <label>Client ID</label>
              <input placeholder="PayPal Client ID" value={gwSettings.paypal?.clientId || ''}
                onChange={e => setGwSettings(g => ({ ...g, paypal: { ...g.paypal, clientId: e.target.value } }))} />
              <label>Secret Key</label>
              <input type="password" placeholder="PayPal Secret" value={gwSettings.paypal?.secret || ''}
                onChange={e => setGwSettings(g => ({ ...g, paypal: { ...g.paypal, secret: e.target.value } }))} />
              <label>Mode</label>
              <select value={gwSettings.paypal?.mode || 'sandbox'}
                onChange={e => setGwSettings(g => ({ ...g, paypal: { ...g.paypal, mode: e.target.value } }))}>
                <option value="sandbox">Sandbox (Test)</option>
                <option value="live">Live (Production)</option>
              </select>
            </div>

            {/* Stripe */}
            <div className="admin-form" style={{ marginBottom: 32 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
                <h3 style={{ margin: 0 }}>Stripe</h3>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input type="checkbox" checked={gwSettings.stripe?.enabled || false}
                    onChange={e => setGwSettings(g => ({ ...g, stripe: { ...g.stripe, enabled: e.target.checked } }))} />
                  <span style={{ color: gwSettings.stripe?.enabled ? '#00d084' : '#999', fontWeight: 600 }}>
                    {gwSettings.stripe?.enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </label>
              </div>
              <label>Publishable Key</label>
              <input placeholder="pk_test_..." value={gwSettings.stripe?.publishableKey || ''}
                onChange={e => setGwSettings(g => ({ ...g, stripe: { ...g.stripe, publishableKey: e.target.value } }))} />
              <label>Secret Key</label>
              <input type="password" placeholder="sk_test_..." value={gwSettings.stripe?.secretKey || ''}
                onChange={e => setGwSettings(g => ({ ...g, stripe: { ...g.stripe, secretKey: e.target.value } }))} />
            </div>

            <button className="btn" onClick={handleSaveGateways}>Save Gateway Settings</button>
          </div>
        )}

        {activeTab === 'delivery' && (
          <div className="admin-content">
            <h2>Delivery Settings</h2>
            {deliveryMsg && <p className="save-msg">{deliveryMsg}</p>}
            <div className="admin-form">
              <div className="form-group">
                <label>Delivery Fee (€)</label>
                <input type="number" step="0.01" min="0" value={deliverySettings.deliveryFee || ''}
                  onChange={e => setDeliverySettings(s => ({ ...s, deliveryFee: parseFloat(e.target.value) || 0 }))} placeholder="e.g. 15" />
                <small>Amount charged for registered delivery</small>
              </div>
              <div className="form-group">
                <label>Free Delivery Threshold (€)</label>
                <input type="number" step="0.01" min="0" value={deliverySettings.freeDeliveryThreshold || ''}
                  onChange={e => setDeliverySettings(s => ({ ...s, freeDeliveryThreshold: parseFloat(e.target.value) || 0 }))} placeholder="e.g. 500" />
                <small>Orders above this amount get free registered delivery</small>
              </div>
              <div className="form-group">
                <label>Registered Delivery - Max Days</label>
                <input type="number" min="1" value={deliverySettings.registeredMaxDays || ''}
                  onChange={e => setDeliverySettings(s => ({ ...s, registeredMaxDays: parseInt(e.target.value) || 0 }))} placeholder="e.g. 5" />
                <small>Maximum working days for registered delivery (calculates expected date)</small>
              </div>
              <div className="form-group">
                <label>Non-Registered Delivery - Max Days</label>
                <input type="number" min="1" value={deliverySettings.nonRegisteredMaxDays || ''}
                  onChange={e => setDeliverySettings(s => ({ ...s, nonRegisteredMaxDays: parseInt(e.target.value) || 0 }))} placeholder="e.g. 10" />
                <small>Maximum working days for non-registered delivery (calculates expected date)</small>
              </div>
              <div className="form-group">
                <label>Categories Requiring Registered Delivery</label>
                <small style={{display:'block',marginBottom:8}}>Select which product categories show the delivery choice popup at checkout</small>
                {['electric-scooters','electric-bikes','spare-parts','accessories'].map(cat => (
                  <label key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={(deliverySettings.categories || []).includes(cat)}
                      onChange={e => {
                        const cats = deliverySettings.categories || [];
                        setDeliverySettings(s => ({ ...s, categories: e.target.checked ? [...cats, cat] : cats.filter(c => c !== cat) }));
                      }} />
                    {cat.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </label>
                ))}
              </div>
              <button className="btn" onClick={async () => {
                try {
                  await api.put('/admin/delivery-settings', deliverySettings);
                  setDeliveryMsg('Delivery settings saved!');
                  setTimeout(() => setDeliveryMsg(''), 3000);
                } catch (e) { setDeliveryMsg('Error: ' + (e.response?.data?.error || e.message)); }
              }}>Save Delivery Settings</button>
            </div>
          </div>
        )}

        {activeTab === 'contactSettings' && (
          <div className="admin-content">
            <h2>Contact Page Settings</h2>
            {contactMsg && <p className="save-msg">{contactMsg}</p>}
            <div className="admin-form">

              <label><strong>Page Title</strong></label>
              <input
                placeholder="e.g. Contact Us"
                value={contactSettings.title}
                onChange={e => setContactSettings(s => ({ ...s, title: e.target.value }))}
              />

              <label><strong>Address</strong></label>
              <input
                placeholder="e.g. Unit 6, Park Shopping Centre, Prussia Street, Dublin 7"
                value={contactSettings.address}
                onChange={e => setContactSettings(s => ({ ...s, address: e.target.value }))}
              />

              <label><strong>Phone</strong></label>
              <input
                placeholder="e.g. 087 100 2241"
                value={contactSettings.phone}
                onChange={e => setContactSettings(s => ({ ...s, phone: e.target.value }))}
              />

              <label><strong>Email</strong></label>
              <input
                placeholder="e.g. sales@greenwheelsscooters.ie"
                value={contactSettings.email}
                onChange={e => setContactSettings(s => ({ ...s, email: e.target.value }))}
              />

              <label><strong>Opening Hours</strong></label>
              <label><strong>Opening Hours</strong> (one per line for special days)</label>
              <textarea
                rows={3}
                placeholder="Mon - Sat: 10:30am - 7:00pm
Sun: 11:00am - 4:30pm
Bank Holidays: Closed"
                value={contactSettings.hours}
                onChange={e => setContactSettings(s => ({ ...s, hours: e.target.value }))}
              />

              <label><strong>Google Maps Embed URL</strong></label>
              <p style={{ fontSize: 12, color: '#888', margin: '0 0 6px', lineHeight: 1.6 }}>
                1. Go to <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer">Google Maps</a> and search your store address.<br />
                2. Click <strong>Share</strong> → <strong>Embed a map</strong> → <strong>Copy HTML</strong>.<br />
                3. From the copied code, paste only the URL inside <code>src="..."</code> below.
              </p>
              <textarea
                rows={5}
                placeholder="https://www.google.com/maps/embed?pb=..."
                value={contactSettings.mapEmbed}
                onChange={e => setContactSettings(s => ({ ...s, mapEmbed: e.target.value }))}
                style={{ fontFamily: 'monospace', fontSize: 12 }}
              />

              {contactSettings.mapEmbed && (
                <div style={{ marginBottom: 8 }}>
                  <p style={{ fontSize: 12, color: '#555', margin: '0 0 6px', fontWeight: 600 }}>Live Preview:</p>
                  <iframe
                    key={contactSettings.mapEmbed}
                    title="Map Preview"
                    src={contactSettings.mapEmbed}
                    width="100%"
                    height="260"
                    style={{ border: '1px solid #ddd', borderRadius: 8, display: 'block' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              )}

              <label><strong>Get Directions URL</strong></label>
              <p style={{ fontSize: 12, color: '#888', margin: '0 0 6px' }}>
                Link for the "Get Directions" button. Example:<br />
                <code>https://maps.google.com/?q=Unit+6+Park+Shopping+Centre+Prussia+Street+Dublin+7</code>
              </p>
              <input
                placeholder="https://maps.google.com/?q=..."
                value={contactSettings.directionsUrl}
                onChange={e => setContactSettings(s => ({ ...s, directionsUrl: e.target.value }))}
              />

              <button className="btn" onClick={handleSaveContactSettings}>Save Contact Page</button>
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="admin-content">
            <h2>Google Reviews via SerpApi</h2>

            {/* SerpApi Settings */}
            <div style={{ background: '#f0faf5', border: '1px solid #b2dfdb', borderRadius: 10, padding: 20, marginBottom: 24 }}>
              <strong style={{ fontSize: 15, color: '#00695c', display: 'block', marginBottom: 4 }}>SerpApi Settings</strong>
              <p style={{ fontSize: 12, color: '#666', margin: '0 0 14px', lineHeight: 1.6 }}>
                Uses <code>engine=google_maps_reviews</code> with your business <code>data_id</code>.
                Get a free API key at <a href="https://serpapi.com" target="_blank" rel="noopener noreferrer">serpapi.com</a>.
              </p>

              <div className="admin-form" style={{ gap: 12 }}>

                <label style={{ fontSize: 13, fontWeight: 600 }}>SerpApi Key</label>
                <input
                  type="password"
                  placeholder="Paste your SerpApi secret key"
                  value={serpSettings.serpApiKey}
                  onChange={e => setSerpSettings(s => ({ ...s, serpApiKey: e.target.value }))}
                />

                <label style={{ fontSize: 13, fontWeight: 600 }}>data_id
                  <span style={{ fontWeight: 400, color: '#888', marginLeft: 6 }}>
                    — unique ID for your Google Maps business listing
                  </span>
                </label>
                <input
                  placeholder="e.g. 0x88f50477cd92dfc5:0xff59cb0e6f64a2b3"
                  value={serpSettings.serpDataId}
                  onChange={e => setSerpSettings(s => ({ ...s, serpDataId: e.target.value }))}
                />
                <p style={{ fontSize: 12, color: '#666', margin: '-4px 0 0', lineHeight: 1.7 }}>
                  To find your <code>data_id</code>:<br />
                  1. Go to <a href="https://serpapi.com/playground" target="_blank" rel="noopener noreferrer">serpapi.com/playground</a><br />
                  2. Set <code>engine = google_maps</code>, search <strong>Green Wheels Scooters Dublin</strong><br />
                  3. Find your business in results &rarr; copy the <code>data_id</code> value
                </p>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                  <button className="btn" style={{ background: '#00695c' }} onClick={async () => {
                    try {
                      await api.put('/admin/serpapi-settings', serpSettings);
                      setReviewMsg('Settings saved to database!');
                      setTimeout(() => setReviewMsg(''), 3000);
                    } catch (e) { setReviewMsg('Error: ' + (e.response?.data?.error || e.message)); }
                  }}>Save Settings</button>

                  <button
                    className="btn"
                    style={{ background: serpImporting ? '#aaa' : '#4285F4' }}
                    disabled={serpImporting}
                    onClick={async () => {
                      if (!serpSettings.serpApiKey) { setReviewMsg('Enter your SerpApi key and save first.'); return; }
                      if (!serpSettings.serpDataId) { setReviewMsg('Enter your data_id and save first.'); return; }
                      setSerpImporting(true);
                      setReviewMsg('Fetching reviews from Google Maps...');
                      try {
                        const { data: result } = await api.post('/admin/reviews/import-serpapi', {
                          serpApiKey: serpSettings.serpApiKey,
                          serpDataId: serpSettings.serpDataId,
                        });
                        if (result.imported > 0) {
                          setReviewMsg(`Imported ${result.imported} reviews.${ result.rating ? ` Rating: ${result.rating}\u2605 (${result.total_ratings} total).` : '' }`);
                        } else {
                          setReviewMsg(result.message || 'No reviews found.');
                        }
                        const { data: rv } = await api.get('/admin/reviews');
                        setReviews(rv || []);
                        setTimeout(() => setReviewMsg(''), 8000);
                      } catch (e) {
                        setReviewMsg('Error: ' + (e.response?.data?.error || e.message));
                        setTimeout(() => setReviewMsg(''), 8000);
                      }
                      setSerpImporting(false);
                    }}
                  >
                    {serpImporting ? 'Importing...' : 'Import Reviews from Google'}
                  </button>
                </div>
              </div>
            </div>

            {reviewMsg && <p className="save-msg" style={{ marginBottom: 16 }}>{reviewMsg}</p>}

            {/* Manual add/edit form */}
            <h3 style={{ marginBottom: 12 }}>Add / Edit Review Manually</h3>
            <form className="admin-form" onSubmit={async (e) => {
              e.preventDefault();
              const updated = editingReview !== null
                ? reviews.map((r, i) => i === editingReview ? { ...reviewForm } : r)
                : [...reviews, { ...reviewForm, source: 'manual' }];
              try {
                await api.put('/admin/reviews', updated);
                setReviews(updated);
                setReviewForm({ title: '', body: '', author: '', date: '', stars: 5, visible: true });
                setEditingReview(null);
                setReviewMsg(editingReview !== null ? 'Review updated!' : 'Review added!');
                setTimeout(() => setReviewMsg(''), 3000);
              } catch (err) { setReviewMsg('Error: ' + err.message); }
            }}>
              <input placeholder="Review title (e.g. Fast delivery)" value={reviewForm.title}
                onChange={e => setReviewForm(f => ({ ...f, title: e.target.value }))} required />
              <textarea rows={3} placeholder="Review body text" value={reviewForm.body}
                onChange={e => setReviewForm(f => ({ ...f, body: e.target.value }))} required />
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="Author name" value={reviewForm.author}
                  onChange={e => setReviewForm(f => ({ ...f, author: e.target.value }))} style={{ flex: 2 }} required />
                <input placeholder="Date (e.g. 2 days ago)" value={reviewForm.date}
                  onChange={e => setReviewForm(f => ({ ...f, date: e.target.value }))} style={{ flex: 1 }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>Star Rating:</label>
                <div style={{ display: 'flex', gap: 4 }}>
                  {[1,2,3,4,5].map(s => (
                    <button key={s} type="button"
                      onClick={() => setReviewForm(f => ({ ...f, stars: s }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 3px', fontSize: 26, lineHeight: 1, color: s <= (reviewForm.stars || 5) ? '#00d084' : '#ddd' }}
                    >&#9733;</button>
                  ))}
                </div>
                <span style={{ fontSize: 13, color: '#555' }}>{reviewForm.stars || 5} / 5</span>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={reviewForm.visible}
                  onChange={e => setReviewForm(f => ({ ...f, visible: e.target.checked }))} />
                Visible on homepage
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn">{editingReview !== null ? 'Update' : 'Add'} Review</button>
                {editingReview !== null && (
                  <button type="button" className="btn" onClick={() => { setEditingReview(null); setReviewForm({ title: '', body: '', author: '', date: '', stars: 5, visible: true }); }}>Cancel</button>
                )}
              </div>
            </form>

            {/* Reviews table */}
            {selectedReviews.length > 0 && (
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 20, padding: '10px 14px', background: '#fce4ec', borderRadius: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedReviews.length} selected</span>
                <button className="btn-small btn-danger" onClick={async () => {
                  if (!window.confirm(`Delete ${selectedReviews.length} selected reviews?`)) return;
                  const updated = reviews.filter((_, idx) => !selectedReviews.includes(idx));
                  await api.put('/admin/reviews', updated);
                  setReviews(updated);
                  setSelectedReviews([]);
                  setReviewMsg(`${selectedReviews.length} reviews deleted!`);
                  setTimeout(() => setReviewMsg(''), 3000);
                }}>Delete Selected</button>
                <button className="btn-small" onClick={() => setSelectedReviews([])}>Clear Selection</button>
              </div>
            )}
            <table className="admin-table" style={{ marginTop: 24 }}>
              <thead>
                <tr><th style={{ width: 36 }}><input type="checkbox" checked={selectedReviews.length === reviews.length && reviews.length > 0} onChange={e => setSelectedReviews(e.target.checked ? reviews.map((_, i) => i) : [])} /></th><th>Author</th><th>Title</th><th>Stars</th><th>Date</th><th>Source</th><th>Visible</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {reviews.map((r, i) => (
                  <tr key={i} style={{ background: selectedReviews.includes(i) ? '#fff3f3' : '' }}>
                    <td><input type="checkbox" checked={selectedReviews.includes(i)} onChange={e => setSelectedReviews(prev => e.target.checked ? [...prev, i] : prev.filter(x => x !== i))} /></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {r.photo && <img src={r.photo} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />}
                        <span style={{ fontSize: 12 }}>{r.author}</span>
                      </div>
                    </td>
                    <td style={{ fontSize: 12, maxWidth: 160 }}>{r.title}</td>
                    <td style={{ color: '#00d084', fontSize: 14, letterSpacing: 1, whiteSpace: 'nowrap' }}>
                      {'\u2605'.repeat(r.stars || 5)}{'\u2606'.repeat(5 - (r.stars || 5))}
                    </td>
                    <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{r.date}</td>
                    <td>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
                        background: r.source === 'serpapi' ? '#e8f0fe' : '#f0f0f0',
                        color: r.source === 'serpapi' ? '#4285F4' : '#666' }}>
                        {r.source === 'serpapi' ? 'Google' : 'Manual'}
                      </span>
                    </td>
                    <td>
                      <span style={{ color: r.visible !== false ? '#00d084' : '#999', fontWeight: 600, fontSize: 12 }}>
                        {r.visible !== false ? 'Visible' : 'Hidden'}
                      </span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-small" onClick={() => {
                        setEditingReview(i);
                        setReviewForm({ title: r.title, body: r.body, author: r.author, date: r.date || '', stars: r.stars || 5, visible: r.visible !== false });
                      }}>Edit</button>
                      <button className="btn-small" onClick={async () => {
                        const updated = reviews.map((rv, idx) => idx === i ? { ...rv, visible: rv.visible === false } : rv);
                        await api.put('/admin/reviews', updated);
                        setReviews(updated);
                      }}>{r.visible !== false ? 'Hide' : 'Show'}</button>
                      <button className="btn-small btn-danger" onClick={async () => {
                        if (!window.confirm('Delete this review?')) return;
                        const updated = reviews.filter((_, idx) => idx !== i);
                        await api.put('/admin/reviews', updated);
                        setReviews(updated);
                      }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'categories' && (
          <div className="admin-content">
            <h2>Manage Categories</h2>
            {saveMsg && <p className="save-msg">{saveMsg}</p>}
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                if (editingCategory) {
                  await api.put(`/admin/categories/${editingCategory._id}`, categoryForm);
                  setEditingCategory(null);
                } else {
                  await api.post('/admin/categories', categoryForm);
                }
                setCategoryForm({ key: '', label: '', order: 0, enabled: true });
                const { data } = await api.get('/admin/categories');
                setAdminCategories(data);
                setSaveMsg('Category saved!'); setTimeout(() => setSaveMsg(''), 3000);
              } catch (err) { setSaveMsg('Error: ' + (err.response?.data?.error || err.message)); }
            }} className="admin-form" style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input placeholder="Key (e.g. electric-scooters)" value={categoryForm.key} onChange={e => setCategoryForm(f => ({ ...f, key: e.target.value }))} required style={{ flex: 2 }} />
                <input placeholder="Label (e.g. Electric Scooters)" value={categoryForm.label} onChange={e => setCategoryForm(f => ({ ...f, label: e.target.value }))} required style={{ flex: 2 }} />
                <input type="number" placeholder="Order" value={categoryForm.order} onChange={e => setCategoryForm(f => ({ ...f, order: +e.target.value }))} style={{ width: 80 }} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input type="checkbox" checked={categoryForm.enabled} onChange={e => setCategoryForm(f => ({ ...f, enabled: e.target.checked }))} /> Enabled
                </label>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                <button type="submit" className="btn">{editingCategory ? 'Update' : 'Add'} Category</button>
                {editingCategory && <button type="button" className="btn" onClick={() => { setEditingCategory(null); setCategoryForm({ key: '', label: '', order: 0, enabled: true }); }}>Cancel</button>}
              </div>
            </form>
            <table className="admin-table">
              <thead><tr><th>Order</th><th>Key</th><th>Label</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {adminCategories.map((cat, i) => (
                  <tr key={cat._id || i}>
                    <td>{cat.order}</td>
                    <td><code>{cat.key}</code></td>
                    <td>{cat.label}</td>
                    <td><span style={{ color: cat.enabled ? '#00d084' : '#999' }}>{cat.enabled ? 'Visible' : 'Hidden'}</span></td>
                    <td>
                      <button className="btn-small" onClick={() => { setEditingCategory(cat); setCategoryForm({ key: cat.key, label: cat.label, order: cat.order, enabled: cat.enabled }); }}>Edit</button>
                      <button className="btn-small" onClick={async () => { await api.put(`/admin/categories/${cat._id}`, { enabled: !cat.enabled }); setAdminCategories(prev => prev.map(c => c._id === cat._id ? { ...c, enabled: !c.enabled } : c)); }}>{cat.enabled ? 'Hide' : 'Show'}</button>
                      <button className="btn-small btn-danger" onClick={async () => { if (!window.confirm(`Delete category "${cat.label}"?`)) return; await api.delete(`/admin/categories/${cat._id}`); setAdminCategories(prev => prev.filter(c => c._id !== cat._id)); }}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'blog' && (
          <div className="admin-content">
            <h2>Manage Blog</h2>
            {blogMsg && <p className="save-msg">{blogMsg}</p>}
            <form onSubmit={handleSaveBlogPost} className="admin-form">

              {/* ── Meta fields ── */}
              <input placeholder="Post Title" value={blogForm.title}
                onChange={e => setBlogForm(f => ({ ...f, title: e.target.value, slug: f.slug || slugify(e.target.value) }))} required />
              <input placeholder="Slug (auto-generated)" value={blogForm.slug}
                onChange={e => setBlogForm(f => ({ ...f, slug: e.target.value }))} />
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="Category" value={blogForm.category}
                  onChange={e => setBlogForm(f => ({ ...f, category: e.target.value }))} style={{ flex: 1 }} />
                <input placeholder="Author" value={blogForm.author}
                  onChange={e => setBlogForm(f => ({ ...f, author: e.target.value }))} style={{ flex: 1 }} />
              </div>
              <textarea placeholder="Short excerpt (shown on blog grid)" rows={2} value={blogForm.excerpt}
                onChange={e => setBlogForm(f => ({ ...f, excerpt: e.target.value }))} />

              {/* ── Hero / Cover Image ── */}
              <label><strong>Hero Image</strong></label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input placeholder="Image URL or upload" value={blogForm.image}
                  onChange={e => setBlogForm(f => ({ ...f, image: e.target.value }))} style={{ flex: 1 }} />
                <button type="button" className="btn-small" style={{ cursor: 'pointer', margin: 0 }} onClick={() => document.getElementById('blog-hero-upload').click()}>
                  📁
                </button>
                <input id="blog-hero-upload" type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={async e => { e.preventDefault(); e.stopPropagation(); if(e.target.files[0]) { const url = await uploadImage(e.target.files[0]); setBlogForm(f => ({ ...f, image: url })); } }} />
                {blogForm.image && <img src={getImageUrl(blogForm.image)} alt="" style={{ height: 48, borderRadius: 4, border: '1px solid #ddd' }} onError={e => { e.target.style.display = 'none'; }} />}
              </div>

              {/* ── Sections ── */}
              <label style={{ marginTop: 16 }}><strong>Page Sections</strong></label>
              {blogSections.map((sec, si) => (
                <div key={si} style={{ border: '2px solid #e0e0e0', borderRadius: 8, padding: 14, marginBottom: 12, background: '#fafafa' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <strong style={{ fontSize: 13, color: '#555', textTransform: 'uppercase', letterSpacing: 1 }}>
                      {sec.type === 'split-row' ? '↔ Split Row' : sec.type === 'full' ? '▦ Product Cards' : sec.type === 'features' ? '★ Features Bar' : '▶ CTA Section'}
                    </strong>
                    <button type="button" className="btn-small btn-danger" onClick={() => removeSection(si)}>✕ Remove</button>
                  </div>

                  {/* SPLIT ROW */}
                  {sec.type === 'split-row' && (
                    <div>
                      <input placeholder="Section Heading (H2)" value={sec.heading}
                        onChange={e => updateSection(si, { heading: e.target.value })}
                        style={{ width: '100%', boxSizing: 'border-box', fontWeight: 600, marginBottom: 8 }} />
                      <label style={{ fontSize: 12, color: '#666' }}>Section Image</label>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                        <input placeholder="Image URL or upload" value={sec.image || ''}
                          onChange={e => updateSection(si, { image: e.target.value })} style={{ flex: 1 }} />
                        <button type="button" className="btn-small" style={{ cursor: 'pointer', margin: 0 }} onClick={() => document.getElementById(`blog-section-${si}-upload`).click()}>
                          📁
                        </button>
                        <input id={`blog-section-${si}-upload`} type="file" accept="image/*" style={{ display: 'none' }}
                          onChange={async e => { e.preventDefault(); e.stopPropagation(); if(e.target.files[0]) { const url = await uploadImage(e.target.files[0]); updateSection(si, { image: url }); } }} />
                        {sec.image && <img src={getImageUrl(sec.image)} alt="" style={{ height: 40, borderRadius: 4, border: '1px solid #ddd' }} onError={e => { e.target.style.display = 'none'; }} />}
                      </div>
                      <label style={{ fontSize: 12, color: '#666' }}>Body Content</label>
                      {(sec.body || []).map((item, bi) => (
                        <div key={bi} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 6 }}>
                          <select value={item.type} onChange={e => updateSplitBody(si, bi, { type: e.target.value, value: '' })} style={{ width: 110, flexShrink: 0 }}>
                            <option value="paragraph">Paragraph</option>
                            <option value="list">Bullet List</option>
                          </select>
                          <textarea rows={item.type === 'list' ? 4 : 2}
                            placeholder={item.type === 'list' ? 'One item per line' : 'Paragraph text...'}
                            value={item.value || ''}
                            onChange={e => updateSplitBody(si, bi, { value: e.target.value })}
                            style={{ flex: 1, boxSizing: 'border-box' }} />
                          <button type="button" className="btn-small btn-danger" onClick={() => removeSplitBody(si, bi)}>✕</button>
                        </div>
                      ))}
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button type="button" className="btn-small" onClick={() => addSplitBody(si, 'paragraph')}>+ Paragraph</button>
                        <button type="button" className="btn-small" onClick={() => addSplitBody(si, 'list')}>+ List</button>
                      </div>
                    </div>
                  )}

                  {/* FULL SECTION — product cards */}
                  {sec.type === 'full' && (
                    <div>
                      <input placeholder="Section Heading (e.g. Our Top Christmas Picks)" value={sec.heading || ''}
                        onChange={e => updateSection(si, { heading: e.target.value })}
                        style={{ width: '100%', boxSizing: 'border-box', marginBottom: 6 }} />
                      <input placeholder="Subheading (optional)" value={sec.subheading || ''}
                        onChange={e => updateSection(si, { subheading: e.target.value })}
                        style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }} />
                      {(sec.cards || []).map((card, ci) => (
                        <div key={ci} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                          <select
                            value={card._productId || ''}
                            onChange={e => {
                              const prod = allProducts.find(p => (p._id || p.id) === e.target.value);
                              updateCard(si, ci, { _productId: prod ? (prod._id || prod.id) : '' });
                            }}
                            style={{ flex: 1, padding: '8px 10px', border: '1.5px solid #ddd', borderRadius: 6 }}
                          >
                            <option value="">— Select a product —</option>
                            {allProducts.map(p => (
                              <option key={p._id || p.id} value={p._id || p.id}>{p.name}</option>
                            ))}
                          </select>
                          {card._productId && (() => {
                            const prod = allProducts.find(p => (p._id || p.id) === card._productId);
                            return prod ? (
                              <img src={getImageUrl(prod.image || (prod.images && prod.images[0]))} alt={prod.name}
                                style={{ height: 40, width: 40, objectFit: 'contain', borderRadius: 4, border: '1px solid #ddd', background: '#f9f9f9' }}
                                onError={e => { e.target.style.display = 'none'; }} />
                            ) : null;
                          })()}
                          <button type="button" className="btn-small btn-danger" onClick={() => removeCard(si, ci)}>✕</button>
                        </div>
                      ))}
                      <button type="button" className="btn-small" onClick={() => addCard(si)}>+ Add Product</button>
                    </div>
                  )}

                  {/* FEATURES BAR */}
                  {sec.type === 'features' && (
                    <div>
                      <input placeholder="Section Heading (e.g. Why Choose Green Wheels)" value={sec.heading || ''}
                        onChange={e => updateSection(si, { heading: e.target.value })}
                        style={{ width: '100%', boxSizing: 'border-box', marginBottom: 10 }} />
                      {(sec.items || []).map((f, fi) => (
                        <div key={fi} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', marginBottom: 8 }}>
                          <input placeholder="Icon (emoji)" value={f.icon || ''}
                            onChange={e => updateFeature(si, fi, { icon: e.target.value })} style={{ width: 60 }} />
                          <input placeholder="Feature heading" value={f.heading || ''}
                            onChange={e => updateFeature(si, fi, { heading: e.target.value })} style={{ flex: 1 }} />
                          <input placeholder="Feature description" value={f.text || ''}
                            onChange={e => updateFeature(si, fi, { text: e.target.value })} style={{ flex: 2 }} />
                          <button type="button" className="btn-small btn-danger" onClick={() => removeFeature(si, fi)}>✕</button>
                        </div>
                      ))}
                      <button type="button" className="btn-small" onClick={() => addFeature(si)}>+ Add Feature</button>
                    </div>
                  )}

                  {/* CTA SECTION */}
                  {sec.type === 'cta' && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <input placeholder="CTA Heading" value={sec.heading || ''}
                        onChange={e => updateSection(si, { heading: e.target.value })}
                        style={{ width: '100%', boxSizing: 'border-box' }} />
                      <textarea rows={2} placeholder="CTA description text" value={sec.text || ''}
                        onChange={e => updateSection(si, { text: e.target.value })}
                        style={{ width: '100%', boxSizing: 'border-box' }} />
                    </div>
                  )}
                </div>
              ))}

              {/* Add section buttons */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                <button type="button" className="btn-small" onClick={() => addSection('split-row')}>+ Split Row</button>
                <button type="button" className="btn-small" onClick={() => addSection('full')}>+ Product Cards</button>
                <button type="button" className="btn-small" onClick={() => addSection('features')}>+ Features Bar</button>
                <button type="button" className="btn-small" onClick={() => addSection('cta')}>+ CTA Section</button>
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input type="checkbox" checked={blogForm.published}
                  onChange={e => setBlogForm(f => ({ ...f, published: e.target.checked }))} />
                Published (visible on site)
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn">{editingPost ? 'Update Post' : 'Add Post'}</button>
                {editingPost && (
                  <button type="button" className="btn" onClick={() => { setEditingPost(null); setBlogForm({ title: '', slug: '', excerpt: '', image: '', category: '', author: '', published: true }); setBlogSections([]); }}>Cancel</button>
                )}
              </div>
            </form>

            <table className="admin-table" style={{ marginTop: 32 }}>
              <thead>
                <tr><th>Title</th><th>Category</th><th>Author</th><th>Status</th><th>Date</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {blogPosts.map((p, i) => (
                  <tr key={p._id || i}>
                    <td>{p.title}</td>
                    <td>{p.category || '—'}</td>
                    <td>{p.author || '—'}</td>
                    <td><span style={{ color: p.published ? '#00d084' : '#999', fontWeight: 600 }}>{p.published ? 'Published' : 'Draft'}</span></td>
                    <td style={{ fontSize: 12 }}>{new Date(p.createdAt).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</td>
                    <td>
                      <button className="btn-small" onClick={() => handleEditPost(p)}>Edit</button>
                      <button className="btn-small btn-danger" onClick={() => handleDeletePost(p._id)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'biketowork' && btwData !== null && (
          <div className="admin-content">
            <h2>Bike to Work Scheme Page</h2>
            {btwMsg && <p className="save-msg">{btwMsg}</p>}
            <div className="admin-form">

              <label><strong>Hero Title</strong></label>
              <input value={btwData.heroTitle || ''} onChange={e => setBtwData(d => ({ ...d, heroTitle: e.target.value }))} placeholder="Bike to Work Scheme" />

              <label><strong>Hero Subtitle</strong></label>
              <input value={btwData.heroSubtitle || ''} onChange={e => setBtwData(d => ({ ...d, heroSubtitle: e.target.value }))} placeholder="Save up to 52%..." />

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label><strong>Hero CTA Text</strong></label>
                  <input value={btwData.heroCta || ''} onChange={e => setBtwData(d => ({ ...d, heroCta: e.target.value }))} placeholder="Shop Electric Bikes" />
                </div>
                <div style={{ flex: 1 }}>
                  <label><strong>Hero CTA Link</strong></label>
                  <input value={btwData.heroCtaLink || ''} onChange={e => setBtwData(d => ({ ...d, heroCtaLink: e.target.value }))} placeholder="/products?category=electric-bikes" />
                </div>
              </div>

              <label><strong>Intro Title</strong></label>
              <input value={btwData.introTitle || ''} onChange={e => setBtwData(d => ({ ...d, introTitle: e.target.value }))} placeholder="What is the Bike to Work Scheme?" />

              <label><strong>Intro Text</strong></label>
              <textarea rows={4} value={btwData.introText || ''} onChange={e => setBtwData(d => ({ ...d, introText: e.target.value }))} placeholder="The Bike to Work Scheme is..." />

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label><strong>Standard Bike Limit</strong></label>
                  <input value={btwData.limitEmployee || ''} onChange={e => setBtwData(d => ({ ...d, limitEmployee: e.target.value }))} placeholder="€1,500" />
                </div>
                <div style={{ flex: 1 }}>
                  <label><strong>Cargo / E-Bike Limit</strong></label>
                  <input value={btwData.limitCargo || ''} onChange={e => setBtwData(d => ({ ...d, limitCargo: e.target.value }))} placeholder="€3,000" />
                </div>
              </div>

              <label><strong>How It Works Steps</strong></label>
              {(btwData.steps || []).map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, marginBottom: 6 }}>
                  <input value={step.icon || ''} onChange={e => { const s=[...btwData.steps]; s[i]={...s[i],icon:e.target.value}; setBtwData(d=>({...d,steps:s})); }} placeholder="1" style={{ width: 50 }} />
                  <input value={step.title || ''} onChange={e => { const s=[...btwData.steps]; s[i]={...s[i],title:e.target.value}; setBtwData(d=>({...d,steps:s})); }} placeholder="Step title" style={{ flex: 1 }} />
                  <input value={step.text || ''} onChange={e => { const s=[...btwData.steps]; s[i]={...s[i],text:e.target.value}; setBtwData(d=>({...d,steps:s})); }} placeholder="Step description" style={{ flex: 2 }} />
                  <button type="button" className="btn-small btn-danger" onClick={() => setBtwData(d => ({ ...d, steps: d.steps.filter((_,idx)=>idx!==i) }))}>✕</button>
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setBtwData(d => ({ ...d, steps: [...(d.steps||[]), { icon: String((d.steps||[]).length+1), title: '', text: '' }] }))}>+ Add Step</button>

              <label><strong>Savings Table Title</strong></label>
              <input value={btwData.savingsTitle || ''} onChange={e => setBtwData(d => ({ ...d, savingsTitle: e.target.value }))} placeholder="How Much Can You Save?" />

              <label><strong>Savings Table Rows</strong> (label | standard | higher)</label>
              {(btwData.savingsRows || []).map((row, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <input value={row.label || ''} onChange={e => { const r=[...btwData.savingsRows]; r[i]={...r[i],label:e.target.value}; setBtwData(d=>({...d,savingsRows:r})); }} placeholder="Label" style={{ flex: 2 }} />
                  <input value={row.standard || ''} onChange={e => { const r=[...btwData.savingsRows]; r[i]={...r[i],standard:e.target.value}; setBtwData(d=>({...d,savingsRows:r})); }} placeholder="Standard" style={{ flex: 1 }} />
                  <input value={row.higher || ''} onChange={e => { const r=[...btwData.savingsRows]; r[i]={...r[i],higher:e.target.value}; setBtwData(d=>({...d,savingsRows:r})); }} placeholder="Higher" style={{ flex: 1 }} />
                  <button type="button" className="btn-small btn-danger" onClick={() => setBtwData(d => ({ ...d, savingsRows: d.savingsRows.filter((_,idx)=>idx!==i) }))}>✕</button>
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setBtwData(d => ({ ...d, savingsRows: [...(d.savingsRows||[]), { label:'', standard:'', higher:'' }] }))}>+ Add Row</button>

              <label><strong>FAQ Title</strong></label>
              <input value={btwData.faqTitle || ''} onChange={e => setBtwData(d => ({ ...d, faqTitle: e.target.value }))} placeholder="Frequently Asked Questions" />

              <label><strong>FAQs</strong></label>
              {(btwData.faqs || []).map((faq, i) => (
                <div key={i} style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 12, marginBottom: 6 }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                    <input value={faq.q || ''} onChange={e => { const f=[...btwData.faqs]; f[i]={...f[i],q:e.target.value}; setBtwData(d=>({...d,faqs:f})); }} placeholder="Question" style={{ flex: 1 }} />
                    <button type="button" className="btn-small btn-danger" onClick={() => setBtwData(d => ({ ...d, faqs: d.faqs.filter((_,idx)=>idx!==i) }))}>✕</button>
                  </div>
                  <textarea rows={2} value={faq.a || ''} onChange={e => { const f=[...btwData.faqs]; f[i]={...f[i],a:e.target.value}; setBtwData(d=>({...d,faqs:f})); }} placeholder="Answer" style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setBtwData(d => ({ ...d, faqs: [...(d.faqs||[]), { q:'', a:'' }] }))}>+ Add FAQ</button>

              <label><strong>CTA Title</strong></label>
              <input value={btwData.ctaTitle || ''} onChange={e => setBtwData(d => ({ ...d, ctaTitle: e.target.value }))} placeholder="Ready to Get Started?" />

              <label><strong>CTA Text</strong></label>
              <textarea rows={2} value={btwData.ctaText || ''} onChange={e => setBtwData(d => ({ ...d, ctaText: e.target.value }))} placeholder="Visit our store or browse online..." />

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label><strong>CTA Button Text</strong></label>
                  <input value={btwData.ctaBtn || ''} onChange={e => setBtwData(d => ({ ...d, ctaBtn: e.target.value }))} placeholder="Shop Electric Bikes" />
                </div>
                <div style={{ flex: 1 }}>
                  <label><strong>CTA Button Link</strong></label>
                  <input value={btwData.ctaBtnLink || ''} onChange={e => setBtwData(d => ({ ...d, ctaBtnLink: e.target.value }))} placeholder="/products?category=electric-bikes" />
                </div>
              </div>

              <label><strong>CTA Phone Number</strong></label>
              <input value={btwData.ctaPhone || ''} onChange={e => setBtwData(d => ({ ...d, ctaPhone: e.target.value }))} placeholder="087 100 2241" />

              <button className="btn" onClick={async () => {
                try {
                  await api.put('/admin/content/bike-to-work', btwData);
                  setBtwMsg('Bike to Work page saved!');
                  setTimeout(() => setBtwMsg(''), 3000);
                } catch (e) { setBtwMsg('Error: ' + (e.response?.data?.error || e.message)); }
              }}>Save Bike to Work Page</button>
            </div>
          </div>
        )}

                {activeTab === 'twilio' && (
          <div className="admin-content">
            <h2>WhatsApp / Twilio Settings</h2>
            {twilioMsg && <p className={`save-msg${twilioMsg.startsWith('Error') ? ' save-msg-err' : ''}`}>{twilioMsg}</p>}

            <div style={{ background: twilioConfigured ? '#e6f9f0' : '#fff8e1', border: `1px solid ${twilioConfigured ? '#a3e0c8' : '#ffe082'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{twilioConfigured ? '✅' : '⚠️'}</span>
              <span style={{ color: twilioConfigured ? '#00875a' : '#5d4037', fontWeight: 600 }}>
                {twilioConfigured ? `Twilio is configured and active (${twilioSettings.mode === 'live' ? 'LIVE' : 'SANDBOX'} mode).` : 'Twilio not configured — WhatsApp notifications are disabled.'}
              </span>
            </div>

            <div className="admin-form">

              {/* Trial account warning */}
              <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: 8, padding: '12px 16px', fontSize: 13, lineHeight: 1.7 }}>
                <strong style={{ color: '#e65100' }}>⚠️ Twilio Trial / Demo Account?</strong><br />
                WhatsApp requires joining the sandbox first. If you see "Channel not found" errors, either:<br />
                <strong>Option A:</strong> Switch <strong>Channel</strong> below to <strong>SMS</strong> — works on trial accounts immediately.<br />
                <strong>Option B:</strong> Keep WhatsApp and send <code>join &lt;your-code&gt;</code> to <code>+14155238886</code> on WhatsApp from your phone first.
              </div>

              <label><strong>Channel</strong></label>
              <select value={twilioSettings.channel || 'whatsapp'} onChange={e => setTwilioSettings(s => ({ ...s, channel: e.target.value }))}>
                <option value="whatsapp">WhatsApp (requires sandbox join or approved number)</option>
                <option value="sms">SMS (works on trial accounts)</option>
              </select>
              <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0' }}>
                {twilioSettings.channel === 'sms'
                  ? 'SMS mode: From number must be your Twilio phone number (e.g. +15005550006 for trial). Admin number receives plain SMS.'
                  : 'WhatsApp mode: From must be whatsapp:+14155238886 (sandbox) or your approved WhatsApp sender.'}
              </p>

              <label><strong>Environment</strong></label>
              <select value={twilioSettings.mode || 'sandbox'} onChange={e => setTwilioSettings(s => ({ ...s, mode: e.target.value }))}>
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="live">Live (Production)</option>
              </select>
              <p style={{ fontSize: 12, color: twilioSettings.mode === 'live' ? '#d32f2f' : '#888', margin: '-8px 0 0' }}>
                {twilioSettings.mode === 'live'
                  ? '⚠️ Live mode — messages will be sent to real customers. Ensure your Twilio account is upgraded and WhatsApp sender is approved.'
                  : 'Sandbox mode — only works with numbers that have joined the sandbox or verified numbers on trial accounts.'}
              </p>

              <label><strong>Account SID</strong></label>
              <input
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                value={twilioSettings.accountSid}
                onChange={e => setTwilioSettings(s => ({ ...s, accountSid: e.target.value }))}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />

              <label><strong>Auth Token</strong></label>
              <input
                type="password"
                placeholder={twilioConfigured ? '•••••••• (leave blank to keep existing)' : 'Paste your Auth Token'}
                value={twilioSettings.authToken}
                onChange={e => setTwilioSettings(s => ({ ...s, authToken: e.target.value }))}
              />

              <div>
                <label><strong>SMS From Number</strong> <span style={{ fontWeight: 400, color: '#888' }}>(your Twilio phone number — used when Channel is SMS)</span></label>
                <input
                  placeholder="+15017122661"
                  value={twilioSettings.smsFrom}
                  onChange={e => setTwilioSettings(s => ({ ...s, smsFrom: e.target.value }))}
                  style={{ fontFamily: 'monospace', fontSize: 13, opacity: twilioSettings.channel === 'sms' ? 1 : 0.5 }}
                />
                <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0' }}>
                  Find your Twilio number at <a href="https://console.twilio.com/us1/develop/phone-numbers/manage/active" target="_blank" rel="noopener noreferrer">Console → Phone Numbers → Active Numbers</a>.
                  Trial accounts can only send to <a href="https://console.twilio.com/us1/develop/phone-numbers/verified" target="_blank" rel="noopener noreferrer">verified numbers</a>.
                </p>
              </div>

              <label><strong>WhatsApp From Number</strong> <span style={{ fontWeight: 400, color: '#888' }}>(sandbox or approved sender)</span></label>
              <input
                placeholder="whatsapp:+14155238886"
                value={twilioSettings.whatsappFrom}
                onChange={e => setTwilioSettings(s => ({ ...s, whatsappFrom: e.target.value }))}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
              <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0' }}>Sandbox default: <code>whatsapp:+14155238886</code> — replace with your approved number for production.</p>

              <label><strong>{twilioSettings.channel === 'sms' ? 'Admin SMS Number' : 'Admin WhatsApp Number'}</strong> <span style={{ fontWeight: 400, color: '#888' }}>(receives order + new account alerts)</span></label>
              <input
                placeholder="+353871002241"
                value={twilioSettings.adminWhatsapp}
                onChange={e => setTwilioSettings(s => ({ ...s, adminWhatsapp: e.target.value }))}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
              <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0' }}>Include country code, e.g. <code>+353871002241</code></p>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                <button className="btn" onClick={async () => {
                  try {
                    await api.put('/admin/twilio-settings', twilioSettings);
                    const { data: refreshed } = await api.get('/admin/twilio-settings');
                    setTwilioConfigured(refreshed.configured || false);
                    setTwilioSettings(s => ({ ...s, authToken: refreshed.authToken || s.authToken }));
                    setTwilioMsg('Twilio settings saved!');
                    setTimeout(() => setTwilioMsg(''), 3000);
                  } catch (e) { setTwilioMsg('Error: ' + (e.response?.data?.error || e.message)); }
                }}>Save Settings</button>

                <button
                  className="btn"
                  style={{ background: twilioTesting ? '#aaa' : '#25D366' }}
                  disabled={twilioTesting || !twilioConfigured}
                  onClick={async () => {
                    setTwilioTesting(true);
                    setTwilioMsg('Sending test WhatsApp message...');
                    try {
                      const { data } = await api.post('/admin/twilio-settings/test', { to: twilioSettings.adminWhatsapp });
                      setTwilioMsg(`Test message sent to ${data.sentTo}!`);
                    } catch (e) {
                      setTwilioMsg('Error: ' + (e.response?.data?.error || e.message));
                    }
                    setTwilioTesting(false);
                    setTimeout(() => setTwilioMsg(''), 6000);
                  }}
                >
                  {twilioTesting ? 'Sending...' : (twilioSettings.channel === 'sms' ? 'Send Test SMS' : 'Send Test WhatsApp')}
                </button>
              </div>

              <div style={{ background: '#f4f6f9', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#555', lineHeight: 1.8 }}>
                <strong style={{ display: 'block', marginBottom: 6, color: '#1a1a2e' }}>Setup Guide</strong>
                <strong>SMS (easiest for trial accounts):</strong><br />
                1. Set <strong>Channel</strong> to <strong>SMS</strong><br />
                2. Set <strong>From</strong> to your Twilio trial number (find it in Console → Phone Numbers)<br />
                3. Set <strong>Admin SMS Number</strong> to your mobile (must be a verified number on trial)<br />
                4. Save and click <strong>Send Test SMS</strong><br /><br />
                <strong>WhatsApp Sandbox:</strong><br />
                1. Set <strong>Channel</strong> to <strong>WhatsApp</strong><br />
                2. Go to <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer">Twilio Console</a> → Messaging → Try it out → Send a WhatsApp message<br />
                3. On your phone, send the join code (e.g. <code>join silver-tiger</code>) to <code>+14155238886</code> on WhatsApp<br />
                4. Save settings and click <strong>Send Test WhatsApp</strong>
              </div>
            </div>
          </div>
        )}
{activeTab === 'smtp' && (
          <div className="admin-content">
            <h2>Email / SMTP Settings</h2>
            {smtpMsg && <p className={`save-msg${smtpMsg.startsWith('Error') ? ' save-msg-err' : ''}`}>{smtpMsg}</p>}

            <div style={{ background: smtpConfigured ? '#e6f9f0' : '#fff8e1', border: `1px solid ${smtpConfigured ? '#a3e0c8' : '#ffe082'}`, borderRadius: 10, padding: '12px 16px', marginBottom: 20, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 18 }}>{smtpConfigured ? '✅' : '⚠️'}</span>
              <span style={{ color: smtpConfigured ? '#00875a' : '#5d4037', fontWeight: 600 }}>
                {smtpConfigured ? 'SMTP is configured and active.' : 'SMTP not configured — emails are disabled. Fill in your credentials below.'}
              </span>
            </div>

            <div className="admin-form">
              <p style={{ fontSize: 13, color: '#666', margin: 0, lineHeight: 1.7 }}>
                Use a <strong>Gmail App Password</strong> (not your normal password).<br />
                Generate one at: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">myaccount.google.com/apppasswords</a>
                &nbsp;(requires 2-Step Verification enabled).
              </p>

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 2 }}>
                  <label><strong>SMTP Host</strong></label>
                  <input placeholder="smtp.gmail.com" value={smtpSettings.host} onChange={e => setSmtpSettings(s => ({ ...s, host: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label><strong>Port</strong></label>
                  <input placeholder="587" value={smtpSettings.port} onChange={e => setSmtpSettings(s => ({ ...s, port: e.target.value }))} />
                </div>
              </div>

              <label><strong>Email Address (Username)</strong></label>
              <input type="email" placeholder="your@gmail.com" value={smtpSettings.user} onChange={e => setSmtpSettings(s => ({ ...s, user: e.target.value }))} />

              <label><strong>App Password</strong></label>
              <input type="password" placeholder={smtpConfigured ? '••••••••  (leave blank to keep existing)' : 'Paste your Gmail App Password'} value={smtpSettings.pass} onChange={e => setSmtpSettings(s => ({ ...s, pass: e.target.value }))} />

              <label><strong>From Name / Address</strong> <span style={{ fontWeight: 400, color: '#888' }}>(shown in email From field)</span></label>
              <input placeholder="Green Wheels Scooters <your@gmail.com>" value={smtpSettings.from} onChange={e => setSmtpSettings(s => ({ ...s, from: e.target.value }))} />

              <label><strong>Admin Notification Email</strong> <span style={{ fontWeight: 400, color: '#888' }}>(receives order + new account alerts)</span></label>
              <input type="email" placeholder="sales@greenwheelsscooters.ie" value={smtpSettings.adminEmail} onChange={e => setSmtpSettings(s => ({ ...s, adminEmail: e.target.value }))} />

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
                <button className="btn" onClick={async () => {
                  try {
                    await api.put('/admin/smtp-settings', smtpSettings);
                    setSmtpMsg('SMTP settings saved!');
                    setSmtpConfigured(!!(smtpSettings.user && smtpSettings.pass));
                    setTimeout(() => setSmtpMsg(''), 3000);
                  } catch (e) { setSmtpMsg('Error: ' + (e.response?.data?.error || e.message)); }
                }}>Save Settings</button>

                <button
                  className="btn"
                  style={{ background: smtpTesting ? '#aaa' : '#1a73e8' }}
                  disabled={smtpTesting || !smtpConfigured}
                  onClick={async () => {
                    setSmtpTesting(true);
                    setSmtpMsg('Sending test email...');
                    try {
                      const { data } = await api.post('/admin/smtp-settings/test', { testTo: smtpSettings.adminEmail || smtpSettings.user });
                      setSmtpMsg(`Test email sent to ${data.sentTo}!`);
                    } catch (e) {
                      setSmtpMsg('Error: ' + (e.response?.data?.error || e.message));
                    }
                    setSmtpTesting(false);
                    setTimeout(() => setSmtpMsg(''), 6000);
                  }}
                >
                  {smtpTesting ? 'Sending...' : 'Send Test Email'}
                </button>
              </div>

              <div style={{ background: '#f4f6f9', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#555', lineHeight: 1.8 }}>
                <strong style={{ display: 'block', marginBottom: 6, color: '#1a1a2e' }}>Setup Guide (Gmail)</strong>
                1. Go to your Google Account &rarr; Security &rarr; 2-Step Verification (enable it)<br />
                2. Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer">App Passwords</a> &rarr; create a new app password<br />
                3. Copy the 16-character password and paste it in the App Password field above<br />
                4. Set Host: <code>smtp.gmail.com</code>, Port: <code>587</code><br />
                5. Save and click Send Test Email to verify
              </div>
            </div>
          </div>
        )}


        {activeTab === 'adminUsers' && role === 'superadmin' && (
          <div className="admin-content">
            <h2>Admin Users</h2>
            {adminUsersMsg && <p className={`save-msg${adminUsersMsg.startsWith('Error') ? ' save-msg-err' : ''}`}>{adminUsersMsg}</p>}

            <div className="admin-form" style={{ marginBottom: 24 }}>
              <h3 style={{ margin: '0 0 12px' }}>{editingAdminUser ? `Editing: ${editingAdminUser.username}` : 'Add New Admin User'}</h3>

              <div style={{ display: 'flex', gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <label><strong>Username</strong></label>
                  <input placeholder="e.g. john" value={adminUserForm.username} onChange={e => setAdminUserForm(f => ({ ...f, username: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label><strong>Password</strong> {editingAdminUser && <span style={{ fontWeight: 400, color: '#888' }}>(leave blank to keep)</span>}</label>
                  <input type="password" placeholder={editingAdminUser ? 'New password (optional)' : 'Set password'} value={adminUserForm.password} onChange={e => setAdminUserForm(f => ({ ...f, password: e.target.value }))} />
                </div>
              </div>

              <label><strong>Role</strong></label>
              <select value={adminUserForm.role} onChange={e => setAdminUserForm(f => ({ ...f, role: e.target.value, permissions: e.target.value === 'superadmin' ? ALL_TABS : f.permissions }))}>
                <option value="superadmin">Super Admin (full access)</option>
                <option value="admin">Admin (full access)</option>
                <option value="custom">Custom (select permissions)</option>
              </select>

              {adminUserForm.role === 'custom' && (
                <div>
                  <label><strong>Permissions</strong></label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {ALL_TABS.map(tab => (
                      <label key={tab} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, background: '#fff', border: '1px solid #ddd', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={adminUserForm.permissions.includes(tab)}
                          onChange={e => setAdminUserForm(f => ({ ...f, permissions: e.target.checked ? [...f.permissions, tab] : f.permissions.filter(p => p !== tab) }))}
                        />
                        {tab}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={async () => {
                  if (!adminUserForm.username) { setAdminUsersMsg('Error: Username required.'); return; }
                  if (!editingAdminUser && !adminUserForm.password) { setAdminUsersMsg('Error: Password required for new users.'); return; }
                  try {
                    if (editingAdminUser) {
                      await api.put(`/admin/users/${editingAdminUser._id}`, adminUserForm);
                      setAdminUsersMsg('User updated!');
                    } else {
                      await api.post('/admin/users', adminUserForm);
                      setAdminUsersMsg('User created!');
                    }
                    setEditingAdminUser(null);
                    setAdminUserForm({ username: '', password: '', role: 'admin', permissions: ALL_TABS });
                    loadData();
                    setTimeout(() => setAdminUsersMsg(''), 3000);
                  } catch (e) { setAdminUsersMsg('Error: ' + (e.response?.data?.error || e.message)); }
                }}>{editingAdminUser ? 'Update User' : 'Create User'}</button>
                {editingAdminUser && <button className="btn" onClick={() => { setEditingAdminUser(null); setAdminUserForm({ username: '', password: '', role: 'admin', permissions: ALL_TABS }); }}>Cancel</button>}
              </div>
            </div>

            <table className="admin-table">
              <thead>
                <tr><th>Username</th><th>Role</th><th>Permissions</th><th>Status</th><th>Created</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {adminUsers.map(u => (
                  <tr key={u._id}>
                    <td><strong>{u.username}</strong> {u.username === username && <span style={{ fontSize: 11, color: '#888' }}>(you)</span>}</td>
                    <td>
                      <span style={{ padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 700, background: u.role === 'superadmin' ? '#fce4ec' : u.role === 'admin' ? '#e3f2fd' : '#f3e5f5', color: u.role === 'superadmin' ? '#c62828' : u.role === 'admin' ? '#1565c0' : '#6a1b9a', textTransform: 'uppercase' }}>{u.role}</span>
                    </td>
                    <td style={{ fontSize: 12, color: '#666' }}>
                      {u.role === 'superadmin' ? 'All' : (u.permissions || []).length + ' tabs'}
                    </td>
                    <td><span style={{ color: u.active ? '#00875a' : '#dc3545', fontWeight: 600, fontSize: 13 }}>{u.active ? 'Active' : 'Disabled'}</span></td>
                    <td style={{ fontSize: 12 }}>{new Date(u.createdAt).toLocaleDateString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-small" onClick={() => {
                        setEditingAdminUser(u);
                        setAdminUserForm({ username: u.username, password: '', role: u.role, permissions: u.permissions || ALL_TABS });
                      }}>Edit</button>
                      <button className="btn-small" onClick={async () => {
                        try { await api.put(`/admin/users/${u._id}`, { active: !u.active }); loadData(); }
                        catch (e) { setAdminUsersMsg('Error: ' + (e.response?.data?.error || e.message)); }
                      }}>{u.active ? 'Disable' : 'Enable'}</button>
                      {u.username !== username && (
                        <button className="btn-small btn-danger" onClick={async () => {
                          if (!window.confirm(`Delete user "${u.username}"?`)) return;
                          try { await api.delete(`/admin/users/${u._id}`); loadData(); }
                          catch (e) { setAdminUsersMsg('Error: ' + (e.response?.data?.error || e.message)); }
                        }}>Delete</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'changePassword' && (
          <div className="admin-content">
            <h2>Change Password</h2>
            {changePwMsg && <p className={`save-msg${changePwMsg.startsWith('Error') ? ' save-msg-err' : ''}`}>{changePwMsg}</p>}
            <div className="admin-form" style={{ maxWidth: 420 }}>
              <label><strong>Current Password</strong></label>
              <input type="password" placeholder="Your current password" value={changePwForm.current} onChange={e => setChangePwForm(f => ({ ...f, current: e.target.value }))} />
              <label><strong>New Password</strong></label>
              <input type="password" placeholder="At least 6 characters" value={changePwForm.next} onChange={e => setChangePwForm(f => ({ ...f, next: e.target.value }))} />
              <label><strong>Confirm New Password</strong></label>
              <input type="password" placeholder="Repeat new password" value={changePwForm.confirm} onChange={e => setChangePwForm(f => ({ ...f, confirm: e.target.value }))} />
              <button className="btn" onClick={async () => {
                if (changePwForm.next !== changePwForm.confirm) { setChangePwMsg('Error: Passwords do not match.'); return; }
                if (changePwForm.next.length < 6) { setChangePwMsg('Error: Password must be at least 6 characters.'); return; }
                try {
                  await api.put('/admin/change-password', { currentPassword: changePwForm.current, newPassword: changePwForm.next });
                  setChangePwMsg('Password changed successfully!');
                  setChangePwForm({ current: '', next: '', confirm: '' });
                  setTimeout(() => setChangePwMsg(''), 4000);
                } catch (e) { setChangePwMsg('Error: ' + (e.response?.data?.error || e.message)); }
              }}>Change Password</button>
            </div>
          </div>
        )}

        {activeTab === 'support' && (
          <div className="admin-content">
            <h2>Support Page</h2>

            {/* Sub-tab bar */}
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 24, borderBottom: '2px solid #eee', paddingBottom: 12 }}>
              <button
                className={`btn-small${supportSubTab === 'settings' ? ' active' : ''}`}
                style={{ background: supportSubTab === 'settings' ? '#222' : '', color: supportSubTab === 'settings' ? '#fff' : '' }}
                onClick={() => setSupportSubTab('settings')}
              >🏗️ Page Settings</button>
              {POLICY_PAGES.map(p => (
                <button
                  key={p.key}
                  className={`btn-small${supportSubTab === p.key ? ' active' : ''}`}
                  style={{ background: supportSubTab === p.key ? '#222' : '', color: supportSubTab === p.key ? '#fff' : '' }}
                  onClick={() => setSupportSubTab(p.key)}
                >{p.icon} {p.title}</button>
              ))}
            </div>

            {/* Page Settings sub-tab */}
            {supportSubTab === 'settings' && (
              <div className="admin-form">
                {supportMsg && <p className="save-msg">{supportMsg}</p>}

                <label><strong>Hero Title</strong></label>
                <input value={supportData.heroTitle} onChange={e => setSupportData(d => ({ ...d, heroTitle: e.target.value }))} placeholder="Support Centre" />

                <label><strong>Hero Subtitle</strong></label>
                <input value={supportData.heroSubtitle} onChange={e => setSupportData(d => ({ ...d, heroSubtitle: e.target.value }))} placeholder="Everything you need..." />

                <label><strong>CTA Title</strong></label>
                <input value={supportData.ctaTitle} onChange={e => setSupportData(d => ({ ...d, ctaTitle: e.target.value }))} placeholder="Still need help?" />

                <label><strong>CTA Subtitle</strong></label>
                <input value={supportData.ctaSubtitle} onChange={e => setSupportData(d => ({ ...d, ctaSubtitle: e.target.value }))} placeholder="Our team is available..." />

                <label><strong>CTA Phone Number</strong></label>
                <input value={supportData.ctaPhone} onChange={e => setSupportData(d => ({ ...d, ctaPhone: e.target.value }))} placeholder="0871002241" />

                <label><strong>Support Cards</strong></label>
                {supportData.links.map((link, i) => (
                  <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 14, marginBottom: 10, background: '#fafafa' }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <input placeholder="Icon" value={link.icon} onChange={e => { const l = [...supportData.links]; l[i] = { ...l[i], icon: e.target.value }; setSupportData(d => ({ ...d, links: l })); }} style={{ width: 60 }} />
                      <input placeholder="Title" value={link.title} onChange={e => { const l = [...supportData.links]; l[i] = { ...l[i], title: e.target.value }; setSupportData(d => ({ ...d, links: l })); }} style={{ flex: 1 }} />
                      <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, whiteSpace: 'nowrap' }}>
                        <input type="checkbox" checked={link.external} onChange={e => { const l = [...supportData.links]; l[i] = { ...l[i], external: e.target.checked }; setSupportData(d => ({ ...d, links: l })); }} /> External
                      </label>
                      <button type="button" className="btn-small btn-danger" onClick={() => setSupportData(d => ({ ...d, links: d.links.filter((_, idx) => idx !== i) }))}>✕</button>
                    </div>
                    <input placeholder="URL (e.g. /support/tos or https://...)" value={link.href} onChange={e => { const l = [...supportData.links]; l[i] = { ...l[i], href: e.target.value }; setSupportData(d => ({ ...d, links: l })); }} style={{ marginBottom: 6 }} />
                    <input placeholder="Description" value={link.desc} onChange={e => { const l = [...supportData.links]; l[i] = { ...l[i], desc: e.target.value }; setSupportData(d => ({ ...d, links: l })); }} />
                  </div>
                ))}
                <button type="button" className="btn-small" onClick={() => setSupportData(d => ({ ...d, links: [...d.links, { icon: '📄', title: '', desc: '', href: '', external: false }] }))}>+ Add Card</button>

                <button className="btn" style={{ marginTop: 16 }} onClick={async () => {
                  try {
                    await api.put('/admin/content/support', supportData);
                    setSupportMsg('Support page settings saved!');
                    setTimeout(() => setSupportMsg(''), 3000);
                  } catch (e) { setSupportMsg('Error: ' + (e.response?.data?.error || e.message)); }
                }}>Save Page Settings</button>
              </div>
            )}

            {/* Policy content sub-tabs */}
            {POLICY_PAGES.map(p => supportSubTab === p.key && (
              <div key={p.key} className="admin-form">
                {policyMsg && <p className="save-msg">{policyMsg}</p>}

                <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px', lineHeight: 1.6 }}>
                  Build the <strong>{p.title}</strong> page by adding sections. Each section has a heading and one or more paragraphs.
                </p>

                {(Array.isArray(policyContents[p.key]) ? policyContents[p.key] : []).map((sec, si) => {
                  const updateSec = (patch) => {
                    const updated = [...(policyContents[p.key] || [])];
                    updated[si] = { ...updated[si], ...patch };
                    setPolicyContents(prev => ({ ...prev, [p.key]: updated }));
                  };
                  return (
                  <div key={si} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 12, background: '#fafafa' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <strong style={{ fontSize: 13, color: '#555' }}>Section {si + 1}</strong>
                      <button type="button" className="btn-small btn-danger" onClick={() =>
                        setPolicyContents(prev => ({ ...prev, [p.key]: (prev[p.key] || []).filter((_, idx) => idx !== si) }))
                      }>✕ Remove Section</button>
                    </div>

                    {/* Heading */}
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Heading</label>
                    <input
                      placeholder="e.g. Introduction, Your Rights, How We Use Data..."
                      value={sec.heading || ''}
                      onChange={e => updateSec({ heading: e.target.value })}
                      style={{ marginBottom: 8 }}
                    />
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#555', display: 'block', marginBottom: 4 }}>Alignment</label>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                      {['left', 'center', 'right', 'justify'].map(a => (
                        <button key={a} type="button" onClick={() => updateSec({ align: a })}
                          style={{ padding: '4px 14px', fontSize: 12, borderRadius: 4, border: '1px solid #ccc', cursor: 'pointer',
                            background: (sec.align || 'left') === a ? '#1a1a2e' : '#fff',
                            color: (sec.align || 'left') === a ? '#fff' : '#333' }}>
                          {a.charAt(0).toUpperCase() + a.slice(1)}
                        </button>
                      ))}
                    </div>

                    {/* Content blocks */}
                    {(sec.blocks || []).map((block, bi) => (
                      <div key={bi} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: block.type === 'list' ? '#00695c' : '#1565c0',
                            background: block.type === 'list' ? '#e8f5e9' : '#e3f2fd',
                            padding: '2px 8px', borderRadius: 4, textAlign: 'center' }}>
                            {block.type === 'list' ? '• List' : '¶ Para'}
                          </span>
                          <select
                            value={block.type}
                            onChange={e => {
                              const updated = [...(policyContents[p.key] || [])];
                              const blocks = [...(updated[si].blocks || [])];
                              blocks[bi] = { ...blocks[bi], type: e.target.value };
                              updated[si] = { ...updated[si], blocks };
                              setPolicyContents(prev => ({ ...prev, [p.key]: updated }));
                            }}
                            style={{ fontSize: 11, padding: '3px 4px', border: '1px solid #ddd', borderRadius: 4 }}
                          >
                            <option value="paragraph">Paragraph</option>
                            <option value="list">Bullet List</option>
                          </select>
                        </div>

                        {block.type === 'paragraph' ? (
                          <textarea
                            rows={3}
                            placeholder="Enter paragraph text..."
                            value={block.value || ''}
                            onChange={e => {
                              const updated = [...(policyContents[p.key] || [])];
                              const blocks = [...(updated[si].blocks || [])];
                              blocks[bi] = { ...blocks[bi], value: e.target.value };
                              updated[si] = { ...updated[si], blocks };
                              setPolicyContents(prev => ({ ...prev, [p.key]: updated }));
                            }}
                            style={{ flex: 1, resize: 'vertical' }}
                          />
                        ) : (
                          <div style={{ flex: 1 }}>
                            {(block.items || ['']).map((item, ii) => (
                              <div key={ii} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 5 }}>
                                <span style={{ color: '#00695c', fontWeight: 700, fontSize: 16, flexShrink: 0 }}>•</span>
                                <input
                                  placeholder={`Bullet item ${ii + 1}...`}
                                  value={item}
                                  onChange={e => {
                                    const updated = [...(policyContents[p.key] || [])];
                                    const blocks = [...(updated[si].blocks || [])];
                                    const items = [...(blocks[bi].items || [])];
                                    items[ii] = e.target.value;
                                    blocks[bi] = { ...blocks[bi], items };
                                    updated[si] = { ...updated[si], blocks };
                                    setPolicyContents(prev => ({ ...prev, [p.key]: updated }));
                                  }}
                                  style={{ flex: 1 }}
                                />
                                {(block.items || []).length > 1 && (
                                  <button type="button" className="btn-small btn-danger" onClick={() => {
                                    const updated = [...(policyContents[p.key] || [])];
                                    const blocks = [...(updated[si].blocks || [])];
                                    blocks[bi] = { ...blocks[bi], items: (blocks[bi].items || []).filter((_, idx) => idx !== ii) };
                                    updated[si] = { ...updated[si], blocks };
                                    setPolicyContents(prev => ({ ...prev, [p.key]: updated }));
                                  }}>✕</button>
                                )}
                              </div>
                            ))}
                            <button type="button" className="btn-small" style={{ fontSize: 11, marginTop: 2 }} onClick={() => {
                              const updated = [...(policyContents[p.key] || [])];
                              const blocks = [...(updated[si].blocks || [])];
                              blocks[bi] = { ...blocks[bi], items: [...(blocks[bi].items || []), ''] };
                              updated[si] = { ...updated[si], blocks };
                              setPolicyContents(prev => ({ ...prev, [p.key]: updated }));
                            }}>+ Add Item</button>
                          </div>
                        )}

                        <button type="button" className="btn-small btn-danger" style={{ marginTop: 2, flexShrink: 0 }} onClick={() => {
                          const updated = [...(policyContents[p.key] || [])];
                          updated[si] = { ...updated[si], blocks: (updated[si].blocks || []).filter((_, idx) => idx !== bi) };
                          setPolicyContents(prev => ({ ...prev, [p.key]: updated }));
                        }}>✕</button>
                      </div>
                    ))}

                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button type="button" className="btn-small" onClick={() => updateSec({ blocks: [...(sec.blocks || []), { type: 'paragraph', value: '' }] })}>+ Paragraph</button>
                      <button type="button" className="btn-small" style={{ background: '#e8f5e9', color: '#2e7d32', border: '1px solid #a5d6a7' }} onClick={() => updateSec({ blocks: [...(sec.blocks || []), { type: 'list', items: [''] }] })}>+ Bullet List</button>
                    </div>
                  </div>
                  );
                })}

                <button type="button" className="btn-small" onClick={() => {
                  const current = Array.isArray(policyContents[p.key]) ? policyContents[p.key] : [];
                  setPolicyContents(prev => ({ ...prev, [p.key]: [...current, { heading: '', blocks: [{ type: 'paragraph', value: '' }] }] }));
                }}>+ Add Section</button>

                <button className="btn" style={{ marginTop: 16 }} onClick={async () => {
                  try {
                    await api.put(`/admin/content/policy_${p.key}`, { sections: policyContents[p.key] || [] });
                    setPolicyMsg(`${p.title} saved!`);
                    setTimeout(() => setPolicyMsg(''), 3000);
                  } catch (e) { setPolicyMsg('Error: ' + (e.response?.data?.error || e.message)); }
                }}>Save {p.title}</button>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'services' && (
          <div className="admin-content">
            <h2>Bike Rentals Page</h2>
            {servicesMsg && <p className="save-msg">{servicesMsg}</p>}
            <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#2e7d32' }}>
              <strong>Debug:</strong> includes={JSON.stringify(servicesData.includes?.length)} | requirements={JSON.stringify(servicesData.requirements?.length)} | faq={JSON.stringify(servicesData.faq?.length)}
            </div>
            <div className="admin-form">
              <label><strong>Hero Title</strong></label>
              <input value={servicesData.heroTitle} onChange={e => setServicesData(d => ({ ...d, heroTitle: e.target.value }))} placeholder="Bike Rentals" />

              <label><strong>Hero Subtitle</strong></label>
              <input value={servicesData.heroSubtitle} onChange={e => setServicesData(d => ({ ...d, heroSubtitle: e.target.value }))} placeholder="Explore the city..." />

              <label><strong>Hero Background Image (optional)</strong></label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input value={servicesData.heroImage || ''} onChange={e => setServicesData(d => ({ ...d, heroImage: e.target.value }))} placeholder="/images/..." style={{ flex: 1 }} />
                <input type="file" accept="image/*" onChange={async e => { const f = e.target.files[0]; if (!f) return; const url = await uploadImage(f); setServicesData(d => ({ ...d, heroImage: url })); }} />
              </div>
              {servicesData.heroImage && <img src={getImageUrl(servicesData.heroImage)} alt="" style={{ height: 80, borderRadius: 8, marginTop: 8 }} />}

              <label><strong>Intro Title</strong></label>
              <input value={servicesData.introTitle || ''} onChange={e => setServicesData(d => ({ ...d, introTitle: e.target.value }))} placeholder="Why Rent With Us?" />

              <label><strong>Intro Text</strong></label>
              <textarea value={servicesData.introText || ''} onChange={e => setServicesData(d => ({ ...d, introText: e.target.value }))} rows={3} placeholder="Whether you're a tourist..." />

              {/* Rental Plans */}
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0 }}><strong>Rental Plans</strong></label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={servicesData.showRentalPlans !== false} onChange={e => setServicesData(d => ({ ...d, showRentalPlans: e.target.checked }))} />
                  <span style={{ color: servicesData.showRentalPlans !== false ? '#00875a' : '#999' }}>{servicesData.showRentalPlans !== false ? 'Visible' : 'Hidden'}</span>
                </label>
              </div>
              {(servicesData.rentalPlans || []).map((plan, i) => (
                <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 14, marginBottom: 10, background: '#fafafa' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input placeholder="Plan Name" value={plan.name} onChange={e => { const p = [...(servicesData.rentalPlans||[])]; p[i] = { ...p[i], name: e.target.value }; setServicesData(d => ({ ...d, rentalPlans: p })); }} style={{ flex: 1 }} />
                    <input placeholder="Price (e.g. €55)" value={plan.price} onChange={e => { const p = [...(servicesData.rentalPlans||[])]; p[i] = { ...p[i], price: e.target.value }; setServicesData(d => ({ ...d, rentalPlans: p })); }} style={{ width: 80 }} />
                    <input placeholder="Duration" value={plan.duration} onChange={e => { const p = [...(servicesData.rentalPlans||[])]; p[i] = { ...p[i], duration: e.target.value }; setServicesData(d => ({ ...d, rentalPlans: p })); }} style={{ width: 100 }} />
                    <label style={{ fontSize: 12, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input type="checkbox" checked={plan.popular || false} onChange={e => { const p = [...(servicesData.rentalPlans||[])]; p[i] = { ...p[i], popular: e.target.checked }; setServicesData(d => ({ ...d, rentalPlans: p })); }} /> Popular
                    </label>
                    <button type="button" className="btn-small btn-danger" onClick={() => setServicesData(d => ({ ...d, rentalPlans: (d.rentalPlans||[]).filter((_, idx) => idx !== i) }))}>✕</button>
                  </div>
                  <input placeholder="Description" value={plan.description} onChange={e => { const p = [...(servicesData.rentalPlans||[])]; p[i] = { ...p[i], description: e.target.value }; setServicesData(d => ({ ...d, rentalPlans: p })); }} />
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setServicesData(d => ({ ...d, rentalPlans: [...(d.rentalPlans||[]), { name: '', price: '', duration: '', description: '', popular: false }] }))}>+ Add Plan</button>

              {/* Fleet */}
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0 }}><strong>Fleet (Bikes Available for Rental)</strong></label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={servicesData.showFleet !== false} onChange={e => setServicesData(d => ({ ...d, showFleet: e.target.checked }))} />
                  <span style={{ color: servicesData.showFleet !== false ? '#00875a' : '#999' }}>{servicesData.showFleet !== false ? 'Visible' : 'Hidden'}</span>
                </label>
              </div>
              <p style={{ fontSize: 12, color: '#888', margin: '0 0 10px' }}>Select electric bikes from your product catalogue to make available for rental.</p>
              {(servicesData.fleet || []).map((bike, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, padding: '10px 12px', background: '#f8f9fa', borderRadius: 8, border: '1px solid #e0e0e0' }}>
                  {bike.image && <img src={getImageUrl(bike.image)} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #ddd' }} onError={e => { e.target.style.display = 'none'; }} />}
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{bike.name}</div>
                    {bike.specs && <div style={{ fontSize: 11, color: '#888' }}>{bike.specs}</div>}
                  </div>
                  <button type="button" className="btn-small btn-danger" onClick={() => setServicesData(d => ({ ...d, fleet: (d.fleet||[]).filter((_, idx) => idx !== i) }))}>✕</button>
                </div>
              ))}
              <select
                value=""
                onChange={e => {
                  if (!e.target.value) return;
                  const prod = products.find(p => (p._id || p.id) === e.target.value);
                  if (!prod) return;
                  const exists = (servicesData.fleet || []).find(f => f.name === prod.name);
                  if (exists) return;
                  setServicesData(d => ({ ...d, fleet: [...(d.fleet||[]), { productId: prod._id || prod.id, name: prod.name, description: prod.description?.substring(0, 100) || '', specs: prod.range || '', image: prod.images?.[0] || prod.image || '' }] }));
                }}
                style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, marginTop: 8 }}
              >
                <option value="">— Add bike from Electric Bikes catalogue —</option>
                {products.filter(p => p.category === 'electric-bikes').map(p => (
                  <option key={p._id || p.id} value={p._id || p.id}>{p.name}{p.brand ? ` (${p.brand})` : ''}</option>
                ))}
              </select>
              {/* How It Works */}
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0 }}><strong>How It Works (Steps)</strong></label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={servicesData.showHowItWorks !== false} onChange={e => setServicesData(d => ({ ...d, showHowItWorks: e.target.checked }))} />
                  <span style={{ color: servicesData.showHowItWorks !== false ? '#00875a' : '#999' }}>{servicesData.showHowItWorks !== false ? 'Visible' : 'Hidden'}</span>
                </label>
              </div>
              {(servicesData.howItWorks || []).map((step, i) => (
                <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 14, marginBottom: 10, background: '#fafafa' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input placeholder="#" value={step.step} onChange={e => { const s = [...(servicesData.howItWorks||[])]; s[i] = { ...s[i], step: e.target.value }; setServicesData(d => ({ ...d, howItWorks: s })); }} style={{ width: 40 }} />
                    <input placeholder="Title" value={step.title} onChange={e => { const s = [...(servicesData.howItWorks||[])]; s[i] = { ...s[i], title: e.target.value }; setServicesData(d => ({ ...d, howItWorks: s })); }} style={{ flex: 1 }} />
                    <button type="button" className="btn-small btn-danger" onClick={() => setServicesData(d => ({ ...d, howItWorks: (d.howItWorks||[]).filter((_, idx) => idx !== i) }))}>✕</button>
                  </div>
                  <input placeholder="Description" value={step.description} onChange={e => { const s = [...(servicesData.howItWorks||[])]; s[i] = { ...s[i], description: e.target.value }; setServicesData(d => ({ ...d, howItWorks: s })); }} />
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setServicesData(d => ({ ...d, howItWorks: [...(d.howItWorks||[]), { step: String((d.howItWorks||[]).length + 1), title: '', description: '' }] }))}>+ Add Step</button>

              {/* Includes */}
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0 }}><strong>What's Included</strong></label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={servicesData.showIncludes !== false} onChange={e => setServicesData(d => ({ ...d, showIncludes: e.target.checked }))} />
                  <span style={{ color: servicesData.showIncludes !== false ? '#00875a' : '#999' }}>{servicesData.showIncludes !== false ? 'Visible' : 'Hidden'}</span>
                </label>
              </div>
              {(servicesData.includes || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input value={item} onChange={e => { const inc = [...(servicesData.includes||[])]; inc[i] = e.target.value; setServicesData(d => ({ ...d, includes: inc })); }} style={{ flex: 1 }} />
                  <button type="button" className="btn-small btn-danger" onClick={() => setServicesData(d => ({ ...d, includes: (d.includes||[]).filter((_, idx) => idx !== i) }))}>✕</button>
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setServicesData(d => ({ ...d, includes: [...(d.includes||[]), ''] }))}>+ Add Item</button>

              {/* Requirements */}
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0 }}><strong>Requirements</strong></label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={servicesData.showRequirements !== false} onChange={e => setServicesData(d => ({ ...d, showRequirements: e.target.checked }))} />
                  <span style={{ color: servicesData.showRequirements !== false ? '#00875a' : '#999' }}>{servicesData.showRequirements !== false ? 'Visible' : 'Hidden'}</span>
                </label>
              </div>
              {(servicesData.requirements || []).map((item, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input value={item} onChange={e => { const req = [...(servicesData.requirements||[])]; req[i] = e.target.value; setServicesData(d => ({ ...d, requirements: req })); }} style={{ flex: 1 }} />
                  <button type="button" className="btn-small btn-danger" onClick={() => setServicesData(d => ({ ...d, requirements: (d.requirements||[]).filter((_, idx) => idx !== i) }))}>✕</button>
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setServicesData(d => ({ ...d, requirements: [...(d.requirements||[]), ''] }))}>+ Add Requirement</button>

              {/* FAQ */}
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0 }}><strong>FAQ</strong></label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={servicesData.showFaq !== false} onChange={e => setServicesData(d => ({ ...d, showFaq: e.target.checked }))} />
                  <span style={{ color: servicesData.showFaq !== false ? '#00875a' : '#999' }}>{servicesData.showFaq !== false ? 'Visible' : 'Hidden'}</span>
                </label>
              </div>
              {(servicesData.faq || []).map((item, i) => (
                <div key={i} style={{ border: '1px solid #e0e0e0', borderRadius: 8, padding: 14, marginBottom: 10, background: '#fafafa' }}>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                    <input placeholder="Question" value={item.question} onChange={e => { const f = [...(servicesData.faq||[])]; f[i] = { ...f[i], question: e.target.value }; setServicesData(d => ({ ...d, faq: f })); }} style={{ flex: 1 }} />
                    <button type="button" className="btn-small btn-danger" onClick={() => setServicesData(d => ({ ...d, faq: (d.faq||[]).filter((_, idx) => idx !== i) }))}>✕</button>
                  </div>
                  <textarea placeholder="Answer" value={item.answer} onChange={e => { const f = [...(servicesData.faq||[])]; f[i] = { ...f[i], answer: e.target.value }; setServicesData(d => ({ ...d, faq: f })); }} rows={3} style={{ width: '100%', boxSizing: 'border-box' }} />
                </div>
              ))}
              <button type="button" className="btn-small" onClick={() => setServicesData(d => ({ ...d, faq: [...(d.faq||[]), { question: '', answer: '' }] }))}>+ Add FAQ</button>

              {/* CTA */}
              <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ margin: 0 }}><strong>CTA Section</strong></label>
                <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input type="checkbox" checked={servicesData.showCta !== false} onChange={e => setServicesData(d => ({ ...d, showCta: e.target.checked }))} />
                  <span style={{ color: servicesData.showCta !== false ? '#00875a' : '#999' }}>{servicesData.showCta !== false ? 'Visible' : 'Hidden'}</span>
                </label>
              </div>
              <label><strong>CTA Title</strong></label>
              <input value={servicesData.ctaTitle} onChange={e => setServicesData(d => ({ ...d, ctaTitle: e.target.value }))} placeholder="Ready to Ride?" />

              <label><strong>CTA Subtitle</strong></label>
              <input value={servicesData.ctaSubtitle} onChange={e => setServicesData(d => ({ ...d, ctaSubtitle: e.target.value }))} placeholder="Book your rental now..." />

              <label><strong>CTA Phone</strong></label>
              <input value={servicesData.ctaPhone} onChange={e => setServicesData(d => ({ ...d, ctaPhone: e.target.value }))} placeholder="0871002241" />

              <label><strong>CTA Button Text</strong></label>
              <input value={servicesData.ctaButtonText} onChange={e => setServicesData(d => ({ ...d, ctaButtonText: e.target.value }))} placeholder="Book Now" />

              <label><strong>CTA Button Link</strong></label>
              <input value={servicesData.ctaButtonLink} onChange={e => setServicesData(d => ({ ...d, ctaButtonLink: e.target.value }))} placeholder="/contact" />

              <button className="btn" style={{ marginTop: 24 }} onClick={async () => {
                try {
                  await api.put('/admin/content/services', servicesData);
                  setServicesMsg('Bike Rentals page saved!');
                  setTimeout(() => setServicesMsg(''), 3000);
                } catch (e) { setServicesMsg('Error: ' + (e.response?.data?.error || e.message)); }
              }}>Save Bike Rentals Page</button>
            </div>
          </div>
        )}

        {activeTab === 'dbSettings' && role === 'superadmin' && (
          <div className="admin-content">
            <h2>Database Settings</h2>
            {dbMsg && <p className={`save-msg${dbMsg.startsWith('Error') ? ' save-msg-err' : ''}`}>{dbMsg}</p>}

            {/* Status card */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
              <div style={{ flex: 1, minWidth: 200, background: dbSettings.connected ? '#e6f9f0' : '#fce4ec', border: `1px solid ${dbSettings.connected ? '#a3e0c8' : '#ef9a9a'}`, borderRadius: 10, padding: '16px 20px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 6 }}>Connection</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: dbSettings.connected ? '#00875a' : '#c62828' }}>
                  {dbSettings.connected ? 'Connected' : 'Disconnected'}
                </div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>Status: {dbSettings.status}</div>
              </div>
              <div style={{ flex: 1, minWidth: 200, background: '#f4f6f9', border: '1px solid #e0e4ea', borderRadius: 10, padding: '16px 20px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 6 }}>Database</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>{dbSettings.dbName || '—'}</div>
                <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{dbSettings.collections.length} collections</div>
              </div>
            </div>

            {/* Collections list */}
            {dbSettings.collections.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <label style={{ fontSize: 13, fontWeight: 700, color: '#555', display: 'block', marginBottom: 8 }}>Collections in database</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {dbSettings.collections.sort().map(col => (
                    <span key={col} style={{ background: '#e8f0fe', color: '#1565c0', fontSize: 12, fontWeight: 600, padding: '4px 12px', borderRadius: 20 }}>{col}</span>
                  ))}
                </div>
              </div>
            )}

            {/* URI config */}
            <div className="admin-form">
              <p style={{ fontSize: 13, color: '#666', margin: 0, lineHeight: 1.7 }}>
                MongoDB creates the database and all collections <strong>automatically</strong> when first written to — no manual setup needed.<br />
                Change the URI below to switch between local MongoDB and MongoDB Atlas.
              </p>

              <label><strong>MongoDB Connection URI</strong></label>
              <input
                placeholder="mongodb://127.0.0.1:27017/greenwheels"
                value={dbTestUri}
                onChange={e => setDbTestUri(e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: 13 }}
              />
              <p style={{ fontSize: 12, color: '#888', margin: '-8px 0 0', lineHeight: 1.7 }}>
                Local: <code>mongodb://127.0.0.1:27017/greenwheels</code><br />
                Atlas: <code>mongodb+srv://user:pass@cluster.mongodb.net/greenwheels?retryWrites=true&w=majority</code>
              </p>

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <button
                  className="btn"
                  style={{ background: dbTesting ? '#aaa' : '#1a73e8' }}
                  disabled={dbTesting || !dbTestUri}
                  onClick={async () => {
                    setDbTesting(true);
                    setDbMsg('Testing connection...');
                    try {
                      const { data } = await api.post('/admin/db-settings/test', { uri: dbTestUri });
                      setDbMsg(`Connected! DB: ${data.dbName} — ${data.collections.length} collections found.`);
                    } catch (e) {
                      setDbMsg('Error: ' + (e.response?.data?.error || e.message));
                    }
                    setDbTesting(false);
                    setTimeout(() => setDbMsg(''), 8000);
                  }}
                >
                  {dbTesting ? 'Testing...' : 'Test Connection'}
                </button>

                <button
                  className="btn"
                  disabled={!dbTestUri}
                  onClick={async () => {
                    if (!window.confirm(`Switch database connection to:\n\n${dbTestUri}\n\nThis will reconnect immediately.`)) return;
                    try {
                      const { data } = await api.put('/admin/db-settings', { uri: dbTestUri });
                      setDbMsg(data.message || 'URI saved and reconnected!');
                      loadData();
                      setTimeout(() => setDbMsg(''), 6000);
                    } catch (e) {
                      setDbMsg('Error: ' + (e.response?.data?.error || e.message));
                    }
                  }}
                >
                  Save URI
                </button>

                <button className="btn-small" onClick={loadData} style={{ marginLeft: 'auto' }}>
                  Refresh Status
                </button>
              </div>

              <div style={{ background: '#f4f6f9', borderRadius: 8, padding: '14px 16px', fontSize: 13, color: '#555', lineHeight: 1.8 }}>
                <strong style={{ display: 'block', marginBottom: 6, color: '#1a1a2e' }}>How it works</strong>
                MongoDB automatically creates the <strong>database</strong> and each <strong>collection</strong> the first time data is written.<br />
                You do not need to create them manually. Just connect and start using the app.<br />
                <strong>To use MongoDB Atlas:</strong> create a free cluster at <a href="https://cloud.mongodb.com" target="_blank" rel="noopener noreferrer">cloud.mongodb.com</a>, copy the connection string, paste it above and save.
              </div>
            </div>

            {/* Backup & Restore */}
            <div style={{ marginTop: 32, paddingTop: 24, borderTop: '2px solid #f0f0f0' }}>
              <h3>Backup & Restore</h3>
              {backupMsg && <p className={`save-msg${backupMsg.startsWith('Error') ? ' save-msg-err' : ''}`}>{backupMsg}</p>}

              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
                <select value={backupCol} onChange={e => setBackupCol(e.target.value)} style={{ padding: '8px 12px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13 }}>
                  <option value="">Full Backup (all collections)</option>
                  {dbSettings.collections.sort().map(c => <option key={c} value={c}>{c}</option>)}
                </select>
                <button
                  className="btn"
                  style={{ background: backupCreating ? '#aaa' : '#00875a' }}
                  disabled={backupCreating}
                  onClick={async () => {
                    setBackupCreating(true);
                    setBackupMsg('Creating backup...');
                    try {
                      const { data } = await api.post('/admin/backups', { collection: backupCol || undefined });
                      setBackupMsg(`Backup created: ${data.filename}`);
                      const { data: bk } = await api.get('/admin/backups');
                      setBackups(bk || []);
                    } catch (e) { setBackupMsg('Error: ' + (e.response?.data?.error || e.message)); }
                    setBackupCreating(false);
                    setTimeout(() => setBackupMsg(''), 6000);
                  }}
                >{backupCreating ? 'Creating...' : 'Create Backup'}</button>
                <button className="btn-small" onClick={async () => { const { data: bk } = await api.get('/admin/backups'); setBackups(bk || []); }} style={{ marginLeft: 'auto' }}>{"\u21bb"} Refresh</button>
              </div>

              {backups.length === 0 ? (
                <p style={{ color: '#999', textAlign: 'center', padding: 24 }}>No backups yet.</p>
              ) : (
                <div className="admin-table-wrap">
                <table className="admin-table">
                  <thead><tr><th>Filename</th><th>Size</th><th>Date</th><th>Actions</th></tr></thead>
                  <tbody>
                    {backups.map(b => (
                      <tr key={b.filename}>
                        <td style={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all' }}>{b.filename}</td>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{(b.size / 1024).toFixed(1)} KB</td>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{new Date(b.date).toLocaleString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn-small" onClick={async () => {
                            try {
                              const token = sessionStorage.getItem('adminToken');
                              const resp = await fetch(`${api.defaults.baseURL}/admin/backups/download/${b.filename}`, { headers: { Authorization: `Bearer ${token}` } });
                              const blob = await resp.blob();
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a'); a.href = url; a.download = b.filename; a.click();
                              URL.revokeObjectURL(url);
                            } catch (e) { setBackupMsg('Error downloading: ' + e.message); }
                          }}>Download</button>
                          <button className="btn-small" style={{ background: '#1a73e8', color: '#fff' }} disabled={backupRestoring} onClick={async () => {
                            try {
                              const { data: info } = await api.get(`/admin/backups/inspect/${b.filename}`);
                              const colList = (info.collections || []).map(c => `  \u2022 ${c.name} (${c.count} docs)`).join('\n');
                              const connUri = dbSettings.uri || 'unknown';
                              if (!window.confirm(`Restore from "${b.filename}"?\n\nTarget DB: ${connUri}\n\nCollections to restore:\n${colList}\n\n\u26a0\ufe0f This will OVERWRITE existing data in these collections.`)) return;
                              setBackupRestoring(true);
                              setBackupMsg('Restoring...');
                              const { data } = await api.post('/admin/backups/restore', { filename: b.filename });
                              const restoredList = (data.restored || []).map(c => `${c.name} (${c.count} docs)`).join(', ');
                              setBackupMsg(`\u2705 Restored to ${connUri}: ${restoredList}`);
                            } catch (e) { setBackupMsg('Error: ' + (e.response?.data?.error || e.message)); }
                            setBackupRestoring(false);
                            setTimeout(() => setBackupMsg(''), 10000);
                          }}>{backupRestoring ? 'Restoring...' : 'Restore'}</button>
                          <button className="btn-small btn-danger" onClick={async () => {
                            if (!window.confirm(`Delete backup "${b.filename}"?`)) return;
                            await api.delete(`/admin/backups/${b.filename}`);
                            setBackups(prev => prev.filter(x => x.filename !== b.filename));
                          }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}

              {/* Auto-Backup Schedule */}
              <div style={{ marginTop: 24, background: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: 10, padding: 20 }}>
                <h4 style={{ margin: '0 0 12px' }}>Auto-Backup Schedule</h4>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={backupSchedule.enabled}
                      onChange={e => setBackupSchedule(s => ({ ...s, enabled: e.target.checked }))} />
                    <span style={{ fontWeight: 600, color: backupSchedule.enabled ? '#00875a' : '#999' }}>
                      {backupSchedule.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </label>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginRight: 6 }}>Every:</label>
                    <select value={backupSchedule.interval} onChange={e => setBackupSchedule(s => ({ ...s, interval: e.target.value }))} style={{ padding: '6px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13 }}>
                      <option value="30m">30 minutes</option>
                      <option value="1h">1 hour</option>
                      <option value="3h">3 hours</option>
                      <option value="6h">6 hours</option>
                      <option value="12h">12 hours</option>
                      <option value="24h">24 hours</option>
                      <option value="2d">2 days</option>
                      <option value="7d">7 days</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#555', marginRight: 6 }}>Keep:</label>
                    <input type="number" min="1" max="100" value={backupSchedule.keep}
                      onChange={e => setBackupSchedule(s => ({ ...s, keep: parseInt(e.target.value) || 20 }))}
                      style={{ width: 60, padding: '6px 10px', border: '1.5px solid #ddd', borderRadius: 6, fontSize: 13 }} />
                    <span style={{ fontSize: 12, color: '#888', marginLeft: 4 }}>backups</span>
                  </div>
                </div>
                {backupSchedule.lastRun && (
                  <p style={{ fontSize: 12, color: '#666', margin: '0 0 10px' }}>Last auto-backup: <strong>{new Date(backupSchedule.lastRun).toLocaleString("en-IE", { timeZone: branding.timezone || "Europe/Dublin" })}</strong></p>
                )}
                <button className="btn" style={{ background: '#1a1a2e' }} onClick={async () => {
                  try {
                    await api.put('/admin/backups/schedule', backupSchedule);
                    setBackupMsg('Schedule saved! Scheduler ' + (backupSchedule.enabled ? 'started.' : 'stopped.'));
                    setTimeout(() => setBackupMsg(''), 4000);
                  } catch (e) { setBackupMsg('Error: ' + (e.response?.data?.error || e.message)); }
                }}>Save Schedule</button>
                <p style={{ fontSize: 12, color: '#888', margin: '8px 0 0', lineHeight: 1.6 }}>
                  When enabled, the server automatically creates full backups at the chosen interval.<br />
                  Old auto-backups beyond the "Keep" limit are deleted automatically.
                </p>
              </div>
            </div>

          </div>
        )}


        </div>
        </div>

      </div>
    </div>
  );


}

export default Admin;
